import { z } from 'zod';

// src/utils/enum.ts
function createEnum(values) {
  const result = {};
  for (const value of values) {
    result[value] = value;
  }
  return Object.freeze(result);
}
function enumValues(e) {
  return Object.values(e);
}
function isEnumValue(e, value) {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(e, value);
}

// src/enums/index.ts
var Currency = createEnum(["TRY", "EUR", "USD", "GBP"]);
var Locale = createEnum(["tr", "en", "ar", "ru"]);
var UserKind = createEnum(["CUSTOMER", "ADMIN"]);
var UserStatus = createEnum(["ACTIVE", "SUSPENDED", "DELETED"]);
var VipTier = createEnum(["STANDARD", "SILVER", "GOLD", "PLATINUM", "BLACK"]);
var VerificationTokenType = createEnum([
  "EMAIL_VERIFICATION",
  "PASSWORD_RESET",
  "ADMIN_PASSWORD"
]);
var ConsentType = createEnum([
  "KVKK_PROCESSING",
  "TERMS_OF_SERVICE",
  "MARKETING_EMAIL",
  "MARKETING_SMS",
  "MARKETING_WHATSAPP",
  "COOKIE_FUNCTIONAL",
  "COOKIE_ANALYTICS",
  "COOKIE_MARKETING",
  "SENSITIVE_DATA"
]);
var DataRequestType = createEnum(["EXPORT", "DELETION"]);
var DataRequestStatus = createEnum(["PENDING", "IN_PROGRESS", "COMPLETED", "REJECTED"]);
var AuditActorType = createEnum(["ADMIN", "CUSTOMER", "SYSTEM"]);
var PricingModel = createEnum(["TIME_BASED", "ROUTE_BASED", "QUOTE"]);
var BookingMode = createEnum(["INSTANT", "REQUEST", "QUOTE"]);
var ListingType = createEnum(["RENTAL", "SALE"]);
var SaleStatus = createEnum(["AVAILABLE", "UNDER_OFFER", "SOLD"]);
var DurationUnit = createEnum(["HOUR", "DAY", "NIGHT", "WEEK", "MONTH", "SESSION"]);
var AssetStatus = createEnum(["DRAFT", "PUBLISHED", "ARCHIVED"]);
var OwnershipType = createEnum(["COMPANY_OWNED", "MANAGED"]);
var LocationType = createEnum([
  "MARINA",
  "AIRPORT",
  "CITY",
  "DISTRICT",
  "OFFICE",
  "HOTEL",
  "PORT",
  "POINT_OF_INTEREST"
]);
var MediaType = createEnum(["IMAGE", "VIDEO", "DOCUMENT", "VIRTUAL_TOUR"]);
var PricingRuleKind = createEnum([
  "BASE",
  "WEEKEND",
  "SPECIAL_DAY",
  "TIME_OF_DAY",
  "DURATION_DISCOUNT",
  "ROUTE_FARE",
  "EXTRA_GUEST"
]);
var PriceAdjustmentType = createEnum(["FIXED_PRICE", "PERCENT", "AMOUNT"]);
var AvailabilityBlockReason = createEnum([
  "MAINTENANCE",
  "OWNER_USE",
  "MANUAL",
  "WEATHER",
  "OTHER"
]);
var AddOnPricingType = createEnum([
  "PER_BOOKING",
  "PER_PERSON",
  "PER_HOUR",
  "PER_DAY",
  "PER_UNIT"
]);
var DiscountType = createEnum(["PERCENT", "FIXED_AMOUNT"]);
var BookingStatus = createEnum([
  "DRAFT",
  "PENDING_PAYMENT",
  "AWAITING_CONFIRMATION",
  "CONFIRMED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
  "REFUNDED",
  "NO_SHOW"
]);
var BookingSource = createEnum([
  "WEB",
  "ADMIN",
  "PHONE",
  "WHATSAPP",
  "EMAIL",
  "WALK_IN",
  "MOBILE_APP",
  "PARTNER"
]);
var BookingItemKind = createEnum(["ASSET_RENTAL", "TRANSFER", "FLIGHT_LEG", "EXPERIENCE"]);
var PaymentPlan = createEnum(["DEPOSIT", "FULL"]);
var GuestDocumentType = createEnum(["PASSPORT", "NATIONAL_ID", "OTHER"]);
var TripType = createEnum(["ONE_WAY", "ROUND_TRIP", "MULTI_LEG"]);
var TransferVehicleClass = createEnum(["BUSINESS", "FIRST", "VAN", "MINIBUS"]);
var OwnedAssetType = createEnum(["YACHT", "JET", "CAR", "RESIDENCE"]);
var ServiceRequestStatus = createEnum([
  "NEW",
  "REVIEWING",
  "QUOTE_SENT",
  "QUOTE_ACCEPTED",
  "QUOTE_REJECTED",
  "SCHEDULED",
  "IN_PROGRESS",
  "COMPLETED",
  "INVOICED",
  "CANCELLED"
]);
var QuoteKind = createEnum(["SERVICE", "CHARTER"]);
var QuoteStatus = createEnum([
  "DRAFT",
  "SENT",
  "VIEWED",
  "ACCEPTED",
  "REJECTED",
  "EXPIRED",
  "CANCELLED",
  "SUPERSEDED"
]);
var QuoteLineType = createEnum(["LABOR", "MATERIAL", "SERVICE", "FEE", "DISCOUNT"]);
var WorkOrderStatus = createEnum([
  "PLANNED",
  "SCHEDULED",
  "IN_PROGRESS",
  "ON_HOLD",
  "COMPLETED",
  "CANCELLED"
]);
var WorkOrderTaskStatus = createEnum(["TODO", "IN_PROGRESS", "DONE", "SKIPPED"]);
var WorkOrderMediaPhase = createEnum(["BEFORE", "DURING", "AFTER", "SIGNATURE"]);
var StaffRole = createEnum([
  "CAPTAIN",
  "CREW",
  "CHEF",
  "HOSTESS",
  "CHAUFFEUR",
  "TECHNICIAN",
  "OPERATIONS",
  "PILOT",
  "FLIGHT_ATTENDANT"
]);
var EmploymentType = createEnum(["FULL_TIME", "PART_TIME", "SEASONAL", "CONTRACTOR"]);
var StaffAssignmentStatus = createEnum([
  "ASSIGNED",
  "CONFIRMED",
  "CHECKED_IN",
  "COMPLETED",
  "CANCELLED"
]);
var StaffAvailabilityType = createEnum([
  "AVAILABLE",
  "UNAVAILABLE",
  "LEAVE",
  "SICK",
  "SHIFT"
]);
var StaffDocumentType = createEnum([
  "DRIVER_LICENSE",
  "CAPTAIN_LICENSE",
  "SRC_CERTIFICATE",
  "HEALTH_REPORT",
  "PASSPORT",
  "PILOT_LICENSE",
  "MEDICAL_CERTIFICATE",
  "CRIMINAL_RECORD",
  "OTHER"
]);
var CashAccountType = createEnum(["CASH", "BANK", "POS", "PAYMENT_PROVIDER"]);
var PaymentKind = createEnum(["DEPOSIT", "BALANCE", "FULL", "QUOTE", "OTHER"]);
var PaymentMethod = createEnum(["CARD", "BANK_TRANSFER", "CASH", "POS", "PAYMENT_LINK"]);
var PaymentProvider = createEnum(["IYZICO", "STRIPE", "MANUAL", "TEST"]);
var PaymentStatus = createEnum([
  "PENDING",
  "REQUIRES_ACTION",
  "AUTHORIZED",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
  "REFUNDED",
  "PARTIALLY_REFUNDED"
]);
var RefundStatus = createEnum(["PENDING", "SUCCEEDED", "FAILED"]);
var InvoiceType = createEnum(["SALES", "PROFORMA", "CREDIT_NOTE"]);
var InvoiceStatus = createEnum([
  "DRAFT",
  "ISSUED",
  "SENT",
  "PAID",
  "PARTIALLY_PAID",
  "CANCELLED"
]);
var LedgerDirection = createEnum(["INFLOW", "OUTFLOW"]);
var LedgerEntryType = createEnum([
  "PAYMENT",
  "REFUND",
  "EXPENSE",
  "TRANSFER",
  "ADJUSTMENT",
  "OPENING_BALANCE"
]);
var ExpenseStatus = createEnum(["PENDING", "PAID", "CANCELLED"]);
var ExchangeRateSource = createEnum(["TCMB", "ECB", "MANUAL"]);
var ContentStatus = createEnum(["DRAFT", "PUBLISHED", "ARCHIVED"]);
var PageType = createEnum(["STATIC", "LEGAL", "LANDING"]);
var HomepageSectionType = createEnum([
  "HERO",
  "CATEGORY_SEARCH",
  "FEATURED_ASSETS",
  "EXPERIENCES",
  "EMPTY_LEGS",
  "WHY_US",
  "TESTIMONIALS",
  "INSTAGRAM",
  "BLOG",
  "NEWSLETTER",
  "CUSTOM"
]);
var BannerPlacement = createEnum(["SITE_TOP", "HOME_HERO", "HOME_INLINE", "LISTING"]);
var EmptyLegStatus = createEnum(["AVAILABLE", "RESERVED", "SOLD", "EXPIRED", "CANCELLED"]);
var NotificationChannel = createEnum(["IN_APP", "EMAIL", "SMS", "WHATSAPP", "PUSH"]);
var NotificationStatus = createEnum(["PENDING", "SENT", "FAILED", "READ"]);
var TicketStatus = createEnum(["OPEN", "PENDING", "RESOLVED", "CLOSED"]);
var TicketPriority = createEnum(["LOW", "NORMAL", "HIGH", "URGENT"]);
var MessageSenderType = createEnum(["CUSTOMER", "ADMIN", "SYSTEM"]);
var LeadSource = createEnum([
  "CONTACT_FORM",
  "CALLBACK_REQUEST",
  "BOOKING_REQUEST",
  "CHARTER_REQUEST",
  "SALE_INQUIRY",
  "VIEWING_REQUEST",
  "WHATSAPP",
  "PHONE",
  "EMAIL",
  "INSTAGRAM",
  "REFERRAL",
  "WALK_IN",
  "OTHER"
]);
var LeadStatus = createEnum([
  "NEW",
  "CONTACTED",
  "QUALIFIED",
  "PROPOSAL",
  "CONVERTED",
  "LOST"
]);
var ReviewStatus = createEnum(["PENDING", "APPROVED", "REJECTED"]);
var TripPlanStatus = createEnum([
  "SUBMITTED",
  "DEPOSIT_PAID",
  "IN_REVIEW",
  "CONFIRMED",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED"
]);
var TripItemRole = createEnum(["JET", "TRANSFER", "STAY", "YACHT", "EXPERIENCE", "CAR"]);
var TripItemStatus = createEnum(["PENDING", "RESERVED", "UNAVAILABLE"]);
var TripPriceKind = createEnum(["EXACT", "RANGE", "ON_REQUEST"]);

// src/constants.ts
var APP_NAME = "DuvaMarine";
var DEFAULT_LOCALE = Locale.tr;
var SUPPORTED_LOCALES = [Locale.tr, Locale.en, Locale.ar, Locale.ru];
var RTL_LOCALES = [Locale.ar];
var DEFAULT_TIMEZONE = "Europe/Istanbul";
var DEFAULT_CURRENCY = Currency.TRY;
var SUPPORTED_CURRENCIES = [
  Currency.TRY,
  Currency.EUR,
  Currency.USD,
  Currency.GBP
];
var API_PREFIX = "/api";
var API_VERSION = "1";
var API_BASE_PATH = `${API_PREFIX}/v${API_VERSION}`;
var BOOKING_TOKEN_HEADER = "x-booking-token";
var QUOTE_TOKEN_HEADER = "x-quote-token";
var TRIP_PLAN_TOKEN_HEADER = "x-trip-plan-token";
var BOOKING_HOLD_TTL_SECONDS = 15 * 60;
var CORE_CATEGORY_KEYS = {
  YACHT: "yacht",
  JET: "jet",
  CAR: "car",
  TRANSFER: "transfer"
};
var BPS_DENOMINATOR = 1e4;
function isRtlLocale(locale) {
  return RTL_LOCALES.includes(locale);
}

// src/errors.ts
var ErrorCode = createEnum([
  "INTERNAL_ERROR",
  "VALIDATION_FAILED",
  "NOT_FOUND",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "CONFLICT",
  "RATE_LIMITED",
  "AUTH_INVALID_CREDENTIALS",
  "AUTH_EMAIL_TAKEN",
  "AUTH_TOKEN_INVALID",
  "AUTH_TOKEN_EXPIRED",
  "AUTH_SESSION_REVOKED",
  "AUTH_REFRESH_REUSED",
  "AUTH_TOO_MANY_ATTEMPTS",
  "AUTH_ACCOUNT_SUSPENDED",
  "AUTH_WRONG_AREA",
  "AUTH_ORIGIN_NOT_ALLOWED",
  "AUTH_CHALLENGE_INVALID",
  "AUTH_TWO_FACTOR_INVALID",
  "AUTH_TWO_FACTOR_NOT_SETUP",
  "AUTH_TWO_FACTOR_ALREADY_ENABLED",
  "AUTH_VERIFICATION_TOKEN_INVALID",
  "ROLE_SYSTEM_IMMUTABLE",
  "BOOKING_INVALID_TRANSITION",
  "BOOKING_CONFLICT",
  "CATEGORY_KEY_TAKEN",
  "CATEGORY_KEY_IMMUTABLE",
  "CATEGORY_LISTING_TYPE_LOCKED",
  "SLUG_TAKEN",
  "ASSET_CODE_TAKEN",
  "ASSET_NOT_PUBLISHABLE",
  "ASSET_ATTRIBUTES_INVALID",
  "MEDIA_UNSUPPORTED_TYPE",
  "MEDIA_TOO_LARGE",
  "MEDIA_REQUIRED",
  "AVAILABILITY_BOOKING_OVERLAP",
  "BOOKING_NOT_BOOKABLE",
  "BOOKING_HOLD_EXPIRED",
  "BOOKING_MANIFEST_REQUIRED",
  "COUPON_INVALID",
  "COUPON_CODE_TAKEN",
  "KEY_TAKEN",
  "PAYMENT_NOT_ALLOWED",
  "PAYMENT_DECLINED",
  "NEWSLETTER_TOKEN_INVALID",
  "PAYMENT_PROVIDER_ERROR",
  "REFUND_EXCEEDS_REFUNDABLE",
  "REFUND_ACCOUNT_REQUIRED",
  "REFUND_FAILED",
  "CASH_ACCOUNT_INVALID",
  "LEDGER_NOT_REVERSIBLE",
  "EXPENSE_NOT_EDITABLE",
  "INVOICE_NOT_EDITABLE",
  "INVOICE_INVALID_STATE",
  "FX_RATE_MISSING",
  "FX_UPDATE_FAILED",
  "RECEIPT_UNSUPPORTED",
  "SERVICE_REQUEST_INVALID_TRANSITION",
  "SERVICE_REQUEST_ACTION_REQUIRED",
  "QUOTE_NOT_EDITABLE",
  "QUOTE_INVALID_STATE",
  "QUOTE_EXPIRED",
  "QUOTE_TOTAL_INVALID",
  "WORK_ORDER_INVALID_TRANSITION",
  "WORK_ORDER_SCHEDULE_REQUIRED",
  "DOCUMENT_UNSUPPORTED",
  "ROLE_IN_USE",
  "ADMIN_SELF_LOCKOUT",
  "ADMIN_USER_INACTIVE",
  "MEDIA_IN_USE",
  "BOOKING_RESCHEDULE_NOT_ALLOWED",
  "REVIEW_NOT_ALLOWED",
  "REVIEW_EXISTS",
  "CRM_SENSITIVE_CONSENT_REQUIRED",
  "DATA_REQUEST_OPEN",
  "DATA_DELETION_BLOCKED",
  "DATA_EXPORT_EXPIRED",
  "TRIP_PLAN_EMPTY",
  "TRIP_PLAN_NOT_CONFIRMED",
  "TRIP_PLAN_INVOICE_EXISTS",
  "TRIP_PLAN_ITEM_UNAVAILABLE",
  "TRIP_PLAN_INVALID_TRANSITION"
]);
var CURRENCY_META = {
  TRY: { code: Currency.TRY, minorDigits: 2, symbol: "\u20BA" },
  EUR: { code: Currency.EUR, minorDigits: 2, symbol: "\u20AC" },
  USD: { code: Currency.USD, minorDigits: 2, symbol: "$" },
  GBP: { code: Currency.GBP, minorDigits: 2, symbol: "\xA3" }
};
var minorAmountSchema = z.number().int().refine(Number.isSafeInteger, {
  error: "validation.money.unsafe"
});
var moneySchema = z.object({
  amountMinor: minorAmountSchema,
  currency: z.enum(Currency)
});
var MoneyError = class extends Error {
  name = "MoneyError";
};
function assertSafeMinor(value) {
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`Amount must be a safe integer in minor units, received ${value}`);
  }
  return value;
}
function parseMajorToMinor(input, currency) {
  const { minorDigits } = CURRENCY_META[currency];
  const match = /^(-)?(\d+)(?:[.,](\d+))?$/.exec(input.trim());
  if (!match) {
    throw new MoneyError(`Invalid amount "${input}"`);
  }
  const negative = match[1] === "-";
  const whole = match[2] ?? "0";
  const fraction = match[3] ?? "";
  if (fraction.length > minorDigits) {
    throw new MoneyError(`Too many decimal places for ${currency}: "${input}"`);
  }
  const scale = 10n ** BigInt(minorDigits);
  const minor = BigInt(whole) * scale + BigInt(fraction.padEnd(minorDigits, "0") || "0");
  return assertSafeMinor(Number(negative ? -minor : minor));
}
function minorToMajorString(amountMinor, currency) {
  assertSafeMinor(amountMinor);
  const { minorDigits } = CURRENCY_META[currency];
  const negative = amountMinor < 0;
  const digits = Math.abs(amountMinor).toString().padStart(minorDigits + 1, "0");
  const whole = digits.slice(0, digits.length - minorDigits);
  const fraction = digits.slice(digits.length - minorDigits);
  return `${negative ? "-" : ""}${whole}${minorDigits > 0 ? `.${fraction}` : ""}`;
}
function formatMoney(money, locale = "tr-TR", options = {}) {
  const { minorDigits } = CURRENCY_META[money.currency];
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: money.currency,
    minimumFractionDigits: minorDigits,
    maximumFractionDigits: minorDigits,
    ...options
  }).format(money.amountMinor / 10 ** minorDigits);
}
function applyBps(amountMinor, bps3) {
  assertSafeMinor(amountMinor);
  if (!Number.isInteger(bps3)) {
    throw new MoneyError(`Basis points must be an integer, received ${bps3}`);
  }
  const product = BigInt(amountMinor) * BigInt(bps3);
  const denominator = BigInt(BPS_DENOMINATOR);
  const magnitude = product < 0n ? -product : product;
  let quotient = magnitude / denominator;
  if (magnitude % denominator * 2n >= denominator) {
    quotient += 1n;
  }
  return assertSafeMinor(Number(product < 0n ? -quotient : quotient));
}
function sumMinor(values) {
  let total = 0n;
  for (const value of values) {
    total += BigInt(assertSafeMinor(value));
  }
  return assertSafeMinor(Number(total));
}
function addMoney(a, b) {
  if (a.currency !== b.currency) {
    throw new MoneyError(`Currency mismatch: ${a.currency} vs ${b.currency}`);
  }
  return { amountMinor: sumMinor([a.amountMinor, b.amountMinor]), currency: a.currency };
}
var RATE_DIGITS = 8;
function scaledRate(rate) {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(rate.trim());
  if (!match) throw new MoneyError(`Invalid exchange rate "${rate}"`);
  const fraction = (match[2] ?? "").slice(0, RATE_DIGITS).padEnd(RATE_DIGITS, "0");
  const value = BigInt(match[1] ?? "0") * 10n ** BigInt(RATE_DIGITS) + BigInt(fraction);
  if (value <= 0n) throw new MoneyError(`Exchange rate must be positive, received "${rate}"`);
  return value;
}
function convertMinor(amountMinor, from, to, rates) {
  assertSafeMinor(amountMinor);
  if (from === to) return amountMinor;
  const fromRate = rates[from];
  const toRate = rates[to];
  if (!fromRate || !toRate) throw new MoneyError(`Missing exchange rate for ${from}/${to}`);
  const numerator = BigInt(amountMinor) * scaledRate(fromRate) * 10n ** BigInt(CURRENCY_META[to].minorDigits);
  const denominator = scaledRate(toRate) * 10n ** BigInt(CURRENCY_META[from].minorDigits);
  const negative = numerator < 0n;
  const magnitude = negative ? -numerator : numerator;
  let quotient = magnitude / denominator;
  if (magnitude % denominator * 2n >= denominator) quotient += 1n;
  return assertSafeMinor(Number(negative ? -quotient : quotient));
}

// src/time.ts
var ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
var LOCAL_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
var formatters = /* @__PURE__ */ new Map();
function formatterFor(timeZone) {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}
function zonedParts(instant, timeZone = DEFAULT_TIMEZONE) {
  const parts = {};
  for (const part of formatterFor(timeZone).formatToParts(instant)) {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year ?? 0,
    month: parts.month ?? 1,
    day: parts.day ?? 1,
    hour: parts.hour ?? 0,
    minute: parts.minute ?? 0,
    second: parts.second ?? 0
  };
}
function timeZoneOffsetMinutes(instant, timeZone = DEFAULT_TIMEZONE) {
  const p = zonedParts(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(instant.getTime() / 1e3) * 1e3) / 6e4);
}
function splitDate(isoDate2) {
  if (!ISO_DATE_PATTERN.test(isoDate2)) throw new RangeError(`Invalid ISO date: ${isoDate2}`);
  const [year = 0, month = 1, day = 1] = isoDate2.split("-").map(Number);
  return [year, month, day];
}
function zonedTimeToUtc(isoDate2, time2 = "00:00", timeZone = DEFAULT_TIMEZONE) {
  const [year, month, day] = splitDate(isoDate2);
  const [hour = 0, minute = 0] = time2.split(":").map(Number);
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  let result = guess - timeZoneOffsetMinutes(new Date(guess), timeZone) * 6e4;
  result = guess - timeZoneOffsetMinutes(new Date(result), timeZone) * 6e4;
  return new Date(result);
}
function localDateTimeToUtc(value, timeZone = DEFAULT_TIMEZONE) {
  if (!LOCAL_DATETIME_PATTERN.test(value)) throw new RangeError(`Invalid local datetime: ${value}`);
  const [date = "", time2 = "00:00"] = value.split("T");
  return zonedTimeToUtc(date, time2, timeZone);
}
var pad = (value) => value.toString().padStart(2, "0");
function isoDateInZone(instant, timeZone = DEFAULT_TIMEZONE) {
  const p = zonedParts(instant, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}
function localDateTimeInZone(instant, timeZone = DEFAULT_TIMEZONE) {
  const p = zonedParts(instant, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}
function addDaysToIsoDate(isoDate2, days2) {
  const [year, month, day] = splitDate(isoDate2);
  const date = new Date(Date.UTC(year, month - 1, day + days2));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}
function addMonthsToIsoDate(isoDate2, months) {
  const [year, month, day] = splitDate(isoDate2);
  const date = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}
function diffIsoDays(from, to) {
  const [y1, m1, d1] = splitDate(from);
  const [y2, m2, d2] = splitDate(to);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 864e5);
}
function isoDateRange(from, to) {
  const days2 = diffIsoDays(from, to);
  return Array.from({ length: Math.max(0, days2 + 1) }, (_, index) => addDaysToIsoDate(from, index));
}
function zonedDayBounds(isoDate2, timeZone = DEFAULT_TIMEZONE) {
  return {
    start: zonedTimeToUtc(isoDate2, "00:00", timeZone),
    end: zonedTimeToUtc(addDaysToIsoDate(isoDate2, 1), "00:00", timeZone)
  };
}

// src/catalog/availability.ts
var DayStatus = createEnum(["AVAILABLE", "PARTIAL", "UNAVAILABLE"]);
var AvailabilityStatus = createEnum(["AVAILABLE", "LIMITED", "UNAVAILABLE"]);
var SERVICE_DAY = { start: "08:00", end: "24:00" };
var MIN_PARTIAL_FREE_MINUTES = 120;
function toInterval(start, end) {
  return { start: start.getTime(), end: end.getTime() };
}
function mergeIntervals(intervals) {
  const sorted = intervals.filter((i) => i.end > i.start).sort((a, b) => a.start - b.start);
  const merged = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last && interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
    } else {
      merged.push({ ...interval });
    }
  }
  return merged;
}
function peakLoad(loads, window) {
  const inside = loads.filter((load) => load.units > 0 && overlaps(load, window)).map((load) => ({
    start: Math.max(load.start, window.start),
    end: Math.min(load.end, window.end),
    units: load.units
  }));
  return inside.reduce(
    (peak, { start }) => Math.max(
      peak,
      inside.reduce(
        (sum, load) => load.start <= start && load.end > start ? sum + load.units : sum,
        0
      )
    ),
    0
  );
}
function saturatedIntervals(loads, capacity, requested = 1) {
  if (requested > capacity) return [{ start: 0, end: Number.MAX_SAFE_INTEGER }];
  const events = loads.filter((load2) => load2.end > load2.start && load2.units > 0).flatMap((load2) => [
    { at: load2.start, delta: load2.units },
    { at: load2.end, delta: -load2.units }
  ]).sort((a, b) => a.at - b.at || a.delta - b.delta);
  const busy = [];
  let load = 0;
  let openedAt = null;
  for (const event of events) {
    load += event.delta;
    const full = load + requested > capacity;
    if (full && openedAt === null) openedAt = event.at;
    else if (!full && openedAt !== null) {
      busy.push({ start: openedAt, end: event.at });
      openedAt = null;
    }
  }
  return mergeIntervals(busy);
}
function intersectBusy(sets) {
  const [first, ...rest] = sets.map((set) => mergeIntervals(set));
  if (!first) return [{ start: 0, end: Number.MAX_SAFE_INTEGER }];
  return rest.reduce((common, set) => {
    const result = [];
    let i = 0;
    let j = 0;
    while (i < common.length && j < set.length) {
      const a = common[i];
      const b = set[j];
      const start = Math.max(a.start, b.start);
      const end = Math.min(a.end, b.end);
      if (start < end) result.push({ start, end });
      if (a.end < b.end) i += 1;
      else j += 1;
    }
    return result;
  }, first);
}
function overlaps(a, b) {
  return a.start < b.end && a.end > b.start;
}
function isFree(busy, window) {
  return !busy.some((interval) => overlaps(interval, window));
}
function longestFreeMinutes(busy, window) {
  let cursor = window.start;
  let best = 0;
  for (const interval of busy) {
    if (interval.end <= window.start) continue;
    if (interval.start >= window.end) break;
    best = Math.max(best, interval.start - cursor);
    cursor = Math.max(cursor, interval.end);
  }
  best = Math.max(best, window.end - cursor);
  return Math.max(0, Math.floor(best / 6e4));
}
function serviceWindow(isoDate2, timeZone = DEFAULT_TIMEZONE) {
  const start = zonedTimeToUtc(isoDate2, SERVICE_DAY.start, timeZone);
  const end = SERVICE_DAY.end === "24:00" ? zonedTimeToUtc(addDaysToIsoDate(isoDate2, 1), "00:00", timeZone) : zonedTimeToUtc(isoDate2, SERVICE_DAY.end, timeZone);
  return toInterval(start, end);
}
function dayStatus(busy, isoDate2, timeZone = DEFAULT_TIMEZONE) {
  const window = serviceWindow(isoDate2, timeZone);
  if (isFree(busy, window)) return DayStatus.AVAILABLE;
  return longestFreeMinutes(busy, window) >= MIN_PARTIAL_FREE_MINUTES ? DayStatus.PARTIAL : DayStatus.UNAVAILABLE;
}
function calendarDays(busy, from, to, timeZone = DEFAULT_TIMEZONE) {
  const merged = mergeIntervals(busy);
  return isoDateRange(from, to).map((date) => ({
    date,
    status: dayStatus(merged, date, timeZone)
  }));
}
function evaluateSlot(busy, slot, timeZone = DEFAULT_TIMEZONE) {
  if (slot.kind === "window") {
    return isFree(busy, slot) ? AvailabilityStatus.AVAILABLE : AvailabilityStatus.UNAVAILABLE;
  }
  const window = serviceWindow(slot.date, timeZone);
  if (isFree(busy, window)) return AvailabilityStatus.AVAILABLE;
  return longestFreeMinutes(busy, window) >= slot.minutes ? AvailabilityStatus.LIMITED : AvailabilityStatus.UNAVAILABLE;
}
function shiftSlot(slot, days2, timeZone = DEFAULT_TIMEZONE) {
  if (slot.kind === "hours") return { ...slot, date: addDaysToIsoDate(slot.date, days2) };
  const shift = (instant) => {
    const local = localDateTimeInZone(new Date(instant), timeZone);
    const [date = "", time2 = "00:00"] = local.split("T");
    return localDateTimeToUtc(`${addDaysToIsoDate(date, days2)}T${time2}`, timeZone).getTime();
  };
  return { kind: "window", start: shift(slot.start), end: shift(slot.end) };
}
function slotStartDate(slot, timeZone = DEFAULT_TIMEZONE) {
  return slot.kind === "hours" ? slot.date : isoDateInZone(new Date(slot.start), timeZone);
}
function slotScanBounds(slot, maxDays, timeZone = DEFAULT_TIMEZONE) {
  const first = slot.kind === "window" ? slot.start : serviceWindow(slot.date, timeZone).start;
  const shifted = shiftSlot(slot, maxDays, timeZone);
  const last = shifted.kind === "window" ? shifted.end : serviceWindow(shifted.date, timeZone).end;
  return { start: first, end: last };
}
function nearestAvailableDate(busy, slot, { timeZone = DEFAULT_TIMEZONE, maxDays = 60 } = {}) {
  const merged = mergeIntervals(busy);
  for (let day = 1; day <= maxDays; day += 1) {
    const candidate = shiftSlot(slot, day, timeZone);
    if (evaluateSlot(merged, candidate, timeZone) === AvailabilityStatus.AVAILABLE) {
      return slotStartDate(candidate, timeZone);
    }
  }
  return null;
}

// src/catalog/estimates.ts
var CHARTER_TAXI_MINUTES = 30;
var CHARTER_MIN_BILLABLE_MINUTES = 60;
var CHARTER_RANGE_LOW_BPS = 9e3;
var CHARTER_RANGE_HIGH_BPS = 12e3;
function billableLegMinutes(flightMinutes) {
  const raw = Math.max(CHARTER_MIN_BILLABLE_MINUTES, flightMinutes + CHARTER_TAXI_MINUTES);
  return Math.ceil(raw / 15) * 15;
}
function estimateCharter({
  flightMinutes,
  hourlyRateMinor,
  legs
}) {
  const billableMinutes = billableLegMinutes(flightMinutes) * Math.max(1, legs);
  const base = Math.round(hourlyRateMinor * billableMinutes / 60);
  return {
    billableMinutes,
    lowMinor: applyBps(base, CHARTER_RANGE_LOW_BPS),
    highMinor: applyBps(base, CHARTER_RANGE_HIGH_BPS)
  };
}
function haversineKm(a, b) {
  const toRad = (deg) => deg * Math.PI / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * 6371 * Math.asin(Math.sqrt(h)));
}
var ESTIMATED_JET_SPEED_KMH = 750;
function estimatedFlightMinutes(distanceKm, cruiseSpeedKmh) {
  const speed = cruiseSpeedKmh && cruiseSpeedKmh > 0 ? cruiseSpeedKmh * 0.9 : ESTIMATED_JET_SPEED_KMH;
  return Math.max(30, Math.round(distanceKm / speed * 60));
}

// src/catalog/search-params.ts
var CatalogSort = createEnum([
  "RECOMMENDED",
  "PRICE_ASC",
  "PRICE_DESC",
  "CAPACITY",
  "NEWEST",
  "RATING"
]);
var FILTER_PARAM_PREFIX = "f.";
var RANGE_SEPARATOR = "~";
var LIST_SEPARATOR = ",";
var MAX_TEXT_LENGTH = 200;
var MAX_LIST_ITEMS = 30;
var DURATION_SUFFIX = {
  HOUR: "h",
  DAY: "d",
  NIGHT: "n",
  WEEK: "w",
  MONTH: "m",
  SESSION: "s"
};
var SUFFIX_UNIT = {
  h: DurationUnit.HOUR,
  d: DurationUnit.DAY,
  n: DurationUnit.NIGHT,
  w: DurationUnit.WEEK,
  m: DurationUnit.MONTH,
  s: DurationUnit.SESSION
};
function emptySearchState() {
  return {
    values: {},
    filters: {},
    amenities: [],
    sort: CatalogSort.RECOMMENDED,
    page: 1,
    availableOnly: false
  };
}
function isValidIsoDate(value) {
  return ISO_DATE_PATTERN.test(value) && addDaysToIsoDate(value, 0) === value;
}
function isValidLocalDateTime(value) {
  if (!LOCAL_DATETIME_PATTERN.test(value)) return false;
  const [date = "", time2 = ""] = value.split("T");
  const [hour = 99, minute = 99] = time2.split(":").map(Number);
  return isValidIsoDate(date) && hour < 24 && minute < 60;
}
function parseInteger(raw, min, max) {
  if (raw === null || !/^-?\d+$/.test(raw)) return void 0;
  const value = Number(raw);
  return value >= min && value <= max ? value : void 0;
}
function parseNumber(raw) {
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return void 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : void 0;
}
function parseRange(raw, item) {
  const [from = "", to = ""] = raw.split(RANGE_SEPARATOR);
  const start = item(from);
  const end = item(to);
  return start !== void 0 && end !== void 0 ? [start, end] : void 0;
}
function parseList(raw) {
  if (!raw) return [];
  return [
    ...new Set(
      raw.split(LIST_SEPARATOR).map((v) => v.trim()).filter((v) => v.length > 0 && v.length <= 64)
    )
  ].slice(0, MAX_LIST_ITEMS);
}
var isoDateOrUndefined = (v) => isValidIsoDate(v) ? v : void 0;
var localDateTimeOrUndefined = (v) => isValidLocalDateTime(v) ? v : void 0;
function parseField(field, raw) {
  if (raw === null || raw === "") return void 0;
  switch (field.type) {
    case "LOCATION":
    case "ORIGIN":
    case "DESTINATION":
      return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(raw) ? { kind: "location", slug: raw } : void 0;
    case "ADDRESS":
      return { kind: "text", value: raw.trim().slice(0, MAX_TEXT_LENGTH) };
    case "DATE":
      return isValidIsoDate(raw) ? { kind: "date", value: raw } : void 0;
    case "DATETIME":
      return isValidLocalDateTime(raw) ? { kind: "datetime", value: raw } : void 0;
    case "DATE_RANGE": {
      const range = parseRange(raw, isoDateOrUndefined);
      return range && range[0] <= range[1] ? { kind: "dateRange", from: range[0], to: range[1] } : void 0;
    }
    case "DATETIME_RANGE": {
      const range = parseRange(raw, localDateTimeOrUndefined);
      return range && range[0] < range[1] ? { kind: "datetimeRange", from: range[0], to: range[1] } : void 0;
    }
    case "DURATION": {
      const match = /^(\d{1,3})([hdwm])$/.exec(raw);
      const unit = match ? SUFFIX_UNIT[match[2] ?? ""] : void 0;
      const value = match ? Number(match[1]) : 0;
      if (!unit || value < 1) return void 0;
      if (field.durationUnits && !field.durationUnits.includes(unit)) return void 0;
      return { kind: "duration", value, unit };
    }
    case "GUESTS":
    case "ROOMS":
    case "PASSENGERS":
    case "LUGGAGE": {
      const value = parseInteger(raw, field.min ?? 0, field.max ?? 500);
      return value === void 0 ? void 0 : { kind: "count", value };
    }
    case "TRIP_TYPE":
      return raw in TripType ? { kind: "tripType", value: raw } : void 0;
    case "TOGGLE":
      return raw === "1" || raw === "0" ? { kind: "toggle", value: raw === "1" } : void 0;
    case "SELECT":
      return !field.options || field.options.some((o) => o.value === raw) ? { kind: "select", value: raw } : void 0;
  }
}
function parseFilter(field, raw) {
  if (raw === null || raw === "") return void 0;
  switch (field.type) {
    case "NUMBER":
    case "INTEGER":
    case "YEAR": {
      const [rawMin = "", rawMax = ""] = raw.split(RANGE_SEPARATOR);
      const min = rawMin ? parseNumber(rawMin) : void 0;
      const max = rawMax ? parseNumber(rawMax) : void 0;
      if (min === void 0 && max === void 0) return void 0;
      return {
        kind: "range",
        ...min === void 0 ? {} : { min },
        ...max === void 0 ? {} : { max }
      };
    }
    case "BOOLEAN":
      return raw === "1" ? { kind: "flag" } : void 0;
    case "SELECT":
    case "MULTI_SELECT":
    case "TEXT": {
      const allowed = field.options?.map((o) => o.value);
      const values = parseList(raw).filter((v) => !allowed || allowed.includes(v));
      return values.length > 0 ? { kind: "options", values } : void 0;
    }
    case "TEXTAREA":
    case "DATE":
      return void 0;
  }
}
function queryReader(query) {
  return {
    get: (name) => {
      const value = query[name];
      if (typeof value === "string") return value;
      if (Array.isArray(value) && typeof value[0] === "string") return value[0];
      return null;
    }
  };
}
function parseCatalogSearch(params, searchFields, attributeFields) {
  const state = emptySearchState();
  for (const field of searchFields) {
    const value = parseField(field, params.get(field.key));
    if (value) state.values[field.key] = value;
  }
  for (const field of attributeFields) {
    const filter = parseFilter(field, params.get(`${FILTER_PARAM_PREFIX}${field.key}`));
    if (filter) state.filters[field.key] = filter;
  }
  state.amenities = parseList(params.get("amenities"));
  const price = params.get("price");
  if (price) {
    const [rawMin = "", rawMax = ""] = price.split(RANGE_SEPARATOR);
    const min = parseInteger(rawMin, 0, 1e9);
    const max = parseInteger(rawMax, 0, 1e9);
    if (min !== void 0) state.priceMin = min;
    if (max !== void 0) state.priceMax = max;
  }
  const sort = params.get("sort");
  if (sort && sort in CatalogSort) state.sort = sort;
  state.page = parseInteger(params.get("page"), 1, 1e3) ?? 1;
  state.availableOnly = params.get("avail") === "1";
  return state;
}
function serializeValue(value) {
  switch (value.kind) {
    case "location":
      return value.slug;
    case "dateRange":
    case "datetimeRange":
      return `${value.from}${RANGE_SEPARATOR}${value.to}`;
    case "duration":
      return `${value.value}${DURATION_SUFFIX[value.unit]}`;
    case "count":
      return String(value.value);
    case "toggle":
      return value.value ? "1" : "0";
    case "text":
    case "date":
    case "datetime":
    case "tripType":
    case "select":
      return value.value;
  }
}
function serializeFilter(filter) {
  switch (filter.kind) {
    case "range":
      return `${filter.min ?? ""}${RANGE_SEPARATOR}${filter.max ?? ""}`;
    case "options":
      return filter.values.join(LIST_SEPARATOR);
    case "flag":
      return "1";
  }
}
function serializeCatalogSearch(state) {
  const entries = [
    ...Object.entries(state.values).map(([key, value]) => [
      key,
      serializeValue(value)
    ]),
    ...Object.entries(state.filters).map(([key, filter]) => [
      `${FILTER_PARAM_PREFIX}${key}`,
      serializeFilter(filter)
    ])
  ];
  if (state.amenities.length > 0) entries.push(["amenities", state.amenities.join(LIST_SEPARATOR)]);
  if (state.priceMin !== void 0 || state.priceMax !== void 0) {
    entries.push(["price", `${state.priceMin ?? ""}${RANGE_SEPARATOR}${state.priceMax ?? ""}`]);
  }
  if (state.sort !== CatalogSort.RECOMMENDED) entries.push(["sort", state.sort]);
  if (state.page > 1) entries.push(["page", String(state.page)]);
  if (state.availableOnly) entries.push(["avail", "1"]);
  return new URLSearchParams(entries.sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0));
}
function durationMinutes(value, unit) {
  return unit === DurationUnit.HOUR ? value * 60 : null;
}

// src/booking/coupon.ts
var CouponStatus = createEnum([
  "APPLIED",
  "INVALID",
  "NOT_STARTED",
  "EXPIRED",
  "EXHAUSTED",
  "ALREADY_USED",
  "CATEGORY",
  "MIN_ORDER",
  "CURRENCY"
]);
function normalizeCouponCode(code) {
  return code.trim().toLocaleUpperCase("en-US");
}
function rejectedCoupon(code, status) {
  return {
    couponId: null,
    code: normalizeCouponCode(code),
    status,
    discountMinor: 0,
    minOrderMinor: null
  };
}
function evaluateCoupon(coupon, context) {
  const reject = (status, minOrderMinor = null) => ({
    couponId: coupon.id,
    code: coupon.code,
    status,
    discountMinor: 0,
    minOrderMinor
  });
  if (!coupon.isActive) return reject(CouponStatus.INVALID);
  if (coupon.validFrom !== null && context.now < coupon.validFrom) {
    return reject(CouponStatus.NOT_STARTED);
  }
  if (coupon.validTo !== null && context.now > coupon.validTo) return reject(CouponStatus.EXPIRED);
  if (coupon.usageLimit !== null && coupon.usedCount >= coupon.usageLimit) {
    return reject(CouponStatus.EXHAUSTED);
  }
  if (coupon.usageLimitPerUser !== null && coupon.usedByCustomer >= coupon.usageLimitPerUser) {
    return reject(CouponStatus.ALREADY_USED);
  }
  if (coupon.categoryIds.length > 0 && !coupon.categoryIds.includes(context.categoryId)) {
    return reject(CouponStatus.CATEGORY);
  }
  const source = coupon.currency ?? context.currency;
  const inBookingCurrency = (amount) => {
    if (amount === null) return null;
    return context.convert(amount, source, context.currency) ?? "MISSING";
  };
  const minOrder = inBookingCurrency(coupon.minOrderMinor);
  const maxDiscount = inBookingCurrency(coupon.maxDiscountMinor);
  if (minOrder === "MISSING" || maxDiscount === "MISSING") return reject(CouponStatus.CURRENCY);
  if (minOrder !== null && context.subtotalMinor < minOrder) {
    return reject(CouponStatus.MIN_ORDER, minOrder);
  }
  let discount;
  if (coupon.discountType === "PERCENT") {
    if (coupon.percentBps === null) return reject(CouponStatus.INVALID);
    discount = applyBps(context.subtotalMinor, coupon.percentBps);
  } else {
    const amount = inBookingCurrency(coupon.amountMinor);
    if (amount === "MISSING") return reject(CouponStatus.CURRENCY);
    if (amount === null) return reject(CouponStatus.INVALID);
    discount = amount;
  }
  if (maxDiscount !== null) discount = Math.min(discount, maxDiscount);
  discount = Math.max(0, Math.min(discount, context.subtotalMinor));
  return {
    couponId: coupon.id,
    code: coupon.code,
    status: CouponStatus.APPLIED,
    discountMinor: discount,
    minOrderMinor: null
  };
}

// src/booking/pricing.ts
var HOUR_MS = 36e5;
var DAY_MS = 24 * HOUR_MS;
var UNIT_MS = {
  HOUR: HOUR_MS,
  DAY: DAY_MS,
  NIGHT: DAY_MS,
  WEEK: 7 * DAY_MS,
  MONTH: 30 * DAY_MS,
  SESSION: DAY_MS
};
var UNIT_RANK = {
  HOUR: 0,
  SESSION: 0,
  DAY: 1,
  NIGHT: 1,
  WEEK: 2,
  MONTH: 3
};
var DEFAULT_WEEKEND = [6, 7];
var PricingError = class extends Error {
  constructor(issue) {
    super(issue.code);
    this.issue = issue;
  }
  issue;
};
function isoWeekday(isoDate2) {
  const [year = 0, month = 1, day = 1] = isoDate2.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}
function inDateWindow(date, from, to) {
  return (from === null || date >= from) && (to === null || date <= to);
}
function inSeason(date, season) {
  if (!season.recursYearly) return date >= season.startDate && date <= season.endDate;
  const day = date.slice(5);
  const start = season.startDate.slice(5);
  const end = season.endDate.slice(5);
  return start <= end ? day >= start && day <= end : day >= start || day <= end;
}
function inMinuteWindow(minute, start, end) {
  if (start === null || end === null) return true;
  return start <= end ? minute >= start && minute < end : minute >= start || minute < end;
}
function pick(candidates) {
  const rank = (scope) => scope === "ASSET" ? 1 : 0;
  return [...candidates].sort(
    (a, b) => rank(b.scope) - rank(a.scope) || b.priority - a.priority || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  )[0] ?? null;
}
function slicesFor(unit, quantity, start, timeZone) {
  const startDate = isoDateInZone(new Date(start), timeZone);
  const startTime = localDateTimeInZone(new Date(start), timeZone).slice(11);
  const slices = [];
  for (let index = 0; index < quantity; index += 1) {
    let instant;
    if (unit === DurationUnit.HOUR) {
      instant = start + index * HOUR_MS;
    } else if (unit === DurationUnit.SESSION) {
      instant = start;
    } else {
      const date = unit === DurationUnit.MONTH ? addMonthsToIsoDate(startDate, index) : addDaysToIsoDate(startDate, index * (unit === DurationUnit.WEEK ? 7 : 1));
      instant = zonedTimeToUtc(date, startTime, timeZone).getTime();
    }
    slices.push(sliceAt(instant, timeZone));
  }
  return slices;
}
function sliceAt(instant, timeZone) {
  const parts = zonedParts(new Date(instant), timeZone);
  return {
    start: instant,
    date: isoDateInZone(new Date(instant), timeZone),
    minute: parts.hour * 60 + parts.minute
  };
}
var LineBook = class {
  entries = /* @__PURE__ */ new Map();
  add(entry, amountMinor) {
    if (amountMinor === 0) return;
    const key = `${entry.kind}:${entry.refId ?? ""}`;
    const existing = this.entries.get(key);
    if (existing) {
      existing.quantity += 1;
      existing.amounts.push(amountMinor);
    } else {
      this.entries.set(key, { ...entry, quantity: 1, amounts: [amountMinor] });
    }
  }
  lines(unit) {
    return [...this.entries.values()].map((entry) => ({
      kind: entry.kind,
      refId: entry.refId,
      label: entry.label,
      quantity: entry.quantity,
      unit,
      unitAmountMinor: null,
      bps: entry.bps,
      amountMinor: sumMinor(entry.amounts)
    }));
  }
};
var RentalCalculator = class {
  constructor(input) {
    this.input = input;
  }
  input;
  money(amountMinor, currency) {
    const converted = this.input.convert(amountMinor, currency, this.input.currency);
    if (converted === null) throw new PricingError({ code: "CURRENCY" });
    return converted;
  }
  /** Signed change a modifier makes to `price` (FIXED_PRICE: difference to the fixed price). */
  adjustment(rule, price) {
    switch (rule.adjustmentType) {
      case "PERCENT":
        return rule.adjustmentValue === null ? 0 : applyBps(price, rule.adjustmentValue);
      case "AMOUNT":
        return rule.amountMinor === null ? 0 : this.money(rule.amountMinor, rule.currency);
      case "FIXED_PRICE":
        return rule.amountMinor === null ? 0 : this.money(rule.amountMinor, rule.currency) - price;
      case null:
        return 0;
    }
  }
  lineMeta(rule) {
    return {
      kind: rule.kind,
      refId: rule.id,
      label: null,
      bps: rule.adjustmentType === "PERCENT" ? rule.adjustmentValue : null
    };
  }
  rules(kind, unit) {
    return this.input.rules.filter(
      (rule) => rule.kind === kind && (rule.unit === null || rule.unit === unit)
    );
  }
  /** Base + season + day/time modifiers for each slice. */
  priceSlices(slices, unit, baseMinor, seasons, quantity) {
    const book = new LineBook();
    const dayRules = unit === null || unit === DurationUnit.HOUR || unit === DurationUnit.DAY || unit === DurationUnit.NIGHT || unit === DurationUnit.SESSION;
    const timeRules = unit === null || unit === DurationUnit.HOUR || unit === DurationUnit.SESSION;
    const specials = dayRules ? this.rules("SPECIAL_DAY", unit) : [];
    const weekends = dayRules ? this.rules("WEEKEND", unit) : [];
    const times = timeRules ? this.rules("TIME_OF_DAY", unit) : [];
    const totals = [];
    for (const slice of slices) {
      const weekday = isoWeekday(slice.date);
      const onDay = (rule, fallback = []) => {
        const days2 = rule.daysOfWeek.length > 0 ? rule.daysOfWeek : fallback;
        return inDateWindow(slice.date, rule.validFrom, rule.validTo) && (days2.length === 0 || days2.includes(weekday));
      };
      let price = baseMinor;
      const season = pick(
        seasons.filter((rate) => rate.unit === unit && inSeason(slice.date, rate)).map((rate) => ({ ...rate, id: rate.seasonId, priority: 0 }))
      );
      if (season) {
        if (season.minDuration !== null && quantity < season.minDuration) {
          throw new PricingError({
            code: "MIN_DURATION",
            minimum: season.minDuration,
            unit: season.unit
          });
        }
        const seasonal = season.amountMinor !== null ? this.money(season.amountMinor, season.currency) : applyBps(baseMinor, season.multiplierBps ?? BPS_DENOMINATOR);
        book.add(
          {
            kind: "SEASON",
            refId: season.seasonId,
            label: season.seasonName,
            bps: season.multiplierBps === null ? null : season.multiplierBps - BPS_DENOMINATOR
          },
          seasonal - baseMinor
        );
        price = seasonal;
      }
      let total = price;
      const special = pick(specials.filter((rule) => onDay(rule)));
      if (special) {
        const delta = this.adjustment(special, price);
        book.add(this.lineMeta(special), delta);
        total += delta;
      }
      if (special?.adjustmentType !== "FIXED_PRICE") {
        const weekend = special ? null : pick(weekends.filter((r) => onDay(r, DEFAULT_WEEKEND)));
        if (weekend) {
          const delta = this.adjustment(weekend, price);
          book.add(this.lineMeta(weekend), delta);
          total += delta;
        }
        const time2 = pick(
          times.filter(
            (rule) => onDay(rule) && inMinuteWindow(slice.minute, rule.startMinute, rule.endMinute)
          )
        );
        if (time2) {
          const delta = this.adjustment(time2, price);
          book.add(this.lineMeta(time2), delta);
          total += delta;
        }
      }
      totals.push(Math.max(0, total));
    }
    return { book, total: sumMinor(totals) };
  }
  timeBased(rate, quantity, start, seasons) {
    if (rate.minDuration !== null && quantity < rate.minDuration) {
      throw new PricingError({ code: "MIN_DURATION", minimum: rate.minDuration, unit: rate.unit });
    }
    const baseMinor = this.money(rate.amountMinor, rate.currency);
    const slices = slicesFor(rate.unit, quantity, start, this.input.timeZone);
    const { book, total } = this.priceSlices(slices, rate.unit, baseMinor, seasons, quantity);
    const firstDate = slices[0]?.date ?? isoDateInZone(new Date(start), this.input.timeZone);
    let rentalMinor = total;
    const discount = pick(
      this.rules("DURATION_DISCOUNT", rate.unit).filter(
        (rule) => quantity >= (rule.minDuration ?? 0) && (rule.maxDuration === null || quantity <= rule.maxDuration) && inDateWindow(firstDate, rule.validFrom, rule.validTo)
      )
    );
    if (discount) {
      const delta = discount.adjustmentType === "FIXED_PRICE" && discount.amountMinor !== null ? this.money(discount.amountMinor, discount.currency) * quantity - total : this.adjustment(discount, total);
      book.add(this.lineMeta(discount), delta);
      rentalMinor = Math.max(0, total + delta);
    }
    const base = {
      kind: "BASE",
      refId: null,
      label: null,
      quantity,
      unit: rate.unit,
      unitAmountMinor: baseMinor,
      bps: null,
      amountMinor: baseMinor * quantity
    };
    return { unit: rate.unit, quantity, lines: [base, ...book.lines(rate.unit)], rentalMinor };
  }
  /**
   * EXTRA_GUEST: each guest above the included ones (per unit booked) pays the rule's amount, or a
   * percentage of the unit rate, for every billed unit. Applied after rooms are multiplied and
   * outside duration discounts.
   */
  extraGuests(rental, rates, units, start) {
    if (rental.unit === null) return null;
    const firstDate = isoDateInZone(new Date(start), this.input.timeZone);
    const rule = pick(
      this.rules("EXTRA_GUEST", rental.unit).filter(
        (candidate) => inDateWindow(firstDate, candidate.validFrom, candidate.validTo)
      )
    );
    if (!rule) return null;
    const extra = Math.max(0, this.input.guests - (rule.includedGuests ?? 0) * units);
    if (extra === 0) return null;
    const rate = rates.find((candidate) => candidate.unit === rental.unit);
    const perGuest = rule.adjustmentType === "PERCENT" ? rate && rule.adjustmentValue !== null ? applyBps(this.money(rate.amountMinor, rate.currency), rule.adjustmentValue) : 0 : rule.amountMinor === null ? 0 : this.money(rule.amountMinor, rule.currency);
    if (perGuest <= 0) return null;
    return {
      kind: "EXTRA_GUEST",
      refId: rule.id,
      label: null,
      quantity: extra,
      unit: rental.unit,
      unitAmountMinor: perGuest,
      bps: rule.adjustmentType === "PERCENT" ? rule.adjustmentValue : null,
      amountMinor: perGuest * extra * rental.quantity
    };
  }
  route(fareMinor, legs) {
    const slices = legs.map((leg) => sliceAt(leg.start, this.input.timeZone));
    const { book, total } = this.priceSlices(slices, null, fareMinor, [], legs.length);
    const base = {
      kind: "BASE",
      refId: null,
      label: null,
      quantity: legs.length,
      unit: null,
      unitAmountMinor: fareMinor,
      bps: null,
      amountMinor: fareMinor * legs.length
    };
    return {
      unit: null,
      quantity: legs.length,
      lines: [base, ...book.lines(null)],
      rentalMinor: total
    };
  }
};
function rentalPrice(input, calculator) {
  const { tariff, period } = input;
  if (tariff.model === "ROUTE_BASED") {
    if (!tariff.fare) throw new PricingError({ code: "NO_FARE" });
    const fare = input.convert(tariff.fare.amountMinor, tariff.fare.currency, input.currency);
    if (fare === null) throw new PricingError({ code: "CURRENCY" });
    const legs = period.kind === "TRIP" ? period.legs : [period.leg];
    return calculator.route(fare, legs);
  }
  if (period.kind === "TRIP") throw new PricingError({ code: "NO_RATE" });
  if (period.kind === "FIXED") {
    const rate = tariff.rates.find((candidate) => candidate.unit === period.unit);
    if (!rate) throw new PricingError({ code: "NO_RATE", unit: period.unit });
    return calculator.timeBased(rate, period.quantity, period.leg.start, tariff.seasons);
  }
  const length = period.leg.end - period.leg.start;
  const options = [];
  let shortest = null;
  for (const rate of [...tariff.rates].sort((a, b) => UNIT_RANK[a.unit] - UNIT_RANK[b.unit])) {
    const quantity = Math.max(1, Math.ceil(length / UNIT_MS[rate.unit]));
    try {
      options.push(calculator.timeBased(rate, quantity, period.leg.start, tariff.seasons));
    } catch (error) {
      if (!(error instanceof PricingError) || error.issue.code !== "MIN_DURATION") throw error;
      shortest ??= error.issue;
    }
  }
  const best = options.reduce(
    (cheapest, option) => !cheapest || option.rentalMinor < cheapest.rentalMinor ? option : cheapest,
    null
  );
  if (best) return best;
  throw new PricingError(shortest ?? { code: "NO_RATE" });
}
function addOnQuantity(addOn, requested, booking) {
  const clamp = (value, max) => Math.min(Math.max(1, Math.floor(value)), max);
  switch (addOn.pricingType) {
    case "PER_BOOKING":
      return 1;
    case "PER_PERSON":
      return Math.max(1, booking.guests);
    case "PER_DAY":
      return Math.max(1, booking.days);
    case "PER_HOUR":
      return clamp(requested ?? 1, addOn.maxQuantity ?? Math.max(1, booking.hours));
    case "PER_UNIT":
      return clamp(requested ?? 1, addOn.maxQuantity ?? 20);
  }
}
function allocate(discount, amounts) {
  const total = amounts.reduce((sum, amount) => sum + BigInt(amount), 0n);
  if (discount === 0 || total === 0n) return amounts.map(() => 0);
  const shares = amounts.map((amount) => Number(BigInt(discount) * BigInt(amount) / total));
  const remainder = discount - shares.reduce((sum, share) => sum + share, 0);
  let largest = 0;
  amounts.forEach((amount, index) => {
    if (amount > (amounts[largest] ?? 0)) largest = index;
  });
  shares[largest] = (shares[largest] ?? 0) + remainder;
  return shares;
}
function computePrice(input) {
  const { period } = input;
  const calculator = new RentalCalculator(input);
  const units = Math.max(1, Math.trunc(input.units ?? 1));
  const single = rentalPrice(input, calculator);
  const legs = period.kind === "TRIP" ? period.legs : [period.leg];
  const start = Math.min(...legs.map((leg) => leg.start));
  const scaled = units === 1 ? single : {
    ...single,
    lines: single.lines.map((line) => ({ ...line, amountMinor: line.amountMinor * units })),
    rentalMinor: single.rentalMinor * units
  };
  const extraGuests = input.tariff.model === "TIME_BASED" ? calculator.extraGuests(scaled, input.tariff.rates, units, start) : null;
  const rental = extraGuests ? {
    ...scaled,
    lines: [...scaled.lines, extraGuests],
    rentalMinor: scaled.rentalMinor + extraGuests.amountMinor
  } : scaled;
  const busyMs = legs.reduce((sum, leg) => sum + (leg.end - leg.start), 0);
  const usage = {
    guests: input.guests,
    hours: Math.max(1, Math.ceil(busyMs / HOUR_MS)),
    days: Math.max(1, Math.ceil(busyMs / DAY_MS))
  };
  const available = new Map(input.addOns.map((addOn) => [addOn.id, addOn]));
  const requested = /* @__PURE__ */ new Map();
  for (const selection of input.selectedAddOns) {
    if (!available.has(selection.addOnId)) {
      throw new PricingError({ code: "ADD_ON_UNAVAILABLE", addOnId: selection.addOnId });
    }
    requested.set(selection.addOnId, selection);
  }
  const addOns = [];
  for (const addOn of input.addOns) {
    const selection = requested.get(addOn.id);
    if (!addOn.isIncluded && !selection) continue;
    const quantity = addOnQuantity(addOn, selection?.quantity, usage);
    if (addOn.isIncluded) {
      addOns.push({
        addOnId: addOn.id,
        pricingType: addOn.pricingType,
        quantity,
        unitPriceMinor: 0,
        totalMinor: 0,
        taxRateBps: addOn.taxRateBps,
        included: true
      });
      continue;
    }
    if (addOn.leadTimeHours > 0 && start - input.now < addOn.leadTimeHours * HOUR_MS) {
      throw new PricingError({ code: "ADD_ON_LEAD_TIME", addOnId: addOn.id });
    }
    const unitPrice = input.convert(addOn.unitPriceMinor, addOn.currency, input.currency);
    if (unitPrice === null) throw new PricingError({ code: "CURRENCY", addOnId: addOn.id });
    addOns.push({
      addOnId: addOn.id,
      pricingType: addOn.pricingType,
      quantity,
      unitPriceMinor: unitPrice,
      totalMinor: unitPrice * quantity,
      taxRateBps: addOn.taxRateBps,
      included: false
    });
  }
  const addOnsMinor = sumMinor(addOns.map((line) => line.totalMinor));
  const subtotalMinor = sumMinor([rental.rentalMinor, addOnsMinor]);
  const coupon = input.coupon ? evaluateCoupon(input.coupon, {
    categoryId: input.categoryId,
    subtotalMinor,
    currency: input.currency,
    now: input.now,
    convert: input.convert
  }) : null;
  const couponMinor = coupon?.discountMinor ?? 0;
  const manualDiscountMinor = Math.min(
    Math.max(0, Math.trunc(input.manualDiscountMinor ?? 0)),
    Math.max(0, subtotalMinor - couponMinor)
  );
  const discountMinor = couponMinor + manualDiscountMinor;
  const groups = /* @__PURE__ */ new Map();
  const addToGroup = (rate, amount) => groups.set(rate, (groups.get(rate) ?? 0) + amount);
  addToGroup(input.vatRateBps, rental.rentalMinor);
  for (const line of addOns) if (line.totalMinor > 0) addToGroup(line.taxRateBps, line.totalMinor);
  const rates = [...groups.keys()].sort((a, b) => b - a);
  const shares = allocate(
    discountMinor,
    rates.map((rate) => groups.get(rate) ?? 0)
  );
  const taxes = rates.map((rateBps, index) => {
    const baseMinor = (groups.get(rateBps) ?? 0) - (shares[index] ?? 0);
    return { rateBps, baseMinor, amountMinor: applyBps(baseMinor, rateBps) };
  }).filter((line) => line.baseMinor > 0 && line.rateBps > 0);
  const taxMinor = sumMinor(taxes.map((line) => line.amountMinor));
  const totalMinor = sumMinor([subtotalMinor, -discountMinor, taxMinor]);
  const balanceDueAt = start - input.balanceDueDays * DAY_MS;
  const depositPossible = input.depositBps > 0 && input.depositBps < BPS_DENOMINATOR && balanceDueAt > input.now;
  return {
    currency: input.currency,
    unit: rental.unit,
    quantity: rental.quantity,
    units,
    lines: rental.lines,
    rentalMinor: rental.rentalMinor,
    addOns,
    addOnsMinor,
    subtotalMinor,
    coupon,
    manualDiscountMinor,
    discountMinor,
    taxes,
    taxMinor,
    totalMinor,
    depositBps: input.depositBps,
    depositMinor: depositPossible ? applyBps(totalMinor, input.depositBps) : null,
    balanceDueAt: depositPossible ? balanceDueAt : null
  };
}
function priceBooking(input) {
  try {
    return { ok: true, breakdown: computePrice(input) };
  } catch (error) {
    if (error instanceof PricingError) return { ok: false, issue: error.issue };
    throw error;
  }
}

// src/booking/selection.ts
var TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
var START_TIME_STEP_MINUTES = 30;
var DEFAULT_TRIP_MINUTES = 90;
var DEFAULT_CHECK_IN_TIME = "15:00";
var DEFAULT_CHECK_OUT_TIME = "11:00";
var fail = (code, field = null) => ({
  ok: false,
  issue: { code, field }
});
function find(fields, values, kind) {
  for (const field of fields) {
    const value = values[field.key];
    if (value?.kind === kind) {
      return { field, value };
    }
  }
  return null;
}
function calendarPeriodEnd(date, unit, quantity) {
  if (unit === DurationUnit.MONTH) return addMonthsToIsoDate(date, quantity);
  return addDaysToIsoDate(date, quantity * (unit === DurationUnit.WEEK ? 7 : 1));
}
function resolveBookingSelection(fields, values, extras, context) {
  const { timeZone } = context;
  for (const field of fields) {
    if (field.required && field.type !== "LOCATION" && !values[field.key]) {
      return fail("REQUIRED", field.key);
    }
  }
  let guests = null;
  let luggage = null;
  let units = 1;
  let originSlug = null;
  let destinationSlug = null;
  let roundTrip = false;
  const options = {};
  for (const field of fields) {
    const value = values[field.key];
    if (!value) continue;
    switch (field.type) {
      case "GUESTS":
      case "PASSENGERS":
        if (value.kind === "count") guests ??= value.value;
        break;
      case "LUGGAGE":
        if (value.kind === "count") luggage ??= value.value;
        break;
      case "ROOMS":
        if (value.kind === "count") units = Math.max(1, value.value);
        break;
      case "ORIGIN":
        if (value.kind === "location") originSlug ??= value.slug;
        break;
      case "DESTINATION":
        if (value.kind === "location") destinationSlug ??= value.slug;
        break;
      case "TOGGLE":
        if (value.kind !== "toggle") break;
        if (field.roundTrip) roundTrip ||= value.value;
        else options[field.key] = value.value;
        break;
      case "TRIP_TYPE":
        if (value.kind !== "tripType") break;
        roundTrip ||= value.value === "ROUND_TRIP";
        options[field.key] = value.value;
        break;
      case "SELECT":
      case "ADDRESS":
        if (value.kind === "select" || value.kind === "text") options[field.key] = value.value;
        break;
    }
  }
  const base = { guests, luggage, units, originSlug, destinationSlug, options };
  const single = (period) => ({
    ok: true,
    selection: { ...base, period, start: period.leg.start, end: period.leg.end }
  });
  const span = find(fields, values, "datetimeRange");
  if (span) {
    const start = localDateTimeToUtc(span.value.from, timeZone).getTime();
    const end = localDateTimeToUtc(span.value.to, timeZone).getTime();
    if (end <= start) return fail("INVALID_RANGE", span.field.key);
    return single({ kind: "SPAN", leg: { start, end } });
  }
  const range = find(fields, values, "dateRange");
  if (range) {
    const { from, to } = range.value;
    if (range.field.stay) {
      if (to <= from) return fail("INVALID_RANGE", range.field.key);
      const checkIn = range.field.checkInTime ?? DEFAULT_CHECK_IN_TIME;
      const checkOut = range.field.checkOutTime ?? DEFAULT_CHECK_OUT_TIME;
      return single({
        kind: "FIXED",
        unit: DurationUnit.NIGHT,
        quantity: diffIsoDays(from, to),
        leg: {
          start: zonedTimeToUtc(from, checkIn, timeZone).getTime(),
          end: zonedTimeToUtc(to, checkOut, timeZone).getTime()
        }
      });
    }
    if (to < from) return fail("INVALID_RANGE", range.field.key);
    const start = zonedTimeToUtc(from, "00:00", timeZone).getTime();
    const end = zonedTimeToUtc(addDaysToIsoDate(to, 1), "00:00", timeZone).getTime();
    const quantity = Math.round((end - start) / 864e5);
    return single({ kind: "FIXED", unit: DurationUnit.DAY, quantity, leg: { start, end } });
  }
  const date = find(fields, values, "date");
  const duration = find(fields, values, "duration");
  if (date && duration) {
    const { unit, value: quantity } = duration.value;
    if (unit === DurationUnit.HOUR) {
      const time2 = extras.startTime ?? "";
      if (!TIME_OF_DAY_PATTERN.test(time2)) return fail("START_TIME_REQUIRED", date.field.key);
      const start2 = zonedTimeToUtc(date.value.value, time2, timeZone).getTime();
      return single({
        kind: "FIXED",
        unit,
        quantity,
        leg: { start: start2, end: start2 + quantity * 36e5 }
      });
    }
    const start = zonedTimeToUtc(date.value.value, "00:00", timeZone).getTime();
    const endDate = calendarPeriodEnd(date.value.value, unit, quantity);
    const end = zonedTimeToUtc(endDate, "00:00", timeZone).getTime();
    return single({ kind: "FIXED", unit, quantity, leg: { start, end } });
  }
  const sessions = context.sessions;
  if (date && sessions && sessions.times.length > 0) {
    const time2 = extras.startTime ?? "";
    if (!sessions.times.includes(time2)) return fail("START_TIME_REQUIRED", date.field.key);
    const start = zonedTimeToUtc(date.value.value, time2, timeZone).getTime();
    return single({
      kind: "FIXED",
      unit: DurationUnit.SESSION,
      quantity: 1,
      leg: { start, end: start + sessions.minutes * 6e4 }
    });
  }
  const pickup = find(fields, values, "datetime");
  if (pickup && originSlug && destinationSlug) {
    const legMs = (context.tripMinutes ?? DEFAULT_TRIP_MINUTES) * 6e4;
    const start = localDateTimeToUtc(pickup.value.value, timeZone).getTime();
    const legs = [{ start, end: start + legMs }];
    if (roundTrip) {
      const returnAt = extras.returnAt ?? "";
      let back;
      try {
        back = localDateTimeToUtc(returnAt, timeZone).getTime();
      } catch {
        return fail("RETURN_REQUIRED", pickup.field.key);
      }
      if (back < start + legMs) return fail("INVALID_RANGE", pickup.field.key);
      legs.push({ start: back, end: back + legMs });
    }
    const end = Math.max(...legs.map((leg) => leg.end));
    return { ok: true, selection: { ...base, period: { kind: "TRIP", legs }, start, end } };
  }
  return fail("NOT_BOOKABLE");
}

// src/finance/invoice.ts
function multiplyMinor(unitMinor, quantity) {
  const milli = Math.round(quantity * 1e3);
  if (!Number.isSafeInteger(milli)) throw new MoneyError(`Invalid quantity ${quantity}`);
  const product = BigInt(assertSafeMinor(unitMinor)) * BigInt(milli);
  const magnitude = product < 0n ? -product : product;
  let quotient = magnitude / 1000n;
  if (magnitude % 1000n * 2n >= 1000n) quotient += 1n;
  return assertSafeMinor(Number(product < 0n ? -quotient : quotient));
}
function calculateInvoice(lines) {
  const calculated = lines.map((line) => {
    const netMinor = multiplyMinor(line.unitPriceMinor, line.quantity);
    return { ...line, netMinor, taxMinor: applyBps(netMinor, line.taxRateBps) };
  });
  const rates = [...new Set(calculated.map((line) => line.taxRateBps))].sort((a, b) => b - a);
  const taxes = rates.map((rateBps) => {
    const group = calculated.filter((line) => line.taxRateBps === rateBps);
    const baseMinor = sumMinor(group.map((line) => line.netMinor));
    const amountMinor = applyBps(baseMinor, rateBps);
    const drift = amountMinor - sumMinor(group.map((line) => line.taxMinor));
    if (drift !== 0) {
      const largest = group.reduce((best, line) => line.netMinor > best.netMinor ? line : best);
      largest.taxMinor += drift;
    }
    return { rateBps, baseMinor, amountMinor };
  });
  const subtotalMinor = sumMinor(calculated.map((line) => line.netMinor));
  const taxMinor = sumMinor(taxes.map((tax) => tax.amountMinor));
  return {
    lines: calculated,
    taxes,
    subtotalMinor,
    taxMinor,
    totalMinor: sumMinor([subtotalMinor, taxMinor])
  };
}
function netOfGross(grossMinor, taxRateBps) {
  assertSafeMinor(grossMinor);
  let net = Math.round(grossMinor * BPS_DENOMINATOR / (BPS_DENOMINATOR + taxRateBps));
  while (net > 0 && net + applyBps(net, taxRateBps) > grossMinor) net -= 1;
  while (net + 1 + applyBps(net + 1, taxRateBps) <= grossMinor) net += 1;
  return net;
}
function invoiceLinesWithDiscount(items, taxes, discountDescription) {
  const discounts = [];
  for (const tax of taxes) {
    const itemsNet = sumMinor(
      items.filter((item) => item.taxRateBps === tax.rateBps).map((item) => multiplyMinor(item.unitPriceMinor, item.quantity))
    );
    const discount = itemsNet - tax.baseMinor;
    if (discount > 0) {
      discounts.push({
        description: discountDescription,
        quantity: 1,
        unitPriceMinor: -discount,
        taxRateBps: tax.rateBps
      });
    }
  }
  return [...items, ...discounts];
}
function includedTaxMinor(grossMinor, rateBps) {
  assertSafeMinor(grossMinor);
  if (rateBps <= 0) return 0;
  const net = BigInt(grossMinor) * BigInt(BPS_DENOMINATOR) * 2n / BigInt(BPS_DENOMINATOR + rateBps);
  const netMinor = Number((net + 1n) / 2n);
  return grossMinor - netMinor;
}
function formatInvoiceNumber(prefix, year, sequence) {
  if (!/^[A-Z0-9]{3}$/.test(prefix)) throw new MoneyError(`Invalid invoice prefix "${prefix}"`);
  if (!Number.isInteger(sequence) || sequence < 1 || sequence > 999999999) {
    throw new MoneyError(`Invoice sequence out of range: ${sequence}`);
  }
  return `${prefix}${year}${String(sequence).padStart(9, "0")}`;
}

// src/finance/refunds.ts
function remainingMinor(payment) {
  return Math.max(0, payment.amountMinor - payment.refundedMinor);
}
function refundableMinor(payments) {
  return sumMinor(payments.map(remainingMinor));
}
function allocateRefund(payments, amountMinor) {
  if (!Number.isSafeInteger(amountMinor) || amountMinor <= 0) {
    throw new MoneyError(`Refund amount must be a positive integer, received ${amountMinor}`);
  }
  if (amountMinor > refundableMinor(payments)) {
    throw new MoneyError("Refund exceeds the refundable amount");
  }
  const ordered = [...payments].sort(
    (a, b) => Date.parse(b.paidAt) - Date.parse(a.paidAt) || b.id.localeCompare(a.id)
  );
  const allocations = [];
  let left = amountMinor;
  for (const payment of ordered) {
    if (left === 0) break;
    const take = Math.min(left, remainingMinor(payment));
    if (take === 0) continue;
    allocations.push({ paymentId: payment.id, amountMinor: take });
    left -= take;
  }
  return allocations;
}

// src/services/quote.ts
function lineOf(line) {
  const grossMinor = multiplyMinor(line.unitPriceMinor, line.quantity);
  const lineDiscountMinor = line.type === "DISCOUNT" || line.discountBps <= 0 ? 0 : applyBps(grossMinor, line.discountBps);
  return { ...line, grossMinor, lineDiscountMinor, netMinor: grossMinor - lineDiscountMinor };
}
function quoteInvoiceLines(lines, discountLabel) {
  return lines.flatMap((line) => {
    const calculated = lineOf(line);
    const base = {
      description: line.description,
      quantity: line.quantity,
      unit: line.unit ?? null,
      unitPriceMinor: line.unitPriceMinor,
      taxRateBps: line.taxRateBps
    };
    if (calculated.lineDiscountMinor <= 0) return [base];
    return [
      base,
      {
        description: discountLabel(line),
        quantity: 1,
        unit: null,
        unitPriceMinor: -calculated.lineDiscountMinor,
        taxRateBps: line.taxRateBps
      }
    ];
  });
}
function calculateQuote(lines) {
  const calculated = lines.map(lineOf);
  const invoice = calculateInvoice(quoteInvoiceLines(lines, (line) => line.description));
  const subtotalMinor = sumMinor(
    calculated.filter((line) => line.grossMinor > 0).map((line) => line.grossMinor)
  );
  return {
    lines: calculated,
    taxes: invoice.taxes,
    subtotalMinor,
    discountMinor: subtotalMinor - invoice.subtotalMinor,
    netMinor: invoice.subtotalMinor,
    taxMinor: invoice.taxMinor,
    totalMinor: invoice.totalMinor
  };
}
function quoteTotalsIssue(totals) {
  if (totals.taxes.some((tax) => tax.baseMinor < 0)) return "NEGATIVE_BASE";
  if (totals.totalMinor <= 0) return "EMPTY";
  return null;
}
function quotePaymentOptions(totalMinor, paidMinor, depositBps) {
  if (totalMinor <= 0 || paidMinor >= totalMinor) return [];
  if (paidMinor > 0) return [{ kind: "BALANCE", amountMinor: totalMinor - paidMinor }];
  const options = [];
  const deposit = depositBps && depositBps > 0 ? applyBps(totalMinor, depositBps) : 0;
  if (deposit > 0 && deposit < totalMinor) options.push({ kind: "DEPOSIT", amountMinor: deposit });
  options.push({ kind: "QUOTE", amountMinor: totalMinor });
  return options;
}

// src/services/recurrence.ts
var RECURRENCE_FREQUENCIES = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"];
var WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
var MAX_INTERVAL = 52;
var MAX_STEPS = 1e4;
function isFrequency(value) {
  return RECURRENCE_FREQUENCIES.includes(value);
}
function isWeekday(value) {
  return WEEKDAYS.includes(value);
}
function integer(value, min, max) {
  if (!/^\d{1,3}$/.test(value)) return null;
  const number = Number(value);
  return number >= min && number <= max ? number : null;
}
function parseRecurrence(rule) {
  const parts = rule.trim().toUpperCase().replace(/^RRULE:/, "").split(";").filter(Boolean);
  const values = /* @__PURE__ */ new Map();
  for (const part of parts) {
    const [key, value, extra] = part.split("=");
    if (!key || !value || extra !== void 0 || values.has(key)) return null;
    values.set(key, value);
  }
  const freq = values.get("FREQ");
  if (!freq || !isFrequency(freq)) return null;
  const recurrence = { freq, interval: 1 };
  for (const [key, value] of values) {
    switch (key) {
      case "FREQ":
        break;
      case "INTERVAL": {
        const interval = integer(value, 1, MAX_INTERVAL);
        if (interval === null) return null;
        recurrence.interval = interval;
        break;
      }
      case "BYDAY": {
        if (freq !== "WEEKLY") return null;
        const days2 = value.split(",");
        if (!days2.every(isWeekday) || new Set(days2).size !== days2.length) return null;
        recurrence.byDay = WEEKDAYS.filter((day) => days2.includes(day));
        break;
      }
      case "BYMONTHDAY": {
        if (freq !== "MONTHLY" && freq !== "YEARLY") return null;
        const day = integer(value, 1, 31);
        if (day === null) return null;
        recurrence.byMonthDay = day;
        break;
      }
      case "BYMONTH": {
        if (freq !== "YEARLY") return null;
        const month = integer(value, 1, 12);
        if (month === null) return null;
        recurrence.byMonth = month;
        break;
      }
      default:
        return null;
    }
  }
  return recurrence;
}
function formatRecurrence(recurrence) {
  const parts = [`FREQ=${recurrence.freq}`];
  if (recurrence.interval > 1) parts.push(`INTERVAL=${recurrence.interval}`);
  if (recurrence.freq === "WEEKLY" && recurrence.byDay?.length) {
    parts.push(`BYDAY=${recurrence.byDay.join(",")}`);
  }
  if (recurrence.freq === "YEARLY" && recurrence.byMonth)
    parts.push(`BYMONTH=${recurrence.byMonth}`);
  if ((recurrence.freq === "MONTHLY" || recurrence.freq === "YEARLY") && recurrence.byMonthDay) {
    parts.push(`BYMONTHDAY=${recurrence.byMonthDay}`);
  }
  return parts.join(";");
}
function weekdayOf(isoDate2) {
  const day = (/* @__PURE__ */ new Date(`${isoDate2}T12:00:00Z`)).getUTCDay();
  return WEEKDAYS[(day + 6) % 7] ?? "MO";
}
function withDay(isoDate2, day) {
  const [year = 0, month = 1] = isoDate2.split("-").map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${isoDate2.slice(0, 8)}${String(Math.min(day, last)).padStart(2, "0")}`;
}
function* occurrences(recurrence, startsOn) {
  if (!ISO_DATE_PATTERN.test(startsOn)) return;
  const step = recurrence.interval;
  const startDay = Number(startsOn.slice(8, 10));
  switch (recurrence.freq) {
    case "DAILY":
      for (let index = 0; index < MAX_STEPS; index += 1) {
        yield addDaysToIsoDate(startsOn, index * step);
      }
      return;
    case "WEEKLY": {
      const days2 = recurrence.byDay?.length ? recurrence.byDay : [weekdayOf(startsOn)];
      const monday = addDaysToIsoDate(startsOn, -WEEKDAYS.indexOf(weekdayOf(startsOn)));
      for (let week = 0; week < MAX_STEPS; week += 1) {
        const weekStart = addDaysToIsoDate(monday, week * 7 * step);
        for (const day of days2) {
          const date = addDaysToIsoDate(weekStart, WEEKDAYS.indexOf(day));
          if (diffIsoDays(startsOn, date) >= 0) yield date;
        }
      }
      return;
    }
    case "MONTHLY": {
      const day = recurrence.byMonthDay ?? startDay;
      const firstOfMonth = `${startsOn.slice(0, 8)}01`;
      for (let index = 0; index < MAX_STEPS; index += 1) {
        const date = withDay(addMonthsToIsoDate(firstOfMonth, index * step), day);
        if (diffIsoDays(startsOn, date) >= 0) yield date;
      }
      return;
    }
    case "YEARLY": {
      const month = recurrence.byMonth ?? Number(startsOn.slice(5, 7));
      const day = recurrence.byMonthDay ?? startDay;
      const year = Number(startsOn.slice(0, 4));
      for (let index = 0; index < MAX_STEPS; index += 1) {
        const anchor = `${year + index * step}-${String(month).padStart(2, "0")}-01`;
        const date = withDay(anchor, day);
        if (diffIsoDays(startsOn, date) >= 0) yield date;
      }
      return;
    }
  }
}
function nextOccurrence(recurrence, startsOn, from, endsOn = null) {
  for (const date of occurrences(recurrence, startsOn)) {
    if (endsOn && diffIsoDays(endsOn, date) > 0) return null;
    if (diffIsoDays(from, date) >= 0) return date;
  }
  return null;
}
function upcomingOccurrences(recurrence, startsOn, from, count, endsOn = null) {
  const result = [];
  let cursor = from;
  while (cursor && result.length < count) {
    const next = nextOccurrence(recurrence, startsOn, cursor, endsOn);
    if (!next) break;
    result.push(next);
    cursor = addDaysToIsoDate(next, 1);
  }
  return result;
}

// src/services/staff.ts
var DOCUMENT_EXPIRY_WARNING_DAYS = 30;
function documentExpiryState(expiresAt, today, warningDays = DOCUMENT_EXPIRY_WARNING_DAYS) {
  if (!expiresAt) return "NO_EXPIRY";
  const daysLeft = diffIsoDays(today, expiresAt.slice(0, 10));
  if (daysLeft < 0) return "EXPIRED";
  return daysLeft <= warningDays ? "EXPIRING" : "VALID";
}
var time = (value) => (value instanceof Date ? value : new Date(value)).getTime();
function rangesOverlap(a, b) {
  return time(a.startsAt) < time(b.endsAt) && time(b.startsAt) < time(a.endsAt);
}
var BLOCKING_AVAILABILITY = ["UNAVAILABLE", "LEAVE", "SICK"];
function nextStaffCode(existing) {
  const highest = existing.reduce((max, code) => {
    const match = /^STF-(\d+)$/.exec(code);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
  return `STF-${String(highest + 1).padStart(3, "0")}`;
}
function maskIdentifier(value) {
  if (!value) return null;
  const visible = value.slice(-4);
  return `${"\u2022".repeat(Math.max(0, value.length - visible.length))}${visible}`;
}
var localeSchema = z.enum(Locale);
var currencySchema = z.enum(Currency);
var uuidSchema = z.uuid();
var idParamSchema = z.object({ id: z.uuid() });
var localizedTextSchema = z.object({
  tr: z.string().trim().min(1, "validation.required"),
  en: z.string().trim().optional(),
  ar: z.string().trim().optional(),
  ru: z.string().trim().optional()
});
function pickLocalized(text7, locale, fallback = DEFAULT_LOCALE) {
  if (!text7) return "";
  return text7[locale] ?? text7[fallback] ?? Object.values(text7).find(Boolean) ?? "";
}
var phoneSchema = z.string().trim().regex(/^\+[1-9]\d{7,14}$/, "validation.phone");
function normalizeTag(value) {
  return value.trim().toLowerCase().normalize("NFD").replace(/\u0307/g, "").normalize("NFC");
}
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20)
});
function paginate(items, total, { page, pageSize }) {
  return { items, page, pageSize, total, totalPages: Math.max(1, Math.ceil(total / pageSize)) };
}

// src/schemas/auth.ts
var emailSchema = z.string().trim().toLowerCase().pipe(z.email({ error: "validation.email" }).max(254, "validation.email"));
var PASSWORD_MIN_LENGTH = 10;
var PASSWORD_MAX_LENGTH = 128;
var passwordSchema = z.string().min(PASSWORD_MIN_LENGTH, "validation.password.min").max(PASSWORD_MAX_LENGTH, "validation.password.max").regex(/\p{L}/u, "validation.password.letter").regex(/\d/, "validation.password.digit");
var nameSchema = z.string().trim().min(1, "validation.required").max(80, "validation.tooLong");
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "validation.required").max(PASSWORD_MAX_LENGTH)
});
var registerSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  phone: phoneSchema.optional(),
  locale: localeSchema.default(DEFAULT_LOCALE),
  /** KVKK aydınlatma metni + üyelik sözleşmesi. Required to register. */
  acceptTerms: z.literal(true, { error: "validation.acceptTerms" }),
  marketingOptIn: z.boolean().default(false)
});
var updateProfileSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  phone: phoneSchema.nullable()
});
var forgotPasswordSchema = z.object({ email: emailSchema });
var resetPasswordSchema = z.object({
  token: z.string().min(20, "validation.token").max(256),
  password: passwordSchema
});
var verifyEmailSchema = z.object({
  token: z.string().min(20, "validation.token").max(256)
});
var totpCodeSchema = z.string().trim().regex(/^\d{6}$/, "validation.totp");
var recoveryCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z0-9]{5}-[A-Z0-9]{5}$/, "validation.recoveryCode");
var challengeTokenSchema = z.string().min(20).max(2048);
var twoFactorSetupSchema = z.object({ challengeToken: challengeTokenSchema });
var twoFactorEnableSchema = z.object({
  challengeToken: challengeTokenSchema,
  code: totpCodeSchema
});
var twoFactorVerifySchema = z.object({
  challengeToken: challengeTokenSchema,
  code: totpCodeSchema.optional(),
  recoveryCode: recoveryCodeSchema.optional()
}).refine((v) => (v.code ? 1 : 0) + (v.recoveryCode ? 1 : 0) === 1, {
  error: "validation.totpOrRecovery",
  path: ["code"]
});
var AttributeFieldType = createEnum([
  "TEXT",
  "TEXTAREA",
  "NUMBER",
  "INTEGER",
  "BOOLEAN",
  "SELECT",
  "MULTI_SELECT",
  "DATE",
  "YEAR"
]);
var attributeOptionSchema = z.object({
  value: z.string().trim().min(1).max(64),
  label: localizedTextSchema
});
var attributeFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-zA-Z0-9]{0,47}$/, "validation.attributeKey"),
  type: z.enum(AttributeFieldType),
  label: localizedTextSchema,
  helpText: localizedTextSchema.optional(),
  /** Display unit, e.g. "m", "km", "kn". */
  unit: z.string().max(16).optional(),
  group: z.string().max(40).optional(),
  required: z.boolean().default(false),
  /** Exposed as a filter on the results page. */
  filterable: z.boolean().default(false),
  /** Shown as a short spec on listing cards. */
  showOnCard: z.boolean().default(false),
  min: z.number().optional(),
  max: z.number().optional(),
  options: z.array(attributeOptionSchema).optional()
}).superRefine((field, ctx) => {
  const needsOptions = field.type === "SELECT" || field.type === "MULTI_SELECT";
  if (needsOptions && (!field.options || field.options.length === 0)) {
    ctx.addIssue({ code: "custom", path: ["options"], message: "validation.optionsRequired" });
  }
  if (field.min !== void 0 && field.max !== void 0 && field.min > field.max) {
    ctx.addIssue({ code: "custom", path: ["min"], message: "validation.minGreaterThanMax" });
  }
});
var attributeSchemaDefinition = z.array(attributeFieldSchema).superRefine((fields, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  fields.forEach((field, index) => {
    if (seen.has(field.key)) {
      ctx.addIssue({ code: "custom", path: [index, "key"], message: "validation.duplicateKey" });
    }
    seen.add(field.key);
  });
});
var typeError = (fallback) => ({
  error: (issue) => issue.input === void 0 ? "validation.required" : fallback
});
function buildFieldValidator(field) {
  switch (field.type) {
    case "TEXT":
    case "TEXTAREA": {
      const schema = z.string(typeError("validation.invalid")).trim().max(field.type === "TEXT" ? 500 : 5e3, "validation.tooLong");
      return field.required ? schema.min(1, "validation.required") : schema;
    }
    case "NUMBER":
    case "INTEGER": {
      let schema = z.number(typeError("validation.number"));
      if (field.type === "INTEGER") schema = schema.int("validation.integer");
      if (field.min !== void 0) schema = schema.min(field.min, "validation.outOfRange");
      if (field.max !== void 0) schema = schema.max(field.max, "validation.outOfRange");
      return schema;
    }
    case "YEAR":
      return z.number(typeError("validation.number")).int("validation.integer").min(field.min ?? 1900, "validation.outOfRange").max(field.max ?? 2100, "validation.outOfRange");
    case "BOOLEAN":
      return z.boolean(typeError("validation.invalid"));
    case "DATE":
      return z.iso.date(typeError("validation.date"));
    case "SELECT":
    case "MULTI_SELECT": {
      const values = (field.options ?? []).map((o) => o.value);
      const single = z.string(typeError("validation.invalidOption")).refine((v) => values.includes(v), "validation.invalidOption");
      if (field.type === "SELECT") return single;
      const list = z.array(single, typeError("validation.invalidOption"));
      return field.required ? list.min(1, "validation.required") : list;
    }
  }
}
function buildAttributesSchema(fields) {
  const shape = {};
  for (const field of fields) {
    const validator = buildFieldValidator(field);
    shape[field.key] = field.required ? validator : validator.optional();
  }
  return z.strictObject(shape, {
    error: (issue) => issue.code === "unrecognized_keys" ? "validation.unknownAttribute" : "validation.invalid"
  });
}
var SearchFieldType = createEnum([
  "LOCATION",
  "ORIGIN",
  "DESTINATION",
  "ADDRESS",
  "DATE",
  "DATE_RANGE",
  "DATETIME",
  "DATETIME_RANGE",
  "DURATION",
  "GUESTS",
  /** Number of identical units booked together (hotel rooms). */
  "ROOMS",
  "PASSENGERS",
  "LUGGAGE",
  "TRIP_TYPE",
  "TOGGLE",
  "SELECT"
]);
var LocationMatch = createEnum(["EXACT_OR_DESCENDANT", "SAME_REGION", "NONE"]);
var TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;
var searchFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-zA-Z0-9]{0,47}$/, "validation.attributeKey"),
  type: z.enum(SearchFieldType),
  label: localizedTextSchema,
  placeholder: localizedTextSchema.optional(),
  required: z.boolean().default(false),
  /** Restricts LOCATION/ORIGIN/DESTINATION pickers, e.g. [MARINA] or [AIRPORT]. */
  locationTypes: z.array(z.enum(LocationType)).optional(),
  /** Defaults: LOCATION → EXACT_OR_DESCENDANT, ORIGIN/DESTINATION → NONE. */
  locationMatch: z.enum(LocationMatch).optional(),
  durationUnits: z.array(z.enum(DurationUnit)).optional(),
  min: z.number().int().optional(),
  max: z.number().int().optional(),
  options: z.array(attributeOptionSchema).optional(),
  /**
   * Asset attribute the value filters on. Counts (GUESTS, PASSENGERS, LUGGAGE) require
   * `attribute >= value` (without it GUESTS/PASSENGERS use Asset.capacity), SELECT requires equality,
   * TOGGLE requires the boolean attribute to be true while the toggle is on, and ORIGIN/DESTINATION
   * require the numeric attribute (e.g. aircraft range in km) to cover the trip distance.
   */
  attribute: z.string().max(48).optional(),
  /** TOGGLE only: boolean attribute that must be true while the toggle is off (self-drive). */
  attributeWhenOff: z.string().max(48).optional(),
  /** TOGGLE only: switching it on makes the trip a round trip (return transfer, fare × 2). */
  roundTrip: z.boolean().optional(),
  /**
   * DATE_RANGE only: a stay from check-in to check-out, billed per night (check-in 15:00 and
   * check-out 11:00 unless set), instead of whole days counted inclusively.
   */
  stay: z.boolean().optional(),
  checkInTime: z.string().regex(TIME_OF_DAY, "validation.invalid").optional(),
  checkOutTime: z.string().regex(TIME_OF_DAY, "validation.invalid").optional(),
  /** Grid hint for the dynamic form (1-12 columns on desktop). */
  span: z.number().int().min(1).max(12).optional()
});
var searchSchemaDefinition = z.array(searchFieldSchema).superRefine((fields, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  fields.forEach((field, index) => {
    if (seen.has(field.key)) {
      ctx.addIssue({ code: "custom", path: [index, "key"], message: "validation.duplicateKey" });
    }
    seen.add(field.key);
  });
});
function locationMatchOf(field) {
  if (field.locationMatch) return field.locationMatch;
  return field.type === "LOCATION" ? "EXACT_OR_DESCENDANT" : "NONE";
}
var CategoryNavGroup = createEnum(["TRAVEL", "STAY", "EXPERIENCE", "ACQUIRE"]);
var CapacityNoun = createEnum(["GUESTS", "PASSENGERS", "SEATS", "ROOMS", "BEDROOMS"]);
var ViewingMode = createEnum([
  "IN_PERSON",
  "VIDEO",
  "SEA_TRIAL",
  "TEST_FLIGHT",
  "TEST_DRIVE"
]);
var CategoryLeadKind = createEnum(["BOOKING_REQUEST", "CHARTER_REQUEST", "SALE_INQUIRY"]);
var categoryFeaturesSchema = z.object({
  navGroup: z.enum(CategoryNavGroup).default("TRAVEL"),
  capacityNoun: z.enum(CapacityNoun).default("GUESTS"),
  leadKind: z.enum(CategoryLeadKind).default("BOOKING_REQUEST"),
  /** Estimated flight time and price range from the hourly rate (private jets). */
  charterEstimate: z.boolean().default(false),
  /** Results grouped into classes by the first SELECT attribute (aircraft class). */
  charterClasses: z.boolean().default(false),
  /** Source of the home page's empty-leg flights. */
  emptyLegs: z.boolean().default(false),
  /**
   * Hotels: a listing is a property whose bookable units are its room types (child listings with
   * a room count). The property itself is not booked; room types are not listed in search.
   */
  roomTypes: z.boolean().default(false),
  /** What the trip planner offers this category's listings as (stay, yacht days, car…). */
  plannerRole: z.enum(TripItemRole).nullable().default(null),
  /** For-sale categories: viewing formats a buyer can request (sea trial, test flight…). */
  viewingModes: z.array(z.enum(ViewingMode)).max(5).refine((modes) => new Set(modes).size === modes.length, "validation.duplicateKey").default(["IN_PERSON", "VIDEO"])
});
var DEFAULT_CATEGORY_FEATURES = categoryFeaturesSchema.parse({});
function parseCategoryFeatures(value) {
  const source = typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};
  const result = { ...DEFAULT_CATEGORY_FEATURES };
  for (const key of Object.keys(categoryFeaturesSchema.shape)) {
    const parsed = categoryFeaturesSchema.shape[key].safeParse(source[key]);
    if (parsed.success) Object.assign(result, { [key]: parsed.data });
  }
  return result;
}

// src/schemas/catalog.ts
var slugSchema = z.string().trim().toLowerCase().min(2, "validation.slug").max(120, "validation.slug").regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "validation.slug");
var categoryKeySchema = z.string().trim().regex(/^[a-z][a-z0-9_]{1,31}$/, "validation.categoryKey");
var isoDateSchema = z.string().refine(isValidIsoDate, "validation.date");
var honeypotSchema = z.string().max(500).optional();
var categoryInputSchema = z.object({
  key: categoryKeySchema,
  slug: slugSchema,
  name: localizedTextSchema,
  description: localizedTextSchema.nullable(),
  icon: z.string().trim().max(40).nullable(),
  listingType: z.enum(ListingType).default("RENTAL"),
  pricingModel: z.enum(PricingModel),
  bookingMode: z.enum(BookingMode),
  durationUnits: z.array(z.enum(DurationUnit)).min(1, "validation.required").max(5),
  defaultDepositBps: z.number().int().min(0).max(1e4),
  requiresGuestManifest: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int().min(0).max(999),
  attributeSchema: attributeSchemaDefinition,
  /** Room-type fields (bed, size, view) for categories whose listings have room types. */
  unitAttributeSchema: attributeSchemaDefinition.default([]),
  searchSchema: searchSchemaDefinition,
  features: categoryFeaturesSchema.default(DEFAULT_CATEGORY_FEATURES)
}).superRefine((value, ctx) => {
  const attributes = new Set(value.attributeSchema.map((field) => field.key));
  value.searchSchema.forEach((field, index) => {
    for (const prop of ["attribute", "attributeWhenOff"]) {
      const key = field[prop];
      if (key && !attributes.has(key)) {
        ctx.addIssue({
          code: "custom",
          path: ["searchSchema", index, prop],
          message: "validation.unknownAttribute"
        });
      }
    }
  });
});
var assetCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9-]{1,31}$/, "validation.assetCode");
var baseRateInputSchema = z.object({
  unit: z.enum(DurationUnit),
  amountMinor: z.number().int().min(1).max(1e11),
  minDuration: z.number().int().min(1).max(365).nullable()
});
var assetAddOnInputSchema = z.object({
  addOnId: z.uuid(),
  priceOverrideMinor: z.number().int().min(0).nullable(),
  isIncluded: z.boolean()
});
var seoInputSchema = z.object({
  title: localizedTextSchema.nullable(),
  description: localizedTextSchema.nullable(),
  noIndex: z.boolean()
});
var saleInputSchema = z.object({
  priceMinor: z.number().int().positive("validation.outOfRange").nullable(),
  currency: currencySchema,
  /** The price stays on file but the site shows "price on request". */
  priceOnRequest: z.boolean(),
  status: z.enum(SaleStatus)
}).superRefine((sale, ctx) => {
  if (sale.priceMinor === null && !sale.priceOnRequest) {
    ctx.addIssue({ code: "custom", path: ["priceMinor"], message: "validation.required" });
  }
});
var assetInputSchema = z.object({
  categoryId: z.uuid(),
  code: assetCodeSchema,
  slug: slugSchema,
  name: z.string().trim().min(2, "validation.required").max(120),
  headline: localizedTextSchema.nullable(),
  description: localizedTextSchema.nullable(),
  ownershipType: z.enum(OwnershipType),
  homeLocationId: z.uuid().nullable(),
  capacity: z.number().int().min(1).max(1e3),
  /** Validated against the category's attribute schema by the API (dynamic category engine). */
  attributes: z.record(z.string(), z.unknown()),
  baseCurrency: currencySchema,
  baseRates: z.array(baseRateInputSchema).max(5).superRefine((rates, ctx) => {
    const units = /* @__PURE__ */ new Set();
    rates.forEach((rate, index) => {
      if (units.has(rate.unit)) {
        ctx.addIssue({
          code: "custom",
          path: [index, "unit"],
          message: "validation.duplicateKey"
        });
      }
      units.add(rate.unit);
    });
  }),
  depositBps: z.number().int().min(0).max(1e4).nullable(),
  minNoticeHours: z.number().int().min(0).max(720),
  bufferBeforeMinutes: z.number().int().min(0).max(10080),
  bufferAfterMinutes: z.number().int().min(0).max(10080),
  cancellationPolicyId: z.uuid().nullable(),
  isFeatured: z.boolean(),
  featuredOrder: z.number().int().min(0).max(999).nullable(),
  amenityIds: z.array(z.uuid()).max(100),
  addOns: z.array(assetAddOnInputSchema).max(100),
  seo: seoInputSchema,
  /** Required for for-sale categories, ignored (stored as null) for rentals. */
  sale: saleInputSchema.nullable().default(null),
  /** Room type of this hotel (categories with room types); null for a hotel or any other listing. */
  parentAssetId: z.uuid().nullable().default(null),
  /** Identical units sold under this listing (rooms of a room type); 1 for a single yacht or villa. */
  inventoryUnits: z.number().int().min(1).max(500).default(1),
  /** Scheduled experiences: fixed local start times ("05:30") and how long a session lasts. */
  sessionTimes: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "validation.invalid")).max(12).refine((times) => new Set(times).size === times.length, "validation.duplicateKey").transform((times) => [...times].sort()).default([]),
  sessionMinutes: z.number().int().min(15).max(1440).nullable().default(null)
}).superRefine((asset, ctx) => {
  if (asset.sessionTimes.length > 0 && asset.sessionMinutes === null) {
    ctx.addIssue({ code: "custom", path: ["sessionMinutes"], message: "validation.required" });
  }
});
var assetStatusChangeSchema = z.object({ status: z.enum(AssetStatus) });
var AdminAssetSort = createEnum(["UPDATED", "NAME", "CODE"]);
var adminAssetListQuerySchema = paginationQuerySchema.extend({
  categoryId: z.uuid().optional(),
  status: z.enum(AssetStatus).optional(),
  q: z.string().trim().max(100).optional(),
  sort: z.enum(AdminAssetSort).default("UPDATED"),
  /** Only listings without a parent (hotels, not their room types), e.g. for the parent picker. */
  topLevel: z.enum(["true", "false"]).transform((value) => value === "true").optional()
});
var assetMediaUpdateSchema = z.object({
  alt: localizedTextSchema.nullable().optional(),
  caption: localizedTextSchema.nullable().optional(),
  isCover: z.literal(true).optional()
});
var reorderSchema = z.object({ ids: z.array(z.uuid()).min(1).max(200) });
var MAX_CALENDAR_DAYS = 93;
var dateWindowQuerySchema = z.object({ from: isoDateSchema, to: isoDateSchema }).refine(({ from, to }) => diffIsoDays(from, to) >= 0, {
  path: ["to"],
  message: "validation.dateOrder"
}).refine(({ from, to }) => diffIsoDays(from, to) < MAX_CALENDAR_DAYS, {
  path: ["to"],
  message: "validation.dateWindow"
});
var availabilityBlockInputSchema = z.object({
  startsAt: z.iso.datetime({ offset: true }),
  endsAt: z.iso.datetime({ offset: true }),
  reason: z.enum(AvailabilityBlockReason),
  note: z.string().trim().max(500).nullable()
}).refine(({ startsAt, endsAt }) => new Date(startsAt) < new Date(endsAt), {
  path: ["endsAt"],
  message: "validation.dateOrder"
});
var LeadKind = createEnum([
  "BOOKING_REQUEST",
  "CHARTER_REQUEST",
  "SALE_INQUIRY",
  "VIEWING_REQUEST",
  "CALLBACK",
  "CONTACT"
]);
var LEAD_KIND_SOURCE = {
  BOOKING_REQUEST: LeadSource.BOOKING_REQUEST,
  CHARTER_REQUEST: LeadSource.CHARTER_REQUEST,
  SALE_INQUIRY: LeadSource.SALE_INQUIRY,
  VIEWING_REQUEST: LeadSource.VIEWING_REQUEST,
  CALLBACK: LeadSource.CALLBACK_REQUEST,
  CONTACT: LeadSource.CONTACT_FORM
};
var ContactChannel = createEnum(["PHONE", "WHATSAPP", "EMAIL"]);
var leadContextSchema = z.object({
  assetSlug: slugSchema.optional(),
  categoryKey: categoryKeySchema.optional(),
  /** Snapshot of the search/booking-card query string (dates, duration, guests…). */
  search: z.record(z.string().max(48), z.string().max(200)).refine((value) => Object.keys(value).length <= 40, "validation.invalid").optional(),
  addOns: z.array(z.string().max(64)).max(30).optional(),
  /** Signature Journey the guest asks about (Wave 7). */
  journeySlug: slugSchema.optional(),
  /** Private viewing request of a for-sale listing. */
  viewing: z.object({ mode: z.enum(ViewingMode), preferredDate: isoDateSchema.optional() }).optional()
});
var leadInputSchema = z.object({
  kind: z.enum(LeadKind),
  firstName: z.string().trim().min(2, "validation.required").max(80),
  lastName: z.string().trim().max(80).optional(),
  email: z.email("validation.email").max(254).optional(),
  phone: phoneSchema.optional(),
  preferredChannel: z.enum(ContactChannel).optional(),
  message: z.string().trim().max(2e3).optional(),
  context: leadContextSchema.default({}),
  kvkkAccepted: z.literal(true, "validation.kvkkRequired"),
  marketingOptIn: z.boolean().default(false),
  website: honeypotSchema
}).refine((value) => Boolean(value.email ?? value.phone), {
  path: ["phone"],
  message: "validation.contactRequired"
});
var adminLeadListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(LeadStatus).optional(),
  source: z.enum(LeadSource).optional(),
  q: z.string().trim().max(100).optional(),
  /** "me" (the signed-in user), "none" (unassigned) or an admin user id. */
  assignee: z.union([z.literal("me"), z.literal("none"), z.uuid()]).optional()
});
var leadUpdateSchema = z.object({
  status: z.enum(LeadStatus).optional(),
  notes: z.string().trim().max(5e3).nullable().optional(),
  /** Responsible advisor (spec §6.5); null unassigns. */
  assignedToId: z.uuid().nullable().optional(),
  /** Day to follow the lead up (Istanbul date); null clears it. */
  followUpDate: z.string().regex(ISO_DATE_PATTERN, "validation.date").nullable().optional()
});
var newsletterSubscribeSchema = z.object({
  email: z.email("validation.email").max(254),
  consent: z.literal(true, "validation.consentRequired"),
  website: honeypotSchema
});
var newsletterConfirmSchema = z.object({
  token: z.string().min(20, "validation.token").max(256)
});
var cancellationRuleSchema = z.object({
  hoursBefore: z.number().int().min(0),
  refundBps: z.number().int().min(0).max(BPS_DENOMINATOR)
});
var cancellationRulesSchema = z.array(cancellationRuleSchema).min(1).refine((rules) => new Set(rules.map((r) => r.hoursBefore)).size === rules.length, {
  error: "validation.cancellation.duplicateTier"
});
function resolveRefundBps(rules, hoursUntilStart) {
  const sorted = [...rules].sort((a, b) => b.hoursBefore - a.hoursBefore);
  for (const rule of sorted) {
    if (hoursUntilStart >= rule.hoursBefore) {
      return rule.refundBps;
    }
  }
  return 0;
}
function calculateRefundMinor(paidMinor, rules, hoursUntilStart) {
  return applyBps(paidMinor, resolveRefundBps(rules, hoursUntilStart));
}

// src/schemas/pricing.ts
var positiveMinor = minorAmountSchema.refine((value) => value > 0, "validation.outOfRange");
var optionalInt = (min, max) => z.number().int("validation.integer").min(min, "validation.outOfRange").max(max, "validation.outOfRange").nullish();
var seasonalRateInputSchema = z.object({
  categoryId: z.uuid().nullish(),
  assetId: z.uuid().nullish(),
  unit: z.enum(DurationUnit),
  currency: currencySchema,
  /** Absolute seasonal tariff per unit … */
  amountMinor: positiveMinor.nullish(),
  /** … or a multiplier on the base rate (12500 = +25%). */
  multiplierBps: optionalInt(1e3, 5 * BPS_DENOMINATOR),
  minDuration: optionalInt(1, 365)
}).superRefine((rate, ctx) => {
  if (Boolean(rate.categoryId) === Boolean(rate.assetId)) {
    ctx.addIssue({ code: "custom", path: ["assetId"], message: "validation.pricing.scope" });
  }
  if (rate.amountMinor == null === (rate.multiplierBps == null)) {
    ctx.addIssue({
      code: "custom",
      path: ["multiplierBps"],
      message: "validation.pricing.seasonValue"
    });
  }
});
var seasonInputSchema = z.object({
  name: z.string().trim().min(1, "validation.required").max(80, "validation.tooLong"),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  /** Recurring seasons only compare month and day, so they may wrap the new year. */
  recursYearly: z.boolean().default(true),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "validation.invalid").nullish(),
  rates: z.array(seasonalRateInputSchema).max(60).default([])
}).superRefine((season, ctx) => {
  if (!season.recursYearly && season.endDate < season.startDate) {
    ctx.addIssue({ code: "custom", path: ["endDate"], message: "validation.dateOrder" });
  }
  const seen = /* @__PURE__ */ new Set();
  season.rates.forEach((rate, index) => {
    const key = `${rate.assetId ?? rate.categoryId ?? ""}:${rate.unit}`;
    if (seen.has(key)) {
      ctx.addIssue({
        code: "custom",
        path: ["rates", index, "unit"],
        message: "validation.duplicateKey"
      });
    }
    seen.add(key);
  });
});
var MANAGED_RULE_KINDS = [
  PricingRuleKind.WEEKEND,
  PricingRuleKind.SPECIAL_DAY,
  PricingRuleKind.TIME_OF_DAY,
  PricingRuleKind.DURATION_DISCOUNT,
  PricingRuleKind.ROUTE_FARE,
  PricingRuleKind.EXTRA_GUEST
];
var pricingRuleInputSchema = z.object({
  name: z.string().trim().min(1, "validation.required").max(120, "validation.tooLong"),
  kind: z.enum(MANAGED_RULE_KINDS),
  categoryId: z.uuid().nullish(),
  assetId: z.uuid().nullish(),
  routeId: z.uuid().nullish(),
  unit: z.enum(DurationUnit).nullish(),
  currency: currencySchema,
  adjustmentType: z.enum(PriceAdjustmentType).nullish(),
  /** PERCENT in bps (negative = discount). */
  adjustmentValue: optionalInt(-BPS_DENOMINATOR, 10 * BPS_DENOMINATOR),
  /** AMOUNT (signed) / FIXED_PRICE / ROUTE_FARE in minor units. */
  amountMinor: minorAmountSchema.nullish(),
  minDuration: optionalInt(1, 365),
  maxDuration: optionalInt(1, 365),
  /** EXTRA_GUEST: guests the base price covers; each one above pays the rule's amount. */
  includedGuests: optionalInt(0, 100),
  daysOfWeek: z.array(z.number().int().min(1).max(7)).max(7).default([]),
  startMinute: optionalInt(0, 1440),
  endMinute: optionalInt(0, 1440),
  validFrom: isoDateSchema.nullish(),
  validTo: isoDateSchema.nullish(),
  /** ROUTE_FARE matching, e.g. { vehicleClass: "FIRST" }. */
  conditions: z.record(z.string().max(48), z.string().max(80)).default({}),
  priority: z.number().int().min(-100).max(100).default(0),
  isActive: z.boolean().default(true)
}).superRefine((rule, ctx) => {
  const issue = (path, message) => ctx.addIssue({ code: "custom", path: [path], message });
  if (rule.kind === "ROUTE_FARE") {
    if (!rule.routeId) issue("routeId", "validation.required");
    if (rule.amountMinor == null || rule.amountMinor <= 0)
      issue("amountMinor", "validation.required");
    return;
  }
  if (Boolean(rule.categoryId) === Boolean(rule.assetId))
    issue("assetId", "validation.pricing.scope");
  if (!rule.adjustmentType) {
    issue("adjustmentType", "validation.required");
  } else if (rule.adjustmentType === "PERCENT") {
    if (rule.adjustmentValue == null) issue("adjustmentValue", "validation.required");
  } else if (rule.amountMinor == null) {
    issue("amountMinor", "validation.required");
  }
  if (rule.kind === "TIME_OF_DAY" && (rule.startMinute == null || rule.endMinute == null)) {
    issue("startMinute", "validation.required");
  }
  if (rule.kind === "SPECIAL_DAY" && (!rule.validFrom || !rule.validTo)) {
    issue("validFrom", "validation.required");
  }
  if (rule.kind === "DURATION_DISCOUNT" && rule.minDuration == null) {
    issue("minDuration", "validation.required");
  }
  if (rule.kind === "EXTRA_GUEST") {
    if (rule.includedGuests == null) issue("includedGuests", "validation.required");
    if (rule.adjustmentType === "FIXED_PRICE")
      issue("adjustmentType", "validation.invalidOption");
  }
  if (rule.validFrom && rule.validTo && rule.validTo < rule.validFrom) {
    issue("validTo", "validation.dateOrder");
  }
  if (rule.minDuration != null && rule.maxDuration != null && rule.maxDuration < rule.minDuration) {
    issue("maxDuration", "validation.minGreaterThanMax");
  }
});
var pricingRuleListQuerySchema = paginationQuerySchema.extend({
  kind: z.enum(MANAGED_RULE_KINDS).optional(),
  categoryId: z.uuid().optional(),
  assetId: z.uuid().optional(),
  pageSize: z.coerce.number().int().min(1).max(200).default(100)
});
var couponCodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9_-]{2,39}$/, "validation.couponCode");
var couponInputSchema = z.object({
  code: couponCodeSchema,
  description: z.string().trim().max(200).nullish(),
  discountType: z.enum(DiscountType),
  percentBps: optionalInt(1, BPS_DENOMINATOR),
  amountMinor: positiveMinor.nullish(),
  /** Currency of amount/limits; required when any of them is set. */
  currency: currencySchema.nullish(),
  maxDiscountMinor: positiveMinor.nullish(),
  minOrderMinor: positiveMinor.nullish(),
  usageLimit: optionalInt(1, 1e6),
  usageLimitPerUser: optionalInt(1, 1e3),
  /** Inclusive local dates (Europe/Istanbul). */
  validFrom: isoDateSchema.nullish(),
  validTo: isoDateSchema.nullish(),
  isActive: z.boolean().default(true),
  categoryIds: z.array(z.uuid()).max(20).default([])
}).superRefine((coupon, ctx) => {
  const issue = (path, message) => ctx.addIssue({ code: "custom", path: [path], message });
  if (coupon.discountType === "PERCENT" && coupon.percentBps == null) {
    issue("percentBps", "validation.required");
  }
  if (coupon.discountType === "FIXED_AMOUNT" && coupon.amountMinor == null) {
    issue("amountMinor", "validation.required");
  }
  const hasAmounts = [coupon.amountMinor, coupon.maxDiscountMinor, coupon.minOrderMinor].some(
    (value) => value != null
  );
  if (hasAmounts && !coupon.currency) issue("currency", "validation.required");
  if (coupon.validFrom && coupon.validTo && coupon.validTo < coupon.validFrom) {
    issue("validTo", "validation.dateOrder");
  }
});
var CouponListStatus = createEnum(["ACTIVE", "SCHEDULED", "EXPIRED", "INACTIVE"]);
var couponListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(40).optional()
});
var addOnInputSchema = z.object({
  key: slugSchema,
  name: localizedTextSchema,
  description: localizedTextSchema.nullish(),
  /** Null = offered for every category. */
  categoryId: z.uuid().nullish(),
  pricingType: z.enum(AddOnPricingType),
  currency: currencySchema,
  priceMinor: minorAmountSchema.refine((value) => value >= 0, "validation.outOfRange"),
  taxRateBps: z.number().int().min(0).max(BPS_DENOMINATOR),
  maxQuantity: optionalInt(1, 99),
  leadTimeHours: z.number().int().min(0).max(720).default(0),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1e3).default(0)
});
var cancellationPolicyInputSchema = z.object({
  key: slugSchema,
  name: localizedTextSchema,
  description: localizedTextSchema.nullish(),
  rules: cancellationRulesSchema,
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true)
});
var pricePreviewSchema = z.object({
  assetId: z.uuid(),
  search: z.record(z.string().max(64), z.string().max(200)).refine((value) => Object.keys(value).length <= 30, "validation.invalid"),
  startTime: z.string().max(5).nullish(),
  returnAt: z.string().max(16).nullish(),
  addOns: z.array(z.object({ addOnId: z.uuid(), quantity: z.number().int().min(1).max(99).optional() })).max(30).default([]),
  couponCode: z.string().trim().max(40).nullish(),
  /** Staff discount (pre-tax, tariff currency, minor units): simulator and manual bookings only. */
  manualDiscountMinor: z.number().int().min(0).max(1e12).nullish()
});

// src/schemas/booking.ts
var localDateTimeSchema = z.string().refine(isValidLocalDateTime, "validation.date");
var timeOfDaySchema = z.string().regex(TIME_OF_DAY_PATTERN, "validation.time");
var bookingAddOnSelectionSchema = z.object({
  addOnId: z.uuid(),
  quantity: z.number().int().min(1).max(99).optional()
});
var bookingRequestSchema = z.object({
  /** Listing slug. */
  asset: slugSchema,
  /** The category's search-form values, encoded like the search URL (`date=…`, `duration=4h`). */
  search: z.record(z.string().max(64), z.string().max(200)).refine((value) => Object.keys(value).length <= 30, "validation.invalid"),
  /** Start of an hourly charter ("HH:mm", listing time zone). */
  startTime: timeOfDaySchema.nullish(),
  /** Return leg of a round-trip transfer ("YYYY-MM-DDTHH:mm"). */
  returnAt: localDateTimeSchema.nullish(),
  addOns: z.array(bookingAddOnSelectionSchema).max(30).default([]),
  couponCode: z.string().trim().max(40).nullish()
});
var travellerSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  nationality: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, "validation.invalid").nullish(),
  dateOfBirth: isoDateSchema.nullish(),
  documentType: z.enum(GuestDocumentType).nullish(),
  documentNumber: z.string().trim().max(40).nullish(),
  documentExpiry: isoDateSchema.nullish()
});
var checkoutContactSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  email: emailSchema,
  /** Required: booking confirmations also go out by SMS (spec §5.4). */
  phone: phoneSchema
});
var ManualBookingSource = createEnum(["PHONE", "WHATSAPP", "EMAIL", "WALK_IN", "ADMIN"]);
var adminBookingCreateSchema = pricePreviewSchema.extend({
  contact: checkoutContactSchema,
  source: z.enum(ManualBookingSource),
  /** Language of the customer's e-mails. */
  locale: z.enum(Locale),
  manualDiscountReason: z.string().trim().max(300).nullish(),
  /** The slot is held this long for the payment; unpaid bookings then expire. */
  paymentWindowHours: z.number().int().min(1).max(168).default(48),
  /** E-mail the customer a payment link right away. */
  sendPaymentLink: z.boolean().default(true),
  /** Internal note on the booking. */
  note: z.string().trim().max(2e3).nullish()
}).refine((value) => !value.manualDiscountMinor || Boolean(value.manualDiscountReason), {
  path: ["manualDiscountReason"],
  message: "validation.required"
});
var checkoutSchema = bookingRequestSchema.extend({
  contact: checkoutContactSchema,
  /** Passenger manifest; mandatory for categories that require it. */
  travellers: z.array(travellerSchema).max(50).default([]),
  specialRequests: z.string().trim().max(2e3).nullish(),
  /** Guest checkout may open an account in the same step. */
  createAccount: z.object({ password: passwordSchema }).nullish(),
  /** Distance sales contract + KVKK notice. */
  acceptTerms: z.literal(true, { error: "validation.acceptTerms" }),
  marketingOptIn: z.boolean().default(false),
  website: honeypotSchema
});
var startPaymentSchema = z.object({
  /** Initial payment only; balance payments ignore it. */
  plan: z.enum(PaymentPlan).default(PaymentPlan.DEPOSIT)
});
var TestPaymentOutcome = createEnum(["SUCCEEDED", "FAILED"]);
var testPaymentCompletionSchema = z.object({ outcome: z.enum(TestPaymentOutcome) });
var cancelBookingSchema = z.object({
  reason: z.string().trim().max(1e3).nullish()
});
var CustomerBookingTab = createEnum(["UPCOMING", "PAST", "CANCELLED"]);
var customerBookingListQuerySchema = paginationQuerySchema.extend({
  tab: z.enum(CustomerBookingTab).default(CustomerBookingTab.UPCOMING)
});
var AdminBookingSort = createEnum(["START_ASC", "START_DESC", "CREATED_DESC"]);
var adminBookingListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
  status: z.enum(BookingStatus).optional(),
  categoryId: z.uuid().optional(),
  /** Start date window (listing time zone). */
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /** Only bookings with an open cancellation request. */
  cancellationRequested: z.enum(["true", "false"]).transform((value) => value === "true").optional(),
  sort: z.enum(AdminBookingSort).default(AdminBookingSort.CREATED_DESC)
});
var bookingTransitionSchema = z.object({
  to: z.enum(BookingStatus),
  reason: z.string().trim().max(1e3).nullish()
});
var bookingNoteSchema = z.object({
  body: z.string().trim().min(1, "validation.required").max(4e3, "validation.tooLong"),
  isPinned: z.boolean().default(false)
});
var MAX_TIMELINE_DAYS = 62;
var calendarBoardQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  categoryId: z.uuid().optional(),
  q: z.string().trim().max(80).optional()
}).superRefine((value, ctx) => {
  if (value.to < value.from || diffIsoDays(value.from, value.to) > MAX_TIMELINE_DAYS) {
    ctx.addIssue({ code: "custom", path: ["to"], message: "validation.dateWindow" });
  }
});
var bookingRescheduleSchema = z.object({
  startsAt: z.iso.datetime({ offset: true, message: "validation.date" }),
  reason: z.string().trim().max(500, "validation.tooLong").nullish(),
  /** Tell the customer about the new dates (default). */
  notifyCustomer: z.boolean().default(true)
});

// src/schemas/trip-plans.ts
var TripOccasion = createEnum([
  "LEISURE",
  "HONEYMOON",
  "ANNIVERSARY",
  "FAMILY",
  "FRIENDS",
  "BUSINESS",
  "CELEBRATION"
]);
var TripTransport = createEnum(["PRIVATE_JET", "VIP_ARRIVAL", "OWN_YACHT", "IN_TURKEY"]);
var TripStayStyle = createEnum(["PALACE", "BOUTIQUE", "VILLA", "NONE"]);
var TripCarMode = createEnum(["NONE", "SELF_DRIVE", "CHAUFFEUR", "TRANSFERS"]);
var TripInterest = createEnum([
  "GASTRONOMY",
  "CULTURE",
  "WELLNESS",
  "ADVENTURE",
  "SEA",
  "NIGHTLIFE"
]);
var TRIP_MAX_NIGHTS = 21;
var TRIP_PLAN_DEPOSIT_BPS = 3e3;
var TRIP_PLAN_PAYMENT_HOURS = 48;
var isoDate = z.string().refine(isValidIsoDate, "validation.date");
function tripNights(startDate, endDate) {
  return Math.round(
    (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 864e5
  );
}
var tripBriefSchema = z.object({
  /** A city location slug (istanbul, bodrum, kapadokya…). */
  destination: z.string().trim().min(1, "validation.required").max(120),
  startDate: isoDate,
  endDate: isoDate,
  adults: z.number().int().min(1).max(20),
  children: z.number().int().min(0).max(10).default(0),
  occasion: z.enum(TripOccasion).default("LEISURE"),
  transport: z.enum(TripTransport),
  /** Departure airport (location slug) for a private jet. */
  departure: z.string().trim().max(120).nullish(),
  stay: z.enum(TripStayStyle),
  yachtDays: z.number().int().min(0).max(TRIP_MAX_NIGHTS).default(0),
  interests: z.array(z.enum(TripInterest)).max(Object.keys(TripInterest).length).refine((items) => new Set(items).size === items.length, "validation.duplicateKey").default([]),
  car: z.enum(TripCarMode).default("NONE")
}).superRefine((brief, ctx) => {
  const nights = tripNights(brief.startDate, brief.endDate);
  if (nights < 1 || nights > TRIP_MAX_NIGHTS) {
    ctx.addIssue({ code: "custom", path: ["endDate"], message: "validation.tripLength" });
  } else if (brief.yachtDays > nights) {
    ctx.addIssue({ code: "custom", path: ["yachtDays"], message: "validation.yachtDays" });
  }
  if (brief.transport === "PRIVATE_JET" && !brief.departure) {
    ctx.addIssue({ code: "custom", path: ["departure"], message: "validation.required" });
  }
});
var tripProposalRequestSchema = z.object({
  brief: tripBriefSchema,
  currency: z.enum(Currency).default("EUR")
});
var tripPlanSelectionSchema = z.object({
  role: z.enum(TripItemRole),
  assetId: z.uuid().nullable()
});
var tripPlanSubmitSchema = z.object({
  brief: tripBriefSchema,
  items: z.array(tripPlanSelectionSchema).min(1).max(16).refine((items) => {
    const listed = items.flatMap(
      (item) => item.assetId ? [`${item.role}:${item.assetId}`] : []
    );
    return new Set(listed).size === listed.length;
  }, "validation.duplicateKey"),
  contact: checkoutContactSchema,
  notes: z.string().trim().max(2e3).nullish(),
  /** Currency the plan is priced and paid in (the visitor's display currency). */
  currency: z.enum(Currency).default("EUR"),
  locale: z.enum(Locale).default("tr"),
  acceptTerms: z.literal(true, { error: "validation.acceptTerms" }),
  marketingOptIn: z.boolean().default(false),
  website: honeypotSchema
});
var TripPlanPaymentPlan = createEnum(["DEPOSIT", "BALANCE"]);
var tripPlanPaymentSchema = z.object({
  plan: z.enum(TripPlanPaymentPlan).default("DEPOSIT")
});
var adminTripPlanQuerySchema = paginationQuerySchema.extend({
  status: z.enum(TripPlanStatus).optional(),
  q: z.string().trim().max(120).optional()
});
var TripPlanAction = createEnum(["REVIEW", "CONFIRM", "COMPLETE", "CANCEL"]);
var tripPlanActionSchema = z.object({
  action: z.enum(TripPlanAction),
  /** Final price of the whole trip (plan currency), set when confirming. */
  confirmedTotalMinor: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullish(),
  /** Shown to the customer in the e-mail (confirmation details, cancellation reason). */
  message: z.string().trim().max(2e3).nullish()
}).superRefine((value, ctx) => {
  if (value.action === "CONFIRM" && !value.confirmedTotalMinor) {
    ctx.addIssue({
      code: "custom",
      path: ["confirmedTotalMinor"],
      message: "validation.required"
    });
  }
});
var tripPlanItemUpdateSchema = z.object({
  status: z.enum(TripItemStatus),
  /** Provider confirmation number, hotel reference… */
  reference: z.string().trim().max(120).nullish(),
  note: z.string().trim().max(1e3).nullish()
});

// src/trip-plans/index.ts
function tripTotals(parts, depositBps) {
  let exactMinor = 0;
  let rangeLow = 0;
  let rangeHigh = 0;
  let onRequest = 0;
  for (const part of parts) {
    if (part.kind === "EXACT" && part.amountMinor !== null && part.amountMinor !== void 0) {
      exactMinor += part.amountMinor;
    } else if (part.kind === "RANGE" && part.lowMinor !== null && part.lowMinor !== void 0) {
      rangeLow += part.lowMinor;
      rangeHigh += part.highMinor ?? part.lowMinor;
    } else {
      onRequest += 1;
    }
  }
  return {
    exactMinor,
    lowMinor: exactMinor + rangeLow,
    highMinor: exactMinor + rangeHigh,
    onRequest,
    depositMinor: applyBps(exactMinor + rangeLow, depositBps)
  };
}
function tripBalanceMinor(confirmedTotalMinor, paidMinor) {
  return confirmedTotalMinor === null ? null : Math.max(0, confirmedTotalMinor - paidMinor);
}
function tripSchedule(brief, experiences) {
  const nights = Math.max(1, tripNights(brief.startDate, brief.endDate));
  const lastDay = nights + 1;
  const yachtDays = Math.min(brief.yachtDays, nights);
  const yachtFrom = yachtDays > 0 ? nights >= 2 ? 2 : 1 : null;
  const onYacht = (day) => yachtFrom !== null && day >= yachtFrom && day < yachtFrom + yachtDays;
  const middle = Array.from({ length: Math.max(0, nights - 1) }, (_, index) => index + 2);
  const free = middle.filter((day) => !onYacht(day));
  const pool = free.length > 0 ? free : middle.length > 0 ? middle : [1];
  return {
    nights,
    lastDay,
    yachtFrom,
    yachtDays,
    experienceDays: Array.from(
      { length: experiences },
      (_, index) => pool[index % pool.length] ?? 1
    )
  };
}
function tripDayDate(startDate, day) {
  return new Date(Date.parse(`${startDate}T00:00:00Z`) + (day - 1) * 864e5).toISOString().slice(0, 10);
}

// src/cms/rich-text.ts
function isSafeHref(href) {
  return /^(https?:\/\/|mailto:|tel:|\/(?!\/))/i.test(href.trim());
}
var INLINE = /\*\*(.+?)\*\*|_(.+?)_|\[([^\]]+)\]\(([^)\s]+)\)/g;
function parseInline(line) {
  const out = [];
  let last = 0;
  for (const match of line.matchAll(INLINE)) {
    const index = match.index;
    if (index > last) out.push({ type: "text", text: line.slice(last, index) });
    const [whole, bold, italic, label, href] = match;
    if (bold !== void 0) out.push({ type: "text", text: bold, marks: [{ type: "bold" }] });
    else if (italic !== void 0) {
      out.push({ type: "text", text: italic, marks: [{ type: "italic" }] });
    } else if (label !== void 0 && href !== void 0 && isSafeHref(href)) {
      out.push({ type: "text", text: label, marks: [{ type: "link", attrs: { href } }] });
    } else out.push({ type: "text", text: whole });
    last = index + whole.length;
  }
  if (last < line.length) out.push({ type: "text", text: line.slice(last) });
  return out.filter((node) => node.text.length > 0);
}
function inlineText(nodes) {
  return (nodes ?? []).map((node) => {
    const marks = node.marks ?? [];
    const link = marks.find((mark) => mark.type === "link");
    let text7 = node.text;
    if (marks.some((mark) => mark.type === "bold")) text7 = `**${text7}**`;
    if (marks.some((mark) => mark.type === "italic")) text7 = `_${text7}_`;
    return link?.attrs ? `[${text7}](${link.attrs.href})` : text7;
  }).join("");
}
var BULLET = /^[-*]\s+(.*)$/;
var NUMBERED = /^\d+[.)]\s+(.*)$/;
function textToRichText(source) {
  const blocks = [];
  const paragraph = [];
  const flush = () => {
    if (paragraph.length === 0) return;
    blocks.push({ type: "paragraph", content: parseInline(paragraph.join(" ")) });
    paragraph.length = 0;
  };
  for (const raw of source.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const heading = /^(#{2,3})\s+(.*)$/.exec(line);
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    if (heading?.[1] && heading[2] !== void 0) {
      flush();
      blocks.push({
        type: "heading",
        attrs: { level: heading[1].length === 2 ? 2 : 3 },
        content: parseInline(heading[2])
      });
    } else if (bullet?.[1] !== void 0 || numbered?.[1] !== void 0) {
      flush();
      const listType = bullet ? "bulletList" : "orderedList";
      const item = {
        type: "listItem",
        content: [{ type: "paragraph", content: parseInline(bullet?.[1] ?? numbered?.[1] ?? "") }]
      };
      const previous = blocks.at(-1);
      if (previous?.type === listType) previous.content.push(item);
      else blocks.push({ type: listType, content: [item] });
    } else paragraph.push(line);
  }
  flush();
  return { type: "doc", content: blocks };
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function readInline(value) {
  if (!isRecord(value) || !Array.isArray(value.content)) return [];
  return value.content.flatMap((node) => {
    if (!isRecord(node)) return [];
    if (node.type === "text" && typeof node.text === "string") {
      const marks = Array.isArray(node.marks) ? node.marks.flatMap((mark) => {
        if (!isRecord(mark)) return [];
        if (mark.type === "bold" || mark.type === "italic") return [{ type: mark.type }];
        const href = isRecord(mark.attrs) ? mark.attrs.href : void 0;
        return mark.type === "link" && typeof href === "string" && isSafeHref(href) ? [{ type: "link", attrs: { href } }] : [];
      }) : [];
      return [{ type: "text", text: node.text, ...marks.length > 0 ? { marks } : {} }];
    }
    return readInline(node);
  });
}
function readRichText(value) {
  if (!isRecord(value) || !Array.isArray(value.content)) return { type: "doc", content: [] };
  const blocks = value.content.flatMap((node) => {
    if (!isRecord(node)) return [];
    if (node.type === "heading") {
      const level = isRecord(node.attrs) && node.attrs.level === 3 ? 3 : 2;
      return [{ type: "heading", attrs: { level }, content: readInline(node) }];
    }
    if ((node.type === "bulletList" || node.type === "orderedList") && Array.isArray(node.content)) {
      const items = node.content.map((item) => ({
        type: "listItem",
        content: [{ type: "paragraph", content: readInline(item) }]
      }));
      return [{ type: node.type, content: items }];
    }
    return [{ type: "paragraph", content: readInline(node) }];
  });
  return { type: "doc", content: blocks };
}
function richTextToText(value) {
  return readRichText(value).content.map((block) => {
    if (block.type === "heading") {
      return `${block.attrs.level === 2 ? "##" : "###"} ${inlineText(block.content)}`;
    }
    if (block.type === "bulletList" || block.type === "orderedList") {
      return block.content.map(
        (item, index) => `${block.type === "bulletList" ? "-" : `${index + 1}.`} ${inlineText(item.content[0]?.content)}`
      ).join("\n");
    }
    return inlineText(block.content);
  }).join("\n\n");
}
function richTextPlain(value) {
  const strip = (nodes) => (nodes ?? []).map((node) => node.text).join("");
  return readRichText(value).content.map(
    (block) => block.type === "bulletList" || block.type === "orderedList" ? block.content.map((item) => strip(item.content[0]?.content)).join("\n") : strip(block.content)
  ).join("\n\n");
}
function readingMinutes(value) {
  const words = richTextPlain(value).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

// src/seo/paths.ts
var PREFIXED_LOCALES = SUPPORTED_LOCALES.filter(
  (locale) => locale !== DEFAULT_LOCALE
);
var RESERVED_ROOT_SEGMENTS = /* @__PURE__ */ new Set([
  ...PREFIXED_LOCALES,
  "account",
  "api",
  "assets",
  "blog",
  "booking",
  "checkout",
  "deneyimler",
  "forgot-password",
  "llms.txt",
  "login",
  "newsletter",
  "quotes",
  "register",
  "reset-password",
  "robots.txt",
  "sahip-hizmetleri",
  "service-requests",
  "seyahat-planla",
  "sitemap.xml",
  "sss",
  "trip-plans",
  "verify-email"
]);
function isPrefixedLocale(value) {
  return value !== void 0 && PREFIXED_LOCALES.includes(value);
}
function splitLocalePath(url) {
  const match = /^\/([a-z]{2})(?=$|[/?#])(.*)$/.exec(url);
  if (!match || !isPrefixedLocale(match[1])) return { locale: DEFAULT_LOCALE, path: url || "/" };
  const rest = match[2] ?? "";
  return { locale: match[1], path: rest.startsWith("/") ? rest : `/${rest}` };
}
function localizePath(url, locale) {
  if (!url.startsWith("/") || url.startsWith("//")) return url;
  const { path } = splitLocalePath(url);
  if (locale === DEFAULT_LOCALE) return path;
  if (path === "/") return `/${locale}`;
  if (path.startsWith("/?") || path.startsWith("/#")) return `/${locale}${path.slice(1)}`;
  return `/${locale}${path}`;
}

// src/seo/meta.ts
var OG_LOCALES = {
  tr: "tr_TR",
  en: "en_GB",
  ar: "ar_AR",
  ru: "ru_RU"
};
function absoluteUrl(origin, path) {
  return `${origin.replace(/\/+$/, "")}${path === "/" ? "/" : path}`;
}
function canonicalUrl(origin, url) {
  const { locale, path } = splitLocalePath(url);
  const pathname = path.split(/[?#]/)[0] || "/";
  return absoluteUrl(origin, localizePath(pathname, locale));
}
function seoAlternates(origin, url) {
  const pathname = splitLocalePath(url).path.split(/[?#]/)[0] || "/";
  return [
    ...SUPPORTED_LOCALES.map((locale) => ({
      hreflang: locale,
      href: absoluteUrl(origin, localizePath(pathname, locale))
    })),
    { hreflang: "x-default", href: absoluteUrl(origin, localizePath(pathname, DEFAULT_LOCALE)) }
  ];
}
function applyTitleTemplate(template, title, brand) {
  if (!title) return brand;
  return template.includes("%s") ? template.replace("%s", title) : `${title} \xB7 ${brand}`;
}
function truncateDescription(text7, max = 160) {
  const clean = text7.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:–-]+$/, "")}\u2026`;
}

// src/seo/structured-data.ts
var CONTEXT = "https://schema.org";
function serializeJsonLd(data) {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
function organizationJsonLd(input) {
  return {
    "@context": CONTEXT,
    "@type": "TravelAgency",
    "@id": `${input.url}#organization`,
    name: input.name,
    url: input.url,
    ...input.logoUrl ? { logo: input.logoUrl, image: input.logoUrl } : {},
    ...input.description ? { description: input.description } : {},
    ...input.phone ? { telephone: input.phone } : {},
    ...input.email ? { email: input.email } : {},
    ...input.address ? {
      address: {
        "@type": "PostalAddress",
        streetAddress: input.address,
        addressLocality: "\u0130stanbul",
        addressCountry: "TR"
      }
    } : {},
    areaServed: ["TR"],
    ...input.sameAs && input.sameAs.length > 0 ? { sameAs: input.sameAs.filter(Boolean) } : {}
  };
}
function productJsonLd(input) {
  return {
    "@context": CONTEXT,
    "@type": "Product",
    name: input.name,
    url: input.url,
    ...input.description ? { description: input.description } : {},
    ...input.images.length > 0 ? { image: input.images.slice(0, 6) } : {},
    ...input.category ? { category: input.category } : {},
    brand: { "@type": "Brand", name: input.brand },
    ...input.price ? {
      offers: {
        "@type": "Offer",
        url: input.url,
        price: minorToMajorString(input.price.amountMinor, input.price.currency),
        priceCurrency: input.price.currency,
        availability: `https://schema.org/${input.availability ?? "InStock"}`
      }
    } : {},
    ...input.rating && input.rating.count > 0 ? {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Math.round(input.rating.average * 10) / 10,
        reviewCount: input.rating.count,
        bestRating: 5,
        worstRating: 1
      }
    } : {},
    ...input.reviews && input.reviews.length > 0 ? {
      review: input.reviews.slice(0, 5).map((review) => ({
        "@type": "Review",
        author: { "@type": "Person", name: review.author },
        datePublished: review.date.slice(0, 10),
        reviewRating: { "@type": "Rating", ratingValue: review.rating, bestRating: 5 },
        ...review.body ? { reviewBody: review.body } : {}
      }))
    } : {}
  };
}
function breadcrumbJsonLd(items) {
  return {
    "@context": CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
}
function blogPostingJsonLd(input) {
  return {
    "@context": CONTEXT,
    "@type": "BlogPosting",
    headline: input.headline,
    url: input.url,
    mainEntityOfPage: input.url,
    ...input.description ? { description: input.description } : {},
    ...input.image ? { image: input.image } : {},
    ...input.datePublished ? { datePublished: input.datePublished } : {},
    author: input.author ? { "@type": "Person", name: input.author } : { "@type": "Organization", name: input.publisher },
    publisher: { "@type": "Organization", name: input.publisher }
  };
}
function faqPageJsonLd(items) {
  return {
    "@context": CONTEXT,
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer }
    }))
  };
}

// src/reports/period.ts
function comparisonRange(range, compare) {
  if (compare === "none") return null;
  if (compare === "lastYear") {
    return { from: addMonthsToIsoDate(range.from, -12), to: addMonthsToIsoDate(range.to, -12) };
  }
  const days2 = diffIsoDays(range.from, range.to) + 1;
  return { from: addDaysToIsoDate(range.from, -days2), to: addDaysToIsoDate(range.from, -1) };
}
function changeBps(current, previous) {
  if (previous === null || previous === 0) return null;
  return Math.round((current - previous) / Math.abs(previous) * BPS_DENOMINATOR);
}
function shareBps(part, whole) {
  if (whole <= 0) return 0;
  return Math.min(BPS_DENOMINATOR, Math.round(part / whole * BPS_DENOMINATOR));
}
function overlapMs(start, end, windowStart, windowEnd) {
  return Math.max(0, Math.min(end, windowEnd) - Math.max(start, windowStart));
}

// src/permissions.ts
var PERMISSION_MODULES = {
  dashboard: ["view"],
  bookings: ["read", "create", "update", "cancel", "export"],
  calendar: ["read", "manage"],
  assets: ["read", "create", "update", "publish", "archive"],
  categories: ["manage"],
  pricing: ["read", "manage"],
  availability: ["manage"],
  campaigns: ["manage"],
  customers: ["read", "update", "export"],
  /** KVKK/GDPR data requests: export and account deletion (spec §8). */
  privacy: ["read", "manage"],
  leads: ["read", "manage"],
  serviceRequests: ["read", "manage"],
  quotes: ["read", "manage", "send"],
  /** Trip planner (Wave 7): planned trips, their deposit and the team's confirmation. */
  tripPlans: ["read", "manage"],
  workOrders: ["read", "manage"],
  staff: ["read", "manage"],
  finance: ["read", "manage", "export"],
  payments: ["read", "manage", "refund"],
  expenses: ["read", "manage"],
  invoices: ["read", "manage"],
  cms: ["read", "manage"],
  media: ["manage"],
  reviews: ["moderate"],
  notifications: ["manage"],
  reports: ["read", "export"],
  settings: ["read", "manage"],
  featureFlags: ["manage"],
  users: ["read", "manage"],
  roles: ["manage"],
  audit: ["read"]
};
var ALL_PERMISSIONS = Object.entries(PERMISSION_MODULES).flatMap(([module, actions]) => actions.map((action) => `${module}.${action}`));
function isPermissionKey(value) {
  return ALL_PERMISSIONS.includes(value);
}
var RoleKey = {
  SUPER_ADMIN: "SUPER_ADMIN",
  OPERATIONS_MANAGER: "OPERATIONS_MANAGER",
  RESERVATION_AGENT: "RESERVATION_AGENT",
  FINANCE: "FINANCE",
  CONTENT_EDITOR: "CONTENT_EDITOR",
  SUPPORT: "SUPPORT",
  TECHNICAL_SERVICE: "TECHNICAL_SERVICE"
};
var SUPER_ADMIN_ROLE = RoleKey.SUPER_ADMIN;
var DEFAULT_ROLE_PERMISSIONS = {
  SUPER_ADMIN: ALL_PERMISSIONS,
  OPERATIONS_MANAGER: [
    "dashboard.view",
    "bookings.read",
    "bookings.create",
    "bookings.update",
    "bookings.cancel",
    "bookings.export",
    "calendar.read",
    "calendar.manage",
    "assets.read",
    "assets.create",
    "assets.update",
    "assets.publish",
    "assets.archive",
    "availability.manage",
    "pricing.read",
    "customers.read",
    "customers.update",
    "privacy.read",
    "privacy.manage",
    "leads.read",
    "leads.manage",
    "serviceRequests.read",
    "serviceRequests.manage",
    "quotes.read",
    "quotes.manage",
    "quotes.send",
    "tripPlans.read",
    "tripPlans.manage",
    "workOrders.read",
    "workOrders.manage",
    "staff.read",
    "staff.manage",
    "payments.read",
    "finance.read",
    "expenses.read",
    "reviews.moderate",
    "reports.read"
  ],
  RESERVATION_AGENT: [
    "dashboard.view",
    "bookings.read",
    "bookings.create",
    "bookings.update",
    "bookings.cancel",
    "calendar.read",
    "assets.read",
    "pricing.read",
    "customers.read",
    "customers.update",
    "leads.read",
    "leads.manage",
    "payments.read",
    "quotes.read",
    "tripPlans.read",
    "tripPlans.manage"
  ],
  FINANCE: [
    "dashboard.view",
    "bookings.read",
    "bookings.export",
    "customers.read",
    "finance.read",
    "finance.manage",
    "finance.export",
    "payments.read",
    "payments.manage",
    "payments.refund",
    "tripPlans.read",
    "expenses.read",
    "expenses.manage",
    "invoices.read",
    "invoices.manage",
    "reports.read",
    "reports.export"
  ],
  CONTENT_EDITOR: [
    "dashboard.view",
    "assets.read",
    "assets.update",
    "cms.read",
    "cms.manage",
    "media.manage",
    "reviews.moderate",
    "notifications.manage"
  ],
  SUPPORT: [
    "dashboard.view",
    "bookings.read",
    "customers.read",
    "privacy.read",
    "leads.read",
    "leads.manage",
    "serviceRequests.read",
    "tripPlans.read",
    "reviews.moderate"
  ],
  TECHNICAL_SERVICE: [
    "dashboard.view",
    "assets.read",
    "availability.manage",
    "serviceRequests.read",
    "serviceRequests.manage",
    "quotes.read",
    "quotes.manage",
    "workOrders.read",
    "workOrders.manage",
    "staff.read"
  ]
};
function hasPermission(granted, required3, options = {}) {
  if (options.isSuperAdmin) return true;
  const set = granted instanceof Set ? granted : new Set(granted);
  const list = typeof required3 === "string" ? [required3] : required3;
  return list.every((permission) => set.has(permission));
}

// src/domain/state-machine.ts
var InvalidTransitionError = class extends Error {
  constructor(machine, from, to) {
    super(`[${machine}] invalid transition ${from} -> ${to}`);
    this.machine = machine;
    this.from = from;
    this.to = to;
    this.name = "InvalidTransitionError";
  }
  machine;
  from;
  to;
};
function createStateMachine(name, transitions) {
  const states = Object.keys(transitions);
  const lookup = new Map(
    states.map((state) => [state, new Set(transitions[state])])
  );
  const canTransition = (from, to) => lookup.get(from)?.has(to) ?? false;
  return {
    name,
    states,
    canTransition,
    assertTransition(from, to) {
      if (!canTransition(from, to)) {
        throw new InvalidTransitionError(name, from, to);
      }
    },
    nextStates: (from) => transitions[from],
    isTerminal: (state) => transitions[state].length === 0
  };
}

// src/domain/booking-status.ts
var BOOKING_TRANSITIONS = {
  DRAFT: ["PENDING_PAYMENT", "AWAITING_CONFIRMATION", "CONFIRMED", "CANCELLED"],
  PENDING_PAYMENT: ["AWAITING_CONFIRMATION", "CONFIRMED", "CANCELLED"],
  AWAITING_CONFIRMATION: ["CONFIRMED", "CANCELLED"],
  CONFIRMED: ["IN_PROGRESS", "CANCELLED", "NO_SHOW"],
  IN_PROGRESS: ["COMPLETED"],
  COMPLETED: [],
  CANCELLED: ["REFUNDED"],
  NO_SHOW: ["REFUNDED"],
  REFUNDED: []
};
var bookingStateMachine = createStateMachine("booking", BOOKING_TRANSITIONS);
var INVENTORY_BLOCKING_BOOKING_STATUSES = [
  BookingStatus.CONFIRMED,
  BookingStatus.IN_PROGRESS,
  BookingStatus.COMPLETED
];
var OPEN_BOOKING_STATUSES = [
  BookingStatus.PENDING_PAYMENT,
  BookingStatus.AWAITING_CONFIRMATION,
  BookingStatus.CONFIRMED,
  BookingStatus.IN_PROGRESS
];
function isInventoryBlocking(status) {
  return INVENTORY_BLOCKING_BOOKING_STATUSES.includes(status);
}

// src/domain/service-request-status.ts
var SERVICE_REQUEST_TRANSITIONS = {
  NEW: ["REVIEWING", "CANCELLED"],
  REVIEWING: ["QUOTE_SENT", "CANCELLED"],
  QUOTE_SENT: ["QUOTE_ACCEPTED", "QUOTE_REJECTED", "REVIEWING", "CANCELLED"],
  QUOTE_ACCEPTED: ["SCHEDULED", "CANCELLED"],
  QUOTE_REJECTED: ["REVIEWING", "CANCELLED"],
  SCHEDULED: ["IN_PROGRESS", "CANCELLED"],
  IN_PROGRESS: ["COMPLETED", "CANCELLED"],
  COMPLETED: ["INVOICED"],
  INVOICED: [],
  CANCELLED: []
};
var serviceRequestStateMachine = createStateMachine(
  "serviceRequest",
  SERVICE_REQUEST_TRANSITIONS
);
var SERVICE_REQUEST_REQUIRED_ACTION = {
  QUOTE_SENT: "SEND_QUOTE",
  QUOTE_ACCEPTED: "QUOTE_DECISION",
  QUOTE_REJECTED: "QUOTE_DECISION",
  SCHEDULED: "SCHEDULE_WORK_ORDER",
  INVOICED: "ISSUE_INVOICE"
};
function isManualServiceRequestTransition(from, to) {
  return serviceRequestStateMachine.canTransition(from, to) && !SERVICE_REQUEST_REQUIRED_ACTION[to];
}
var OPEN_SERVICE_REQUEST_STATUSES = [
  "NEW",
  "REVIEWING",
  "QUOTE_SENT",
  "QUOTE_ACCEPTED",
  "QUOTE_REJECTED",
  "SCHEDULED",
  "IN_PROGRESS",
  "COMPLETED"
];

// src/domain/work-order-status.ts
var WORK_ORDER_TRANSITIONS = {
  PLANNED: ["SCHEDULED", "CANCELLED"],
  SCHEDULED: ["IN_PROGRESS", "CANCELLED"],
  IN_PROGRESS: ["ON_HOLD", "COMPLETED", "CANCELLED"],
  ON_HOLD: ["IN_PROGRESS", "CANCELLED"],
  COMPLETED: [],
  CANCELLED: []
};
var workOrderStateMachine = createStateMachine("workOrder", WORK_ORDER_TRANSITIONS);
var REQUEST_STATUS_FOR_WORK_ORDER = {
  PLANNED: null,
  SCHEDULED: "SCHEDULED",
  IN_PROGRESS: "IN_PROGRESS",
  ON_HOLD: null,
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED"
};

// src/domain/quote-status.ts
var QUOTE_TRANSITIONS = {
  DRAFT: ["SENT", "CANCELLED"],
  SENT: ["VIEWED", "ACCEPTED", "REJECTED", "EXPIRED", "SUPERSEDED", "CANCELLED"],
  VIEWED: ["ACCEPTED", "REJECTED", "EXPIRED", "SUPERSEDED", "CANCELLED"],
  ACCEPTED: [],
  REJECTED: [],
  EXPIRED: [],
  CANCELLED: [],
  SUPERSEDED: []
};
var quoteStateMachine = createStateMachine("quote", QUOTE_TRANSITIONS);

// src/domain/trip-plan-status.ts
var TRIP_PLAN_TRANSITIONS = {
  SUBMITTED: ["DEPOSIT_PAID", "EXPIRED", "CANCELLED"],
  DEPOSIT_PAID: ["IN_REVIEW", "CONFIRMED", "CANCELLED"],
  IN_REVIEW: ["CONFIRMED", "CANCELLED"],
  CONFIRMED: ["COMPLETED", "CANCELLED"],
  COMPLETED: [],
  CANCELLED: [],
  EXPIRED: []
};
var tripPlanStateMachine = createStateMachine("trip-plan", TRIP_PLAN_TRANSITIONS);
var TRIP_PLAN_ACTION_TARGET = {
  REVIEW: "IN_REVIEW",
  CONFIRM: "CONFIRMED",
  COMPLETE: "COMPLETED",
  CANCEL: "CANCELLED"
};
function tripPlanActions(status) {
  return Object.keys(TRIP_PLAN_ACTION_TARGET).filter(
    (action) => tripPlanStateMachine.canTransition(status, TRIP_PLAN_ACTION_TARGET[action])
  );
}
var updateRolePermissionsSchema = z.object({
  permissions: z.array(z.string().refine(isPermissionKey, "validation.invalidOption")).max(ALL_PERMISSIONS.length)
});
var auditLogQuerySchema = paginationQuerySchema.extend({
  action: z.string().max(100).optional(),
  entityType: z.string().max(100).optional(),
  entityId: z.string().max(100).optional(),
  actorId: z.uuid().optional(),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional()
});
var updateFeatureFlagSchema = z.object({ isEnabled: z.boolean() });
var personName = z.string().trim().min(1, "validation.required").max(80, "validation.tooLong");
var adminUserListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100).optional(),
  roleId: z.uuid().optional(),
  status: z.enum(["ACTIVE", "SUSPENDED"]).optional()
});
var adminUserCreateSchema = z.object({
  email: z.email("validation.email").max(254).transform((value) => value.toLowerCase()),
  firstName: personName,
  lastName: personName,
  phone: phoneSchema.nullish(),
  roleIds: z.array(z.uuid()).min(1, "validation.admin.roleRequired").max(7)
});
var adminUserUpdateSchema = z.object({
  firstName: personName,
  lastName: personName,
  phone: phoneSchema.nullish(),
  roleIds: z.array(z.uuid()).min(1, "validation.admin.roleRequired").max(7),
  status: z.enum(["ACTIVE", "SUSPENDED"])
});
var adminSetPasswordSchema = z.object({
  token: z.string().min(16).max(256),
  password: passwordSchema
});
var roleCreateSchema = z.object({
  name: z.string().trim().min(2, "validation.required").max(60, "validation.tooLong"),
  description: z.string().trim().max(200, "validation.tooLong").nullish(),
  /** Start from an existing role's permissions. */
  copyFromRoleId: z.uuid().nullish()
});
var adminSearchQuerySchema = z.object({ q: z.string().trim().min(2).max(80) });
var positiveMinor2 = minorAmountSchema.refine((value) => value > 0, "validation.outOfRange");
var nonNegativeMinor = minorAmountSchema.refine((value) => value >= 0, "validation.outOfRange");
var text = (max) => z.string().trim().max(max, "validation.tooLong");
var required = (max) => text(max).min(1, "validation.required");
var instantSchema = z.iso.datetime({ offset: true, message: "validation.date" });
var dateRange = {
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional()
};
var MANUAL_PAYMENT_METHODS = [
  PaymentMethod.BANK_TRANSFER,
  PaymentMethod.CASH,
  PaymentMethod.POS
];
var manualPaymentSchema = z.object({
  kind: z.enum([PaymentKind.DEPOSIT, PaymentKind.BALANCE, PaymentKind.FULL, PaymentKind.OTHER]),
  method: z.enum(MANUAL_PAYMENT_METHODS),
  amountMinor: positiveMinor2,
  cashAccountId: z.uuid(),
  paidAt: instantSchema.nullish(),
  /** Bank receipt / POS slip number. */
  reference: text(80).nullish(),
  note: text(500).nullish()
});
var bookingRefundSchema = z.object({
  amountMinor: positiveMinor2,
  reason: text(500).nullish(),
  /** Pays out the non-card part (bank transfer, cash); required when such payments are refunded. */
  cashAccountId: z.uuid().nullish()
});
var adminCancelBookingSchema = cancelBookingSchema.extend({
  refundMinor: nonNegativeMinor.nullish(),
  refundCashAccountId: z.uuid().nullish()
});
var paymentLinkSchema = z.object({
  message: text(500).nullish()
});
var paymentListQuerySchema = paginationQuerySchema.extend({
  ...dateRange,
  q: text(80).optional(),
  status: z.enum(PaymentStatus).optional(),
  provider: z.enum(PaymentProvider).optional(),
  method: z.enum(PaymentMethod).optional()
});
var refundListQuerySchema = paginationQuerySchema.extend({
  ...dateRange,
  status: z.enum(RefundStatus).optional()
});
var receivablesQuerySchema = paginationQuerySchema.extend({
  q: text(80).optional(),
  overdueOnly: z.enum(["true", "false"]).optional().transform((value) => value === "true")
});
var cashAccountInputSchema = z.object({
  name: required(80),
  type: z.enum(CashAccountType),
  currency: currencySchema,
  provider: z.enum(PaymentProvider).nullish(),
  bankName: text(80).nullish(),
  iban: z.string().trim().transform((value) => value.replace(/\s+/g, "").toUpperCase()).pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/, "validation.finance.iban")).nullish(),
  isActive: z.boolean().default(true),
  notes: text(500).nullish(),
  /** Create only: posted as an OPENING_BALANCE ledger entry. */
  openingBalanceMinor: nonNegativeMinor.default(0)
});
var transferSchema = z.object({
  fromAccountId: z.uuid(),
  toAccountId: z.uuid(),
  amountMinor: positiveMinor2,
  /** Required when the accounts hold different currencies (what arrived). */
  toAmountMinor: positiveMinor2.nullish(),
  occurredAt: instantSchema.nullish(),
  description: required(200)
}).refine((value) => value.fromAccountId !== value.toAccountId, {
  path: ["toAccountId"],
  message: "validation.finance.sameAccount"
});
var adjustmentSchema = z.object({
  cashAccountId: z.uuid(),
  direction: z.enum(LedgerDirection),
  amountMinor: positiveMinor2,
  occurredAt: instantSchema.nullish(),
  description: required(200)
});
var reversalSchema = z.object({ reason: required(200) });
var ledgerListQuerySchema = paginationQuerySchema.extend({
  ...dateRange,
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  cashAccountId: z.uuid().optional(),
  type: z.enum(LedgerEntryType).optional(),
  direction: z.enum(LedgerDirection).optional(),
  q: text(80).optional()
});
var expenseInputSchema = z.object({
  expenseCategoryId: z.uuid(),
  description: required(200),
  currency: currencySchema,
  /** What was paid, VAT included. */
  amountMinor: positiveMinor2,
  /** VAT contained in the amount (deductible VAT reporting). */
  taxMinor: nonNegativeMinor.default(0),
  incurredAt: isoDateSchema,
  status: z.enum([ExpenseStatus.PENDING, ExpenseStatus.PAID]).default(ExpenseStatus.PENDING),
  paidAt: instantSchema.nullish(),
  cashAccountId: z.uuid().nullish(),
  assetId: z.uuid().nullish(),
  bookingId: z.uuid().nullish(),
  /** Owner service work order or staff member (wages) the cost belongs to (Phase 4). */
  workOrderId: z.uuid().nullish(),
  staffId: z.uuid().nullish(),
  vendorName: text(120).nullish(),
  notes: text(1e3).nullish()
}).superRefine((expense, ctx) => {
  if (expense.taxMinor > expense.amountMinor) {
    ctx.addIssue({ code: "custom", path: ["taxMinor"], message: "validation.outOfRange" });
  }
  if (expense.status === "PAID" && !expense.cashAccountId) {
    ctx.addIssue({ code: "custom", path: ["cashAccountId"], message: "validation.required" });
  }
});
var ExpenseSort = createEnum(["INCURRED_DESC", "INCURRED_ASC", "AMOUNT_DESC"]);
var expenseListQuerySchema = paginationQuerySchema.extend({
  ...dateRange,
  q: text(80).optional(),
  expenseCategoryId: z.uuid().optional(),
  assetId: z.uuid().optional(),
  status: z.enum(ExpenseStatus).optional(),
  sort: z.enum(ExpenseSort).default("INCURRED_DESC")
});
var expenseCategoryInputSchema = z.object({
  key: slugSchema,
  name: localizedTextSchema,
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1e3).default(0)
});
var billingAddressSchema = z.object({
  line: required(200),
  city: required(80),
  district: text(80).nullish(),
  postalCode: text(16).nullish(),
  country: required(80)
});
var billingSchema = z.object({
  name: required(160),
  /** VKN (10 digits, companies) or TCKN (11 digits, individuals). */
  taxNumber: z.string().trim().regex(/^\d{10,11}$/, "validation.finance.taxNumber").nullish(),
  taxOffice: text(80).nullish(),
  address: billingAddressSchema.nullish(),
  email: z.email("validation.email").nullish()
});
var invoiceLineSchema = z.object({
  description: required(300),
  quantity: z.number().positive("validation.outOfRange").max(1e5, "validation.outOfRange").refine((value) => Number.isInteger(Math.round(value * 1e3)), "validation.number"),
  unit: text(20).nullish(),
  unitPriceMinor: minorAmountSchema,
  taxRateBps: z.number().int().min(0).max(BPS_DENOMINATOR)
});
var invoiceInputSchema = z.object({
  type: z.enum([InvoiceType.SALES, InvoiceType.PROFORMA]).default(InvoiceType.SALES),
  bookingId: z.uuid().nullish(),
  currency: currencySchema,
  billing: billingSchema,
  lines: z.array(invoiceLineSchema).min(1, "validation.required").max(100),
  dueAt: isoDateSchema.nullish(),
  notes: text(1e3).nullish()
});
var invoiceFromBookingSchema = z.object({
  type: z.enum([InvoiceType.SALES, InvoiceType.PROFORMA]).default(InvoiceType.SALES),
  billing: billingSchema.nullish()
});
var creditNoteSchema = z.object({
  /** VAT-inclusive amount to credit; omitted = the whole invoice. */
  amountMinor: positiveMinor2.nullish(),
  reason: required(300)
});
var invoiceCancelSchema = z.object({ reason: required(300) });
var invoiceListQuerySchema = paginationQuerySchema.extend({
  ...dateRange,
  q: text(80).optional(),
  status: z.enum(InvoiceStatus).optional(),
  type: z.enum(InvoiceType).optional()
});
var FOREIGN_CURRENCIES = ["EUR", "USD", "GBP"];
var exchangeRateInputSchema = z.object({
  base: z.enum(FOREIGN_CURRENCIES),
  /** TRY per unit, up to 8 decimals ("52.41500000"). */
  rate: z.string().trim().regex(/^\d{1,6}(?:\.\d{1,8})?$/, "validation.number").refine((value) => Number(value) > 0, "validation.outOfRange"),
  effectiveDate: isoDateSchema
});
var ReportGrouping = createEnum(["DAY", "WEEK", "MONTH"]);
var cashFlowQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  groupBy: z.enum(ReportGrouping).default("DAY"),
  cashAccountId: z.uuid().optional()
});
var ProfitabilityGrouping = createEnum(["ASSET", "CATEGORY", "MONTH"]);
var profitabilityQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  groupBy: z.enum(ProfitabilityGrouping).default("ASSET"),
  categoryId: z.uuid().optional()
});
var ExportFormat = createEnum(["xlsx", "csv", "pdf"]);
var FinanceExport = createEnum([
  "ledger",
  "payments",
  "refunds",
  "receivables",
  "expenses",
  "invoices",
  "cash-flow",
  "profitability"
]);
var exportQuerySchema = z.object({ format: z.enum(ExportFormat).default("xlsx") });
var adminBookingExportQuerySchema = adminBookingListQuerySchema.extend({
  format: z.enum(ExportFormat).default("xlsx")
});
var text2 = (max) => z.string().trim().max(max, "validation.tooLong");
var required2 = (max) => text2(max).min(1, "validation.required");
var instantSchema2 = z.iso.datetime({ offset: true, message: "validation.date" });
var bps = z.number().int().min(0).max(BPS_DENOMINATOR);
var positiveMinor3 = minorAmountSchema.refine((value) => value > 0, "validation.outOfRange");
var nonNegativeMinor2 = minorAmountSchema.refine((value) => value >= 0, "validation.outOfRange");
var flag = z.enum(["true", "false"]).optional().transform((value) => value === "true");
function laterThan(value, ctx, start, end, orEqual = false) {
  const from = value[start];
  const to = value[end];
  if (typeof from !== "string" || typeof to !== "string") return;
  const ok = orEqual ? to >= from : Date.parse(to) > Date.parse(from);
  if (!ok) {
    ctx.addIssue({ code: "custom", path: [end], message: "validation.services.endBeforeStart" });
  }
}
var OwnerServicePeriod = ["MONTH", "YEAR", "JOB"];
var titled = z.object({ title: localizedTextSchema, body: localizedTextSchema });
var ownerServiceTierSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/, "validation.attributeKey"),
  name: localizedTextSchema,
  tagline: localizedTextSchema.nullable().default(null),
  /** "From" price in minor units; null = on request. */
  priceFromMinor: z.number().int().positive("validation.outOfRange").nullable().default(null),
  currency: currencySchema.default("EUR"),
  period: z.enum(OwnerServicePeriod).nullable().default(null),
  /** Response time / SLA promise ("Within 4 hours, around the clock"). */
  response: localizedTextSchema.nullable().default(null),
  features: z.array(localizedTextSchema).max(12).default([]),
  featured: z.boolean().default(false)
});
var ownerServiceContentSchema = z.object({
  intro: localizedTextSchema.optional(),
  /** Short proof points: "1,200+ / yachts looked after". */
  stats: z.array(z.object({ value: z.string().trim().min(1).max(16), label: localizedTextSchema })).max(4).default([]),
  process: z.array(titled).max(8).default([]),
  tiers: z.array(ownerServiceTierSchema).max(4).default([]),
  references: z.array(
    z.object({
      title: localizedTextSchema,
      detail: localizedTextSchema,
      /** Outcome in a few words ("6 working days"). */
      metric: localizedTextSchema.optional()
    })
  ).max(8).default([]),
  guarantees: z.array(localizedTextSchema).max(8).default([])
});
function parseOwnerServiceContent(value) {
  const source = typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};
  const out = ownerServiceContentSchema.parse({});
  for (const key of Object.keys(
    ownerServiceContentSchema.shape
  )) {
    const parsed = ownerServiceContentSchema.shape[key].safeParse(source[key]);
    if (parsed.success) Object.assign(out, { [key]: parsed.data });
  }
  return out;
}
var ownerServiceItemInputSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]{0,47}$/, "validation.attributeKey"),
  name: localizedTextSchema,
  description: localizedTextSchema.nullable().default(null),
  isActive: z.boolean().default(true)
});
var ownerServiceInputSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]{1,47}$/, "validation.attributeKey"),
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "validation.slug").max(120),
  assetType: z.enum(OwnedAssetType).nullable(),
  icon: text2(40).nullable().default(null),
  name: localizedTextSchema,
  summary: localizedTextSchema,
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
  items: z.array(ownerServiceItemInputSchema).min(1, "validation.required").max(30),
  content: ownerServiceContentSchema
}).superRefine((service, ctx) => {
  const keys = /* @__PURE__ */ new Set();
  service.items.forEach((item, index) => {
    if (keys.has(item.key)) {
      ctx.addIssue({
        code: "custom",
        path: ["items", index, "key"],
        message: "validation.duplicateKey"
      });
    }
    keys.add(item.key);
  });
});
var ownedAssetInputSchema = z.object({
  type: z.enum(OwnedAssetType),
  /** Yacht name, aircraft or car model the owner uses for it. */
  name: required2(80),
  make: text2(60).nullish(),
  model: text2(60).nullish(),
  year: z.number().int().min(1950, "validation.outOfRange").max(2100).nullish(),
  /** Licence plate, tail number or registry number. */
  registration: text2(30).nullish(),
  lengthMeters: z.number().positive("validation.outOfRange").max(200).nullish(),
  homeLocationId: z.uuid().nullish(),
  /** Marina, hangar or address when it is not one of our locations. */
  locationText: text2(160).nullish(),
  notes: text2(500).nullish()
});
var requestFields = {
  serviceId: z.uuid(),
  itemIds: z.array(z.uuid()).max(20),
  /** A saved asset, or `asset` for a new one. */
  ownedAssetId: z.uuid().nullish(),
  asset: ownedAssetInputSchema.nullish(),
  /** Keeps a new asset in the account for repeat requests. */
  saveAsset: z.boolean().default(true),
  preferredStartDate: isoDateSchema.nullish(),
  preferredEndDate: isoDateSchema.nullish(),
  notes: text2(2e3).nullish()
};
function refineRequest(value, ctx) {
  if (!value.ownedAssetId && !value.asset) {
    ctx.addIssue({ code: "custom", path: ["asset"], message: "validation.required" });
  }
  laterThan(value, ctx, "preferredStartDate", "preferredEndDate", true);
}
var serviceRequestInputSchema = z.object({
  ...requestFields,
  itemIds: requestFields.itemIds.min(1, "validation.services.itemsRequired")
}).superRefine(refineRequest);
var adminServiceRequestInputSchema = z.object({
  ...requestFields,
  customerId: z.uuid(),
  source: z.enum(BookingSource).default(BookingSource.PHONE),
  internalNotes: text2(4e3).nullish()
}).superRefine(refineRequest);
var ServiceRequestSort = ["NEWEST", "OLDEST", "PREFERRED_DATE", "UPDATED"];
var requestFilters = {
  q: text2(80).optional(),
  serviceId: z.uuid().optional(),
  assignedToId: z.uuid().optional(),
  /** Only requests nobody is assigned to. */
  unassigned: flag
};
var serviceRequestListQuerySchema = paginationQuerySchema.extend({
  ...requestFilters,
  status: z.enum(ServiceRequestStatus).optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  sort: z.enum(ServiceRequestSort).default("NEWEST")
});
var serviceRequestBoardQuerySchema = z.object({
  ...requestFilters,
  closedDays: z.coerce.number().int().min(0).max(365).default(30)
});
var serviceRequestUpdateSchema = z.object({
  assignedToId: z.uuid().nullish(),
  internalNotes: text2(4e3).nullish(),
  preferredStartDate: isoDateSchema.nullish(),
  preferredEndDate: isoDateSchema.nullish()
}).superRefine(
  (value, ctx) => laterThan(value, ctx, "preferredStartDate", "preferredEndDate", true)
);
var serviceRequestTransitionSchema = z.object({
  to: z.enum(ServiceRequestStatus),
  reason: text2(300).nullish()
});
var serviceRequestCancelSchema = z.object({ reason: text2(500).nullish() });
var customerSearchQuerySchema = z.object({ q: required2(80) });
var quoteLineSchema = z.object({
  type: z.enum(QuoteLineType),
  description: required2(300),
  quantity: z.number().positive("validation.outOfRange").max(1e5, "validation.outOfRange").refine((value) => Number.isInteger(Math.round(value * 1e3)), "validation.number"),
  unit: text2(20).nullish(),
  /** Net unit price; DISCOUNT lines carry a negative amount. */
  unitPriceMinor: minorAmountSchema,
  discountBps: bps.default(0),
  taxRateBps: bps
}).superRefine((line, ctx) => {
  if (line.type === "DISCOUNT" ? line.unitPriceMinor >= 0 : line.unitPriceMinor < 0) {
    ctx.addIssue({
      code: "custom",
      path: ["unitPriceMinor"],
      message: "validation.outOfRange"
    });
  }
  if (line.type === "DISCOUNT" && line.discountBps > 0) {
    ctx.addIssue({ code: "custom", path: ["discountBps"], message: "validation.outOfRange" });
  }
});
var quoteInputSchema = z.object({
  currency: currencySchema,
  lines: z.array(quoteLineSchema).min(1, "validation.required").max(100),
  /** Last day the offer stands (Istanbul calendar day). */
  validUntil: isoDateSchema.nullish(),
  /** Advance the customer pays first (optional). */
  depositBps: bps.nullish(),
  /** Shown to the customer and printed on the PDF. */
  notes: text2(2e3).nullish(),
  terms: text2(4e3).nullish(),
  internalNotes: text2(2e3).nullish()
});
var quoteSendSchema = z.object({
  /** Defaults to the customer's e-mail address. */
  to: z.email("validation.email").nullish(),
  message: text2(1e3).nullish()
});
var quoteDecisionSchema = z.object({ reason: text2(500).nullish() });
var quoteListQuerySchema = paginationQuerySchema.extend({
  q: text2(80).optional(),
  status: z.enum(QuoteStatus).optional()
});
var QuotePaymentPlan = ["DEPOSIT", "FULL", "BALANCE"];
var quotePaymentSchema = z.object({ plan: z.enum(QuotePaymentPlan).default("FULL") });
var workOrderUpdateSchema = z.object({
  scheduledStart: instantSchema2.nullish(),
  scheduledEnd: instantSchema2.nullish(),
  locationText: text2(200).nullish(),
  notes: text2(4e3).nullish()
}).superRefine((value, ctx) => {
  if (!!value.scheduledStart !== !!value.scheduledEnd) {
    ctx.addIssue({
      code: "custom",
      path: [value.scheduledStart ? "scheduledEnd" : "scheduledStart"],
      message: "validation.required"
    });
  }
  laterThan(value, ctx, "scheduledStart", "scheduledEnd");
});
var workOrderTransitionSchema = z.object({
  to: z.enum(WorkOrderStatus),
  reason: text2(300).nullish()
});
var workOrderTaskInputSchema = z.object({
  title: required2(200),
  description: text2(1e3).nullish(),
  status: z.enum(WorkOrderTaskStatus).default(WorkOrderTaskStatus.TODO),
  assigneeStaffId: z.uuid().nullish(),
  estimatedMinutes: z.number().int().min(0).max(1e5).nullish()
});
var workOrderMediaFieldsSchema = z.object({
  phase: z.enum(WorkOrderMediaPhase).default(WorkOrderMediaPhase.BEFORE),
  caption: text2(200).nullish()
});
var workOrderListQuerySchema = paginationQuerySchema.extend({
  q: text2(80).optional(),
  status: z.enum(WorkOrderStatus).optional(),
  staffId: z.uuid().optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /** Only work orders that still need a date. */
  unscheduled: flag
});
var staffInputSchema = z.object({
  firstName: required2(60),
  lastName: required2(60),
  role: z.enum(StaffRole),
  email: z.email("validation.email").nullish(),
  phone: phoneSchema.nullish(),
  /** TCKN, stored encrypted. Omitted keeps the stored value, null clears it. */
  nationalId: z.string().trim().regex(/^\d{11}$/, "validation.services.nationalId").nullish(),
  employmentType: z.enum(EmploymentType).default(EmploymentType.FULL_TIME),
  currency: currencySchema.default("TRY"),
  monthlySalaryMinor: nonNegativeMinor2.nullish(),
  dailyRateMinor: nonNegativeMinor2.nullish(),
  skills: z.array(required2(40)).max(20).default([]),
  /** ISO 639-1 codes ("tr", "en"). */
  languages: z.array(z.string().regex(/^[a-z]{2}$/, "validation.services.language")).max(12).default([]),
  isActive: z.boolean().default(true),
  hiredAt: isoDateSchema.nullish(),
  notes: text2(2e3).nullish()
});
var staffListQuerySchema = paginationQuerySchema.extend({
  q: text2(80).optional(),
  role: z.enum(StaffRole).optional(),
  status: z.enum(["active", "inactive", "all"]).default("active"),
  /** Only staff with an expired or soon expiring document. */
  documents: flag
});
var staffDocumentInputSchema = z.object({
  type: z.enum(StaffDocumentType),
  /** Document number, stored encrypted. Omitted keeps the stored value, null clears it. */
  number: text2(60).nullish(),
  issuedAt: isoDateSchema.nullish(),
  expiresAt: isoDateSchema.nullish(),
  notes: text2(500).nullish()
}).superRefine((value, ctx) => laterThan(value, ctx, "issuedAt", "expiresAt", true));
var staffAvailabilityInputSchema = z.object({
  type: z.enum(StaffAvailabilityType),
  startsAt: instantSchema2,
  endsAt: instantSchema2,
  note: text2(200).nullish()
}).superRefine((value, ctx) => laterThan(value, ctx, "startsAt", "endsAt"));
var assignmentFields = {
  staffId: z.uuid(),
  role: z.enum(StaffRole),
  startsAt: instantSchema2,
  endsAt: instantSchema2,
  notes: text2(300).nullish()
};
var targetAssignmentSchema = z.object(assignmentFields).superRefine((value, ctx) => laterThan(value, ctx, "startsAt", "endsAt"));
var staffAssignmentInputSchema = z.object({
  ...assignmentFields,
  bookingId: z.uuid().nullish(),
  workOrderId: z.uuid().nullish()
}).superRefine((value, ctx) => {
  if (!value.bookingId === !value.workOrderId) {
    ctx.addIssue({ code: "custom", path: ["workOrderId"], message: "validation.required" });
  }
  laterThan(value, ctx, "startsAt", "endsAt");
});
var staffAssignmentUpdateSchema = z.object({
  status: z.enum(StaffAssignmentStatus).optional(),
  startsAt: instantSchema2.optional(),
  endsAt: instantSchema2.optional(),
  notes: text2(300).nullish()
}).superRefine((value, ctx) => laterThan(value, ctx, "startsAt", "endsAt"));
var staffScheduleQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  role: z.enum(StaffRole).optional(),
  staffId: z.uuid().optional()
}).superRefine((value, ctx) => {
  laterThan(value, ctx, "from", "to", true);
  const days2 = (Date.parse(value.to) - Date.parse(value.from)) / 864e5;
  if (days2 > 62) ctx.addIssue({ code: "custom", path: ["to"], message: "validation.outOfRange" });
});
var staffPayoutSchema = z.object({
  amountMinor: positiveMinor3,
  cashAccountId: z.uuid(),
  paidAt: instantSchema2.nullish(),
  description: required2(200),
  /** Defaults to the crew & staff wages category. */
  expenseCategoryId: z.uuid().nullish(),
  workOrderId: z.uuid().nullish()
});
var recurrenceRuleSchema = z.string().trim().max(120, "validation.tooLong").refine((value) => parseRecurrence(value) !== null, "validation.services.recurrence");
var servicePlanInputSchema = z.object({
  customerId: z.uuid(),
  ownerServiceId: z.uuid(),
  ownedAssetId: z.uuid().nullish(),
  name: required2(120),
  recurrenceRule: recurrenceRuleSchema,
  startsOn: isoDateSchema,
  endsOn: isoDateSchema.nullish(),
  /** Work orders are generated this many days before each occurrence. */
  leadDays: z.number().int().min(0).max(60).default(7),
  /** Local start time of each occurrence (HH:mm, Istanbul). */
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "validation.time").default("09:00"),
  durationMinutes: z.number().int().min(15).max(31 * 24 * 60).default(480),
  currency: currencySchema,
  /** Contract price per occurrence (excl. VAT). */
  priceMinor: positiveMinor3,
  taxRateBps: bps,
  staffId: z.uuid().nullish(),
  isActive: z.boolean().default(true),
  notes: text2(1e3).nullish()
}).superRefine((value, ctx) => laterThan(value, ctx, "startsOn", "endsOn", true));
var text3 = (max) => z.string().trim().max(max, "validation.tooLong");
var CustomerSegment = createEnum([
  "VIP",
  "HIGH_VALUE",
  "REPEAT",
  "NEW",
  "INACTIVE",
  "UPCOMING",
  "CORPORATE",
  "OWNERS",
  "MARKETING"
]);
var HIGH_VALUE_LTV_TRY_MINOR = 5e7;
var NEW_CUSTOMER_DAYS = 30;
var INACTIVE_CUSTOMER_DAYS = 365;
var CustomerSort = createEnum(["NEWEST", "NAME", "LTV", "LAST_ACTIVITY"]);
var customerListQuerySchema = paginationQuerySchema.extend({
  q: text3(100).optional(),
  vipTier: z.enum(VipTier).optional(),
  tag: text3(40).transform(normalizeTag).optional(),
  segment: z.enum(CustomerSegment).optional(),
  corporateAccountId: z.uuid().optional(),
  sort: z.enum(CustomerSort).default("NEWEST")
});
var tagSchema = z.string().trim().min(1).max(40, "validation.tooLong").transform(normalizeTag);
var customerProfileUpdateSchema = z.object({
  firstName: text3(80).min(1, "validation.required"),
  lastName: text3(80).min(1, "validation.required"),
  phone: phoneSchema.nullish(),
  vipTier: z.enum(VipTier),
  tags: z.array(tagSchema).max(20).transform((tags) => [...new Set(tags)]),
  /** Non-sensitive preferences, e.g. { "drink": "Sparkling water", "music": "Jazz" }. */
  preferences: z.record(z.string().trim().min(1).max(40), z.string().trim().max(200)).refine((value) => Object.keys(value).length <= 20, "validation.tooLong"),
  /**
   * Allergies / health notes. Stored encrypted and only accepted when the customer has granted the
   * SENSITIVE_DATA consent (KVKK md. 6); `null` clears them.
   */
  sensitiveNotes: text3(1e3).nullish(),
  internalNotes: text3(4e3).nullish(),
  dateOfBirth: isoDateSchema.nullish(),
  nationality: z.string().regex(/^[A-Z]{2}$/, "validation.invalidOption").nullish(),
  preferredCurrency: z.enum(Currency).nullish(),
  acquisitionSource: text3(80).nullish(),
  corporateAccountId: z.uuid().nullish()
});
var corporateAccountInputSchema = z.object({
  name: text3(120).min(1, "validation.required"),
  legalName: text3(200).nullish(),
  taxNumber: z.string().trim().regex(/^\d{10,11}$/, "validation.services.nationalId").nullish(),
  taxOffice: text3(80).nullish(),
  email: z.email("validation.email").max(254).nullish(),
  phone: phoneSchema.nullish(),
  currency: z.enum(Currency).default("TRY"),
  creditLimitMinor: minorAmountSchema.nullish(),
  paymentTermDays: z.number().int().min(0).max(365).default(0),
  isActive: z.boolean().default(true),
  notes: text3(2e3).nullish()
});
var corporateAccountListQuerySchema = paginationQuerySchema.extend({
  q: text3(100).optional(),
  status: z.enum(["active", "inactive", "all"]).default("active")
});
var text4 = (max) => z.string().trim().max(max, "validation.tooLong");
var optionalText = (max) => text4(max).optional();
var partialLocalizedTextSchema = z.object({
  tr: optionalText(2e3),
  en: optionalText(2e3),
  ar: optionalText(2e3),
  ru: optionalText(2e3)
});
var richTextInputSchema = z.object({
  tr: z.string().trim().min(1, "validation.required").max(5e4, "validation.tooLong"),
  en: optionalText(5e4),
  ar: optionalText(5e4),
  ru: optionalText(5e4)
});
var linkUrlSchema = z.string().trim().max(500, "validation.tooLong").refine(isSafeHref, "validation.url");
var cmsSeoInputSchema = z.object({
  title: partialLocalizedTextSchema.default({}),
  description: partialLocalizedTextSchema.default({}),
  noIndex: z.boolean().default(false),
  ogImageId: z.uuid().nullish()
});
var cmsListQuerySchema = paginationQuerySchema.extend({
  q: optionalText(100),
  status: z.enum(ContentStatus).optional()
});
var pageInputSchema = z.object({
  slug: slugSchema,
  type: z.enum(PageType),
  status: z.enum(ContentStatus),
  title: localizedTextSchema,
  content: richTextInputSchema,
  seo: cmsSeoInputSchema.default({ title: {}, description: {}, noIndex: false }),
  sortOrder: z.number().int().min(0).max(1e4).default(0)
});
var blogCategoryInputSchema = z.object({
  slug: slugSchema,
  name: localizedTextSchema,
  sortOrder: z.number().int().min(0).max(1e4).default(0)
});
var blogPostInputSchema = z.object({
  slug: slugSchema,
  status: z.enum(ContentStatus),
  title: localizedTextSchema,
  excerpt: partialLocalizedTextSchema.default({}),
  content: richTextInputSchema,
  blogCategoryId: z.uuid().nullish(),
  tags: z.array(text4(40).min(1)).max(15).transform((tags) => [...new Set(tags.map(normalizeTag))]),
  coverFileId: z.uuid().nullish(),
  seo: cmsSeoInputSchema.default({ title: {}, description: {}, noIndex: false })
});
var journeyItemInputSchema = z.object({
  categoryId: z.uuid().nullish(),
  assetId: z.uuid().nullish(),
  addOnId: z.uuid().nullish(),
  description: localizedTextSchema
}).refine((item) => [item.categoryId, item.assetId, item.addOnId].filter(Boolean).length <= 1, {
  path: ["assetId"],
  message: "validation.invalid"
});
var journeyDayInputSchema = z.object({
  title: localizedTextSchema,
  description: localizedTextSchema
});
var journeyInputSchema = z.object({
  slug: slugSchema,
  status: z.enum(ContentStatus),
  title: localizedTextSchema,
  summary: localizedTextSchema.nullable(),
  description: localizedTextSchema.nullable(),
  currency: currencySchema,
  /** "From" price per journey in minor units; null = price on request. */
  priceFromMinor: z.number().int().positive("validation.outOfRange").nullable(),
  nights: z.number().int().min(1).max(60).nullable(),
  locationId: z.uuid().nullable(),
  isFeatured: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(1e4).default(0),
  coverFileId: z.uuid().nullish(),
  highlights: z.array(localizedTextSchema).max(12).default([]),
  inclusions: z.array(localizedTextSchema).max(20).default([]),
  itinerary: z.array(journeyDayInputSchema).max(30).default([]),
  items: z.array(journeyItemInputSchema).max(20).default([]),
  seo: cmsSeoInputSchema.default({ title: {}, description: {}, noIndex: false })
});
var blogListQuerySchema = paginationQuerySchema.extend({
  q: optionalText(100),
  status: z.enum(ContentStatus).optional(),
  category: optionalText(80),
  tag: text4(40).transform(normalizeTag).optional()
});
var faqInputSchema = z.object({
  question: localizedTextSchema,
  answer: localizedTextSchema,
  /** Free grouping on the FAQ page, e.g. "booking", "payment". */
  group: text4(40).nullish(),
  categoryId: z.uuid().nullish(),
  sortOrder: z.number().int().min(0).max(1e4).default(0),
  isActive: z.boolean().default(true)
});
var testimonialInputSchema = z.object({
  authorName: text4(80).min(1, "validation.required"),
  authorTitle: text4(80).nullish(),
  content: localizedTextSchema,
  rating: z.number().int().min(1).max(5).nullish(),
  isFeatured: z.boolean().default(false),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1e4).default(0)
});
var bannerInputSchema = z.object({
  title: localizedTextSchema,
  body: partialLocalizedTextSchema.default({}),
  linkUrl: linkUrlSchema.nullish(),
  placement: z.enum(BannerPlacement),
  mediaFileId: z.uuid().nullish(),
  startsAt: z.iso.datetime({ offset: true, message: "validation.date" }).nullish(),
  endsAt: z.iso.datetime({ offset: true, message: "validation.date" }).nullish(),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1e4).default(0)
}).superRefine((value, ctx) => {
  if (value.startsAt && value.endsAt && Date.parse(value.endsAt) <= Date.parse(value.startsAt)) {
    ctx.addIssue({
      code: "custom",
      path: ["endsAt"],
      message: "validation.services.endBeforeStart"
    });
  }
});
var menuLeafSchema = z.object({
  label: localizedTextSchema,
  /** Either a URL (internal path or https link) or a CMS page. */
  url: linkUrlSchema.nullish(),
  pageId: z.uuid().nullish(),
  target: z.enum(["_self", "_blank"]).default("_self"),
  isActive: z.boolean().default(true)
});
var menuItemInputSchema = menuLeafSchema.extend({ children: z.array(menuLeafSchema).max(20).default([]) }).superRefine((value, ctx) => {
  if (!value.url && !value.pageId && value.children.length === 0) {
    ctx.addIssue({ code: "custom", path: ["url"], message: "validation.required" });
  }
});
var menuSaveSchema = z.object({ items: z.array(menuItemInputSchema).max(30) });
var homepageSectionUpdateSchema = z.object({
  title: localizedTextSchema,
  subtitle: partialLocalizedTextSchema.default({}),
  isEnabled: z.boolean(),
  /**
   * Type-specific values; the API validates them per section type:
   * HERO { videoUrl, posterUrl } · FEATURED_ASSETS { categoryKey, limit } · CATEGORY_SEARCH
   * { categories } · INSTAGRAM { handle } · CUSTOM { body (localized), linkUrl, linkLabel } ·
   * list sections { limit }.
   */
  content: z.record(z.string(), z.unknown()).default({}),
  settings: z.record(z.string(), z.unknown()).default({})
});
var heroSectionContentSchema = z.object({
  videoUrl: linkUrlSchema.or(z.literal("")).default(""),
  posterUrl: linkUrlSchema.or(z.literal("")).default("")
});
var listSectionSettingsSchema = z.object({
  limit: z.number().int().min(1, "validation.outOfRange").max(12, "validation.outOfRange").default(6)
});
var featuredSectionSettingsSchema = listSectionSettingsSchema.extend({
  categoryKey: z.string().trim().max(40).nullish()
});
var categorySearchSettingsSchema = z.object({
  categories: z.array(z.string().trim().max(40)).max(10).default([])
});
var instagramSettingsSchema = z.object({
  handle: z.string().trim().regex(/^[A-Za-z0-9._]{1,30}$/, "validation.invalidOption")
});
var customSectionContentSchema = z.object({
  body: partialLocalizedTextSchema.default({}),
  linkUrl: linkUrlSchema.or(z.literal("")).default(""),
  linkLabel: partialLocalizedTextSchema.default({})
});
var emptySchema = z.object({}).catch({});
function homepageSectionSchemas(type) {
  switch (type) {
    case "HERO":
      return { content: heroSectionContentSchema, settings: emptySchema };
    case "CATEGORY_SEARCH":
      return { content: emptySchema, settings: categorySearchSettingsSchema };
    case "FEATURED_ASSETS":
      return { content: emptySchema, settings: featuredSectionSettingsSchema };
    case "EXPERIENCES":
    case "EMPTY_LEGS":
    case "TESTIMONIALS":
    case "BLOG":
      return { content: emptySchema, settings: listSectionSettingsSchema };
    case "INSTAGRAM":
      return { content: emptySchema, settings: instagramSettingsSchema };
    case "CUSTOM":
      return { content: customSectionContentSchema, settings: emptySchema };
    case "WHY_US":
    case "NEWSLETTER":
      return { content: emptySchema, settings: emptySchema };
  }
}
var mediaListQuerySchema = paginationQuerySchema.extend({
  q: optionalText(100),
  folder: optionalText(60),
  type: z.enum(["IMAGE", "VIDEO", "DOCUMENT"]).optional()
});
var mediaUpdateSchema = z.object({
  alt: partialLocalizedTextSchema,
  folder: text4(60).regex(/^[a-z0-9-]*$/, "validation.slug").min(1, "validation.required")
});
var reviewListQuerySchema = paginationQuerySchema.extend({
  q: optionalText(100),
  status: z.enum(ReviewStatus).optional(),
  rating: z.coerce.number().int().min(1).max(5).optional(),
  assetId: z.uuid().optional()
});
var reviewModerationSchema = z.object({
  status: z.enum(["APPROVED", "REJECTED"]).optional(),
  /** Public reply under the review; `null` removes it. */
  reply: text4(1e3).nullish(),
  /** Featured reviews also appear as home page testimonials. */
  isFeatured: z.boolean().optional()
});
var reviewInputSchema = z.object({
  rating: z.number().int().min(1, "validation.outOfRange").max(5, "validation.outOfRange"),
  title: text4(120).nullish(),
  comment: z.string().trim().min(10, "validation.reviews.commentTooShort").max(2e3, "validation.tooLong")
});
var ReportKey = createEnum([
  "sales",
  "occupancy",
  "categories",
  "acquisition",
  "cancellations",
  "staff",
  "serviceConversion"
]);
var ReportCompare = createEnum(["none", "previous", "lastYear"]);
var MAX_REPORT_DAYS = 400;
var reportQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  compare: z.enum(ReportCompare).default("previous"),
  groupBy: z.enum(ReportGrouping).default("DAY"),
  categoryId: z.uuid().optional()
}).superRefine((value, ctx) => {
  if (value.to < value.from) {
    ctx.addIssue({ code: "custom", path: ["to"], message: "validation.services.endBeforeStart" });
  } else if (diffIsoDays(value.from, value.to) > MAX_REPORT_DAYS) {
    ctx.addIssue({ code: "custom", path: ["to"], message: "validation.outOfRange" });
  }
});
var reportExportQuerySchema = z.object({ format: z.enum(ExportFormat).default("xlsx") });
var dashboardQuerySchema = z.object({ categoryId: z.uuid().optional() });
var text5 = (max) => z.string().trim().max(max, "validation.tooLong");
var bps2 = z.number().int().min(0).max(BPS_DENOMINATOR);
var emailList = z.array(z.email("validation.email").max(254)).max(10);
var companyProfileSettingSchema = z.object({
  name: text5(80).min(1, "validation.required"),
  legalName: text5(200),
  phone: phoneSchema.or(z.literal("")),
  whatsapp: phoneSchema.or(z.literal("")),
  email: z.email("validation.email").max(254),
  address: text5(300),
  /** Public URL of the logo (media library); empty = built-in mark. */
  logoUrl: text5(500).default("")
});
var companyTaxSettingSchema = z.object({
  taxOffice: text5(80),
  taxNumber: z.string().trim().regex(/^(\d{10,11})?$/, "validation.services.nationalId"),
  mersisNo: z.string().trim().regex(/^(\d{16})?$/, "validation.invalidOption")
});
var vatRatesSettingSchema = z.object({ standard: bps2, reduced: bps2, accommodation: bps2 });
var localizationSettingSchema = z.object({
  defaultLocale: z.enum(Locale),
  locales: z.array(z.enum(Locale)).min(1),
  defaultCurrency: z.enum(Currency),
  currencies: z.array(z.enum(Currency)).min(1),
  timezone: text5(60).min(1)
}).superRefine((value, ctx) => {
  if (!value.locales.includes(value.defaultLocale)) {
    ctx.addIssue({
      code: "custom",
      path: ["defaultLocale"],
      message: "validation.invalidOption"
    });
  }
  if (!value.currencies.includes(value.defaultCurrency)) {
    ctx.addIssue({
      code: "custom",
      path: ["defaultCurrency"],
      message: "validation.invalidOption"
    });
  }
});
var seoDefaultsSettingSchema = z.object({
  titleTemplate: text5(120).refine(
    (value) => value.includes("%s"),
    "validation.settings.titleTemplate"
  ),
  description: localizedTextSchema
});
var analyticsSettingSchema = z.object({
  ga4MeasurementId: z.string().trim().regex(/^(G-[A-Z0-9]{4,12})?$/, "validation.settings.ga4"),
  metaPixelId: z.string().trim().regex(/^(\d{8,20})?$/, "validation.settings.metaPixel")
});
var cookieSettingSchema = z.object({
  /** Show the consent banner (analytics/marketing scripts load only after consent). */
  bannerEnabled: z.boolean(),
  /** Bumping the version asks every visitor again (stored with each ConsentLog). */
  policyVersion: text5(20).min(1, "validation.required"),
  analytics: z.boolean(),
  marketing: z.boolean()
});
var socialSettingSchema = z.object({
  instagram: z.url("validation.url").or(z.literal("")),
  linkedin: z.url("validation.url").or(z.literal("")),
  youtube: z.url("validation.url").or(z.literal(""))
});
var paymentProvidersSettingSchema = z.object({
  iyzico: z.object({ mode: z.enum(["test", "live"]), enabled: z.boolean() }),
  stripe: z.object({ mode: z.enum(["test", "live"]), enabled: z.boolean() })
});
var notificationSettingSchema = z.object({
  /** Operations alerts (new booking, request, cancellation…); empty = company e-mail. */
  operationsEmails: emailList,
  /** Booking reminder this many hours before the start (0 = off). */
  bookingReminderHours: z.number().int().min(0).max(168),
  /** Review request this many days after a booking is completed (0 = off). */
  reviewRequestDelayDays: z.number().int().min(0).max(30),
  /** Send SMS copies of confirmations and reminders when the customer has a phone number. */
  smsEnabled: z.boolean()
});
var reportScheduleSettingSchema = z.object({
  enabled: z.boolean(),
  /** Weekly: Mondays 08:00 for the previous week; monthly: the 1st for the previous month. */
  frequency: z.enum(["WEEKLY", "MONTHLY"]),
  recipients: emailList,
  reports: z.array(z.enum(ReportKey)).min(1).max(7)
});
var days = z.number().int().min(0).max(365);
var SETTING_DEFINITIONS = {
  "company.profile": { group: "company", isPublic: true, schema: companyProfileSettingSchema },
  "company.tax": { group: "company", isPublic: false, schema: companyTaxSettingSchema },
  "finance.vat_rates_bps": { group: "finance", isPublic: false, schema: vatRatesSettingSchema },
  "finance.default_deposit_bps": { group: "finance", isPublic: false, schema: bps2 },
  /** Stored on each plan when it is sent, so a change only affects new plans. */
  "finance.trip_plan_deposit_bps": {
    group: "finance",
    isPublic: false,
    schema: z.number().int().min(100).max(BPS_DENOMINATOR)
  },
  "finance.balance_due_days_before_start": { group: "finance", isPublic: false, schema: days },
  "booking.hold_minutes": {
    group: "booking",
    isPublic: true,
    schema: z.number().int().min(5).max(60)
  },
  localization: { group: "general", isPublic: true, schema: localizationSettingSchema },
  "seo.defaults": { group: "seo", isPublic: true, schema: seoDefaultsSettingSchema },
  analytics: { group: "integrations", isPublic: true, schema: analyticsSettingSchema },
  cookies: { group: "integrations", isPublic: true, schema: cookieSettingSchema },
  social: { group: "general", isPublic: true, schema: socialSettingSchema },
  "payments.providers": {
    group: "payments",
    isPublic: false,
    schema: paymentProvidersSettingSchema
  },
  "quotes.validity_days": { group: "services", isPublic: false, schema: days.min(1) },
  "quotes.default_terms": { group: "services", isPublic: false, schema: text5(4e3) },
  notifications: { group: "notifications", isPublic: false, schema: notificationSettingSchema },
  "reports.schedule": { group: "reports", isPublic: false, schema: reportScheduleSettingSchema }
};
var SETTING_KEYS = Object.keys(SETTING_DEFINITIONS);
function isSettingKey(value) {
  return Object.hasOwn(SETTING_DEFINITIONS, value);
}
var settingUpdateSchema = z.object({ value: z.unknown() });
var cookieConsentSchema = z.object({
  /** Random id kept in the browser (no account needed). */
  anonymousId: z.uuid(),
  version: z.string().trim().min(1).max(20),
  analytics: z.boolean(),
  marketing: z.boolean()
});
var MAIL_TEMPLATE_GROUPS = {
  account: [
    "verify-email",
    "reset-password",
    "newsletter-confirm",
    "admin-invite",
    "data-deletion-completed",
    "data-request-rejected"
  ],
  bookings: [
    "booking-awaiting",
    "booking-confirmed",
    "booking-rescheduled",
    "booking-reminder",
    "booking-cancelled",
    "booking-cancellation-requested",
    "payment-received",
    "payment-refunded",
    "balance-reminder",
    "balance-overdue",
    "payment-link",
    "invoice-sent",
    "review-request"
  ],
  services: [
    "service-request-received",
    "quote-sent",
    "quote-accepted",
    "service-scheduled",
    "service-completed",
    "service-payment-received",
    "service-request-cancelled"
  ],
  trips: ["trip-plan-received", "trip-plan-paid", "trip-plan-confirmed", "trip-plan-cancelled"],
  operations: ["ops-alert", "staff-documents-expiring", "scheduled-report"]
};
var MAIL_TEMPLATE_KEYS = Object.values(MAIL_TEMPLATE_GROUPS).flat();
function isMailTemplateKey(value) {
  return MAIL_TEMPLATE_KEYS.includes(value);
}
var SMS_TEMPLATE_KEYS = [
  "booking-confirmed",
  "booking-reminder",
  "payment-link",
  "quote-sent"
];
function isSmsTemplateKey(value) {
  return SMS_TEMPLATE_KEYS.includes(value);
}
var SMS_MAX_LENGTH = 480;
function templateVariables(...texts) {
  const names = texts.flatMap(
    (value) => [...value.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((m) => m[1] ?? "")
  );
  return [...new Set(names.filter(Boolean))];
}
var mailTemplateUpdateSchema = z.object({
  subject: z.string().trim().min(1, "validation.required").max(200, "validation.tooLong"),
  body: z.string().trim().min(1, "validation.required").max(5e3, "validation.tooLong"),
  isActive: z.boolean().default(true)
});
var mailTemplatePreviewSchema = z.object({
  subject: z.string().max(200),
  body: z.string().max(5e3)
});
var smsTemplateUpdateSchema = z.object({
  body: z.string().trim().min(1, "validation.required").max(SMS_MAX_LENGTH, "validation.tooLong"),
  isActive: z.boolean().default(true)
});
var templateLocaleParamSchema = z.object({ locale: z.enum(Locale) });
var ADMIN_NOTIFICATION_EVENTS = {
  "booking.created": "bookings.read",
  "booking.cancellationRequested": "bookings.read",
  "serviceRequest.created": "serviceRequests.read",
  "quote.accepted": "quotes.read",
  "quote.rejected": "quotes.read",
  "lead.created": "leads.read",
  "payment.received": "payments.read",
  "review.submitted": "reviews.moderate",
  "dataRequest.created": "privacy.read",
  "tripPlan.depositPaid": "tripPlans.read"
};
function isAdminNotificationEvent(value) {
  return Object.hasOwn(ADMIN_NOTIFICATION_EVENTS, value);
}
var adminNotificationQuerySchema = paginationQuerySchema.extend({
  unread: z.enum(["true", "false"]).optional().transform((value) => value === "true")
});
var NOTIFICATION_SOCKET_PATH = "/ws";
var text6 = (max) => z.string().trim().max(max, "validation.tooLong");
var dataRequestCreateSchema = z.object({
  type: z.enum(DataRequestType),
  /** The customer's own words (optional). */
  reason: text6(1e3).nullish()
});
var adminDataRequestListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(DataRequestStatus).optional(),
  type: z.enum(DataRequestType).optional(),
  /** Customer name or e-mail. */
  q: text6(100).optional()
});
var dataRequestDecisionSchema = z.object({
  action: z.enum(["start", "complete", "reject"]),
  note: text6(1e3).nullish()
}).refine((input) => input.action !== "reject" || Boolean(input.note), {
  path: ["note"],
  message: "validation.required"
});
var DATA_EXPORT_VALID_DAYS = 7;
var RETENTION_POLICY = {
  /** Leads that never became a booking are deleted after this many months. */
  leadsMonths: 24,
  /** Unconfirmed newsletter sign-ups are deleted after this many days. */
  newsletterPendingDays: 30,
  /** Unsubscribed newsletter addresses are deleted after this many days. */
  newsletterUnsubscribedDays: 30,
  /** Expired or revoked sessions and used/expired one-time tokens are deleted after this many days. */
  sessionsDays: 30,
  /** In-app notifications are deleted after this many months. */
  notificationsMonths: 12
};

// src/http/api-client.ts
var ApiError = class extends Error {
  constructor(status, code, message, issues = [], requestId, details = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.issues = issues;
    this.requestId = requestId;
    this.details = details;
    this.name = "ApiError";
  }
  status;
  code;
  issues;
  requestId;
  details;
};
async function toApiError(response) {
  let body = {};
  try {
    body = await response.json();
  } catch {
  }
  return new ApiError(
    response.status,
    body.code ?? "INTERNAL_ERROR",
    body.message ?? response.statusText,
    body.issues ?? [],
    body.requestId,
    body.details ?? {}
  );
}
function createApiClient(options) {
  const doFetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
  let inflightRefresh = null;
  async function attemptRefresh(isRetry) {
    let response;
    try {
      response = await doFetch(`${options.baseUrl}${options.refreshPath}`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" }
      });
    } catch {
      return null;
    }
    if (response.ok) return await response.json();
    const error = await toApiError(response);
    if (!isRetry && error.code === "AUTH_TOKEN_INVALID") {
      await new Promise((resolve) => setTimeout(resolve, 250));
      return attemptRefresh(true);
    }
    return null;
  }
  function refresh() {
    inflightRefresh ??= attemptRefresh(false).then((session) => {
      if (session) options.onSession(session);
      return session;
    }).finally(() => {
      inflightRefresh = null;
    });
    return inflightRefresh;
  }
  async function request(path, init = {}, retried = false) {
    const auth = init.auth ?? true;
    const headers = { Accept: "application/json", ...init.headers };
    const locale = options.getLocale?.();
    const hasLanguage = Object.keys(headers).some(
      (name) => name.toLowerCase() === "accept-language"
    );
    if (locale && !hasLanguage) headers["Accept-Language"] = locale;
    let body;
    if (init.body instanceof FormData) {
      body = init.body;
    } else if (init.body !== void 0) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(init.body);
    }
    const token = auth ? options.getAccessToken() : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    let response;
    try {
      response = await doFetch(`${options.baseUrl}${path}`, {
        method: init.method ?? "GET",
        headers,
        body,
        credentials: "include",
        signal: init.signal
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new ApiError(0, "NETWORK_ERROR", "Network request failed");
    }
    if (response.status === 401 && auth && !retried) {
      const session = await refresh();
      if (session) return request(path, init, true);
      options.onSessionLost();
    }
    if (!response.ok) throw await toApiError(response);
    if (init.responseType === "blob") return await response.blob();
    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return void 0;
    }
    const text7 = await response.text();
    return text7 ? JSON.parse(text7) : void 0;
  }
  return {
    request,
    refresh,
    get: (path, init) => request(path, { ...init, method: "GET" }),
    post: (path, body, init) => request(path, { ...init, method: "POST", body }),
    put: (path, body, init) => request(path, { ...init, method: "PUT", body }),
    patch: (path, body, init) => request(path, { ...init, method: "PATCH", body }),
    delete: (path, init) => request(path, { ...init, method: "DELETE" })
  };
}

export { ADMIN_NOTIFICATION_EVENTS, ALL_PERMISSIONS, API_BASE_PATH, API_PREFIX, API_VERSION, APP_NAME, AddOnPricingType, AdminAssetSort, AdminBookingSort, ApiError, AssetStatus, AttributeFieldType, AuditActorType, AvailabilityBlockReason, AvailabilityStatus, BLOCKING_AVAILABILITY, BOOKING_HOLD_TTL_SECONDS, BOOKING_TOKEN_HEADER, BOOKING_TRANSITIONS, BPS_DENOMINATOR, BannerPlacement, BookingItemKind, BookingMode, BookingSource, BookingStatus, CHARTER_MIN_BILLABLE_MINUTES, CHARTER_RANGE_HIGH_BPS, CHARTER_RANGE_LOW_BPS, CHARTER_TAXI_MINUTES, CORE_CATEGORY_KEYS, CURRENCY_META, CapacityNoun, CashAccountType, CatalogSort, CategoryLeadKind, CategoryNavGroup, ConsentType, ContactChannel, ContentStatus, CouponListStatus, CouponStatus, Currency, CustomerBookingTab, CustomerSegment, CustomerSort, DATA_EXPORT_VALID_DAYS, DEFAULT_CATEGORY_FEATURES, DEFAULT_CHECK_IN_TIME, DEFAULT_CHECK_OUT_TIME, DEFAULT_CURRENCY, DEFAULT_LOCALE, DEFAULT_ROLE_PERMISSIONS, DEFAULT_TIMEZONE, DEFAULT_TRIP_MINUTES, DOCUMENT_EXPIRY_WARNING_DAYS, DataRequestStatus, DataRequestType, DayStatus, DiscountType, DurationUnit, ESTIMATED_JET_SPEED_KMH, EmploymentType, EmptyLegStatus, ErrorCode, ExchangeRateSource, ExpenseSort, ExpenseStatus, ExportFormat, FILTER_PARAM_PREFIX, FOREIGN_CURRENCIES, FinanceExport, GuestDocumentType, HIGH_VALUE_LTV_TRY_MINOR, HomepageSectionType, INACTIVE_CUSTOMER_DAYS, INVENTORY_BLOCKING_BOOKING_STATUSES, ISO_DATE_PATTERN, InvalidTransitionError, InvoiceStatus, InvoiceType, LEAD_KIND_SOURCE, LOCAL_DATETIME_PATTERN, LeadKind, LeadSource, LeadStatus, LedgerDirection, LedgerEntryType, ListingType, Locale, LocationMatch, LocationType, MAIL_TEMPLATE_GROUPS, MAIL_TEMPLATE_KEYS, MANAGED_RULE_KINDS, MANUAL_PAYMENT_METHODS, MAX_CALENDAR_DAYS, MAX_REPORT_DAYS, MAX_TIMELINE_DAYS, MIN_PARTIAL_FREE_MINUTES, ManualBookingSource, MediaType, MessageSenderType, MoneyError, NEW_CUSTOMER_DAYS, NOTIFICATION_SOCKET_PATH, NotificationChannel, NotificationStatus, OG_LOCALES, OPEN_BOOKING_STATUSES, OPEN_SERVICE_REQUEST_STATUSES, OwnedAssetType, OwnerServicePeriod, OwnershipType, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PERMISSION_MODULES, PREFIXED_LOCALES, PageType, PaymentKind, PaymentMethod, PaymentPlan, PaymentProvider, PaymentStatus, PriceAdjustmentType, PricingModel, PricingRuleKind, ProfitabilityGrouping, QUOTE_TOKEN_HEADER, QUOTE_TRANSITIONS, QuoteKind, QuoteLineType, QuotePaymentPlan, QuoteStatus, RECURRENCE_FREQUENCIES, REQUEST_STATUS_FOR_WORK_ORDER, RESERVED_ROOT_SEGMENTS, RETENTION_POLICY, RTL_LOCALES, RefundStatus, ReportCompare, ReportGrouping, ReportKey, ReviewStatus, RoleKey, SERVICE_DAY, SERVICE_REQUEST_REQUIRED_ACTION, SERVICE_REQUEST_TRANSITIONS, SETTING_DEFINITIONS, SETTING_KEYS, SMS_MAX_LENGTH, SMS_TEMPLATE_KEYS, START_TIME_STEP_MINUTES, SUPER_ADMIN_ROLE, SUPPORTED_CURRENCIES, SUPPORTED_LOCALES, SaleStatus, SearchFieldType, ServiceRequestSort, ServiceRequestStatus, StaffAssignmentStatus, StaffAvailabilityType, StaffDocumentType, StaffRole, TIME_OF_DAY_PATTERN, TRIP_MAX_NIGHTS, TRIP_PLAN_ACTION_TARGET, TRIP_PLAN_DEPOSIT_BPS, TRIP_PLAN_PAYMENT_HOURS, TRIP_PLAN_TOKEN_HEADER, TRIP_PLAN_TRANSITIONS, TestPaymentOutcome, TicketPriority, TicketStatus, TransferVehicleClass, TripCarMode, TripInterest, TripItemRole, TripItemStatus, TripOccasion, TripPlanAction, TripPlanPaymentPlan, TripPlanStatus, TripPriceKind, TripStayStyle, TripTransport, TripType, UserKind, UserStatus, VerificationTokenType, ViewingMode, VipTier, WEEKDAYS, WORK_ORDER_TRANSITIONS, WorkOrderMediaPhase, WorkOrderStatus, WorkOrderTaskStatus, absoluteUrl, addDaysToIsoDate, addMoney, addMonthsToIsoDate, addOnInputSchema, addOnQuantity, adjustmentSchema, adminAssetListQuerySchema, adminBookingCreateSchema, adminBookingExportQuerySchema, adminBookingListQuerySchema, adminCancelBookingSchema, adminDataRequestListQuerySchema, adminLeadListQuerySchema, adminNotificationQuerySchema, adminSearchQuerySchema, adminServiceRequestInputSchema, adminSetPasswordSchema, adminTripPlanQuerySchema, adminUserCreateSchema, adminUserListQuerySchema, adminUserUpdateSchema, allocateRefund, analyticsSettingSchema, applyBps, applyTitleTemplate, assertSafeMinor, assetAddOnInputSchema, assetCodeSchema, assetInputSchema, assetMediaUpdateSchema, assetStatusChangeSchema, attributeFieldSchema, attributeOptionSchema, attributeSchemaDefinition, auditLogQuerySchema, availabilityBlockInputSchema, bannerInputSchema, baseRateInputSchema, billableLegMinutes, billingAddressSchema, billingSchema, blogCategoryInputSchema, blogListQuerySchema, blogPostInputSchema, blogPostingJsonLd, bookingAddOnSelectionSchema, bookingNoteSchema, bookingRefundSchema, bookingRequestSchema, bookingRescheduleSchema, bookingStateMachine, bookingTransitionSchema, breadcrumbJsonLd, buildAttributesSchema, calculateInvoice, calculateQuote, calculateRefundMinor, calendarBoardQuerySchema, calendarDays, calendarPeriodEnd, cancelBookingSchema, cancellationPolicyInputSchema, cancellationRuleSchema, cancellationRulesSchema, canonicalUrl, cashAccountInputSchema, cashFlowQuerySchema, categoryFeaturesSchema, categoryInputSchema, categoryKeySchema, categorySearchSettingsSchema, challengeTokenSchema, changeBps, checkoutContactSchema, checkoutSchema, cmsListQuerySchema, cmsSeoInputSchema, companyProfileSettingSchema, companyTaxSettingSchema, comparisonRange, convertMinor, cookieConsentSchema, cookieSettingSchema, corporateAccountInputSchema, corporateAccountListQuerySchema, couponCodeSchema, couponInputSchema, couponListQuerySchema, createApiClient, createEnum, createStateMachine, creditNoteSchema, currencySchema, customSectionContentSchema, customerBookingListQuerySchema, customerListQuerySchema, customerProfileUpdateSchema, customerSearchQuerySchema, dashboardQuerySchema, dataRequestCreateSchema, dataRequestDecisionSchema, dateWindowQuerySchema, dayStatus, diffIsoDays, documentExpiryState, durationMinutes, emailSchema, emptySearchState, enumValues, estimateCharter, estimatedFlightMinutes, evaluateCoupon, evaluateSlot, exchangeRateInputSchema, expenseCategoryInputSchema, expenseInputSchema, expenseListQuerySchema, exportQuerySchema, faqInputSchema, faqPageJsonLd, featuredSectionSettingsSchema, forgotPasswordSchema, formatInvoiceNumber, formatMoney, formatRecurrence, hasPermission, haversineKm, heroSectionContentSchema, homepageSectionSchemas, homepageSectionUpdateSchema, honeypotSchema, idParamSchema, inSeason, includedTaxMinor, instagramSettingsSchema, intersectBusy, invoiceCancelSchema, invoiceFromBookingSchema, invoiceInputSchema, invoiceLineSchema, invoiceLinesWithDiscount, invoiceListQuerySchema, isAdminNotificationEvent, isEnumValue, isFree, isInventoryBlocking, isMailTemplateKey, isManualServiceRequestTransition, isPermissionKey, isRtlLocale, isSafeHref, isSettingKey, isSmsTemplateKey, isValidIsoDate, isValidLocalDateTime, isoDateInZone, isoDateRange, isoDateSchema, journeyDayInputSchema, journeyInputSchema, journeyItemInputSchema, leadContextSchema, leadInputSchema, leadUpdateSchema, ledgerListQuerySchema, linkUrlSchema, listSectionSettingsSchema, localDateTimeInZone, localDateTimeSchema, localDateTimeToUtc, localeSchema, localizationSettingSchema, localizePath, localizedTextSchema, locationMatchOf, loginSchema, longestFreeMinutes, mailTemplatePreviewSchema, mailTemplateUpdateSchema, manualPaymentSchema, maskIdentifier, mediaListQuerySchema, mediaUpdateSchema, menuItemInputSchema, menuSaveSchema, mergeIntervals, minorAmountSchema, minorToMajorString, moneySchema, multiplyMinor, nameSchema, nearestAvailableDate, netOfGross, newsletterConfirmSchema, newsletterSubscribeSchema, nextOccurrence, nextStaffCode, normalizeCouponCode, normalizeTag, notificationSettingSchema, occurrences, organizationJsonLd, overlapMs, overlaps, ownedAssetInputSchema, ownerServiceContentSchema, ownerServiceInputSchema, ownerServiceItemInputSchema, ownerServiceTierSchema, pageInputSchema, paginate, paginationQuerySchema, parseCatalogSearch, parseCategoryFeatures, parseInline, parseMajorToMinor, parseOwnerServiceContent, parseRecurrence, partialLocalizedTextSchema, passwordSchema, paymentLinkSchema, paymentListQuerySchema, paymentProvidersSettingSchema, peakLoad, phoneSchema, pickLocalized, priceBooking, pricePreviewSchema, pricingRuleInputSchema, pricingRuleListQuerySchema, productJsonLd, profitabilityQuerySchema, queryReader, quoteDecisionSchema, quoteInputSchema, quoteInvoiceLines, quoteLineSchema, quoteListQuerySchema, quotePaymentOptions, quotePaymentSchema, quoteSendSchema, quoteStateMachine, quoteTotalsIssue, rangesOverlap, readRichText, readingMinutes, receivablesQuerySchema, recoveryCodeSchema, recurrenceRuleSchema, refundListQuerySchema, refundableMinor, registerSchema, rejectedCoupon, remainingMinor, reorderSchema, reportExportQuerySchema, reportQuerySchema, reportScheduleSettingSchema, resetPasswordSchema, resolveBookingSelection, resolveRefundBps, reversalSchema, reviewInputSchema, reviewListQuerySchema, reviewModerationSchema, richTextInputSchema, richTextPlain, richTextToText, roleCreateSchema, saleInputSchema, saturatedIntervals, searchFieldSchema, searchSchemaDefinition, seasonInputSchema, seasonalRateInputSchema, seoAlternates, seoDefaultsSettingSchema, seoInputSchema, serializeCatalogSearch, serializeJsonLd, servicePlanInputSchema, serviceRequestBoardQuerySchema, serviceRequestCancelSchema, serviceRequestInputSchema, serviceRequestListQuerySchema, serviceRequestStateMachine, serviceRequestTransitionSchema, serviceRequestUpdateSchema, serviceWindow, settingUpdateSchema, shareBps, shiftSlot, slotScanBounds, slotStartDate, slugSchema, smsTemplateUpdateSchema, socialSettingSchema, splitLocalePath, staffAssignmentInputSchema, staffAssignmentUpdateSchema, staffAvailabilityInputSchema, staffDocumentInputSchema, staffInputSchema, staffListQuerySchema, staffPayoutSchema, staffScheduleQuerySchema, startPaymentSchema, sumMinor, targetAssignmentSchema, templateLocaleParamSchema, templateVariables, testPaymentCompletionSchema, testimonialInputSchema, textToRichText, timeOfDaySchema, timeZoneOffsetMinutes, toInterval, totpCodeSchema, transferSchema, travellerSchema, tripBalanceMinor, tripBriefSchema, tripDayDate, tripNights, tripPlanActionSchema, tripPlanActions, tripPlanItemUpdateSchema, tripPlanPaymentSchema, tripPlanSelectionSchema, tripPlanStateMachine, tripPlanSubmitSchema, tripProposalRequestSchema, tripSchedule, tripTotals, truncateDescription, twoFactorEnableSchema, twoFactorSetupSchema, twoFactorVerifySchema, upcomingOccurrences, updateFeatureFlagSchema, updateProfileSchema, updateRolePermissionsSchema, uuidSchema, vatRatesSettingSchema, verifyEmailSchema, weekdayOf, workOrderListQuerySchema, workOrderMediaFieldsSchema, workOrderStateMachine, workOrderTaskInputSchema, workOrderTransitionSchema, workOrderUpdateSchema, zonedDayBounds, zonedParts, zonedTimeToUtc };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map